// Scene construction: sky, sun, ocean, chunked terrain (its trees instanced
// models on a desktop, little voxel trees baked into the chunk geometry
// elsewhere), the Sky Ring objective marker, and each team's base tower. The
// fair-weather clouds are their own module (clouds.js).

import * as THREE from "three";
import { fbm, hash2, LAND_BUILDING_GRID_M } from "./terrain_core.js";
import { mergeBoxes, TEAM_INFO, CLASS_BUILDERS, CLASS_MODELS, planeGeometry } from "./planes.js";
import { bakedGeometry, loadModel, ModelInstance, modelsEnabled, paintMaterialDSInstanced, settle } from "./models.js";
import { GFX, softwareRenderer, TREES_FORCED } from "./gfx.js";
import { anchorsFor, modelForClass } from "./aircraft_models.js";
import {
  AIR_VEHICLE_MODEL,
  EMPLACEMENTS,
  LAND_VEHICLE_MODEL,
  LOD_DIST,
  VEHICLE_MODELS,
  BALLOON,
  RADAR_SWEEP_RAD_S,
  STRUCTURES,
  disposeTreads,
  emplacementOf,
  loadLevels,
  lodOf,
  navalModelOf,
  parkedGearBoxes,
  plinthMesh,
  scrollTreads,
  towerBoxes,
  townLot,
  vehicleParts,
  warshipParts,
  wreckOf,
} from "./unit_models.js";
import { SHIP_TURRET_ARC_DEG, batteryLayRange, navalMountOf, navalTurretLay, shipTurretBears } from "./gunsight.js";
import { muzzleFlash } from "./flipbooks.js";
import { CITY_KIND, buildCityBlock } from "./brickton.js";
import { hullPoseAt, pushHullSample } from "./hullpath.js";
import { buildSun } from "./sun.js";
import { buildMoon } from "./moon.js";
import { buildStars } from "./stars.js";

// A gun's muzzle flash: a flipbook flash card on each tube's muzzle (z across
// the mount), `size` metres of frame each (flipbooks.js muzzleFlash), grouped
// so the owner lights and hides them as one, the way the flash boxes were.
function flashGroup(size, zs = [0]) {
  const g = new THREE.Group();
  g.userData.flashSize = size;
  setFlashTubes(g, zs);
  g.visible = false;
  return g;
}

// One tube's flash card on its muzzle (x, y, z in the barrel's frame). A tip
// behind the trunnion faces aft: the recoilless rifle's back-blast.
function tubeFlash(size, x, y, z) {
  const f = muzzleFlash(size);
  f.visible = true; // the mount's flash group is the switch
  f.position.set(x, y, z);
  if (x < 0) f.rotation.y = Math.PI;
  return f;
}

// Re-seat a flash group's cards on these tubes (a model's own rifles arrive).
function setFlashTubes(g, zs) {
  for (const c of [...g.children]) g.remove(c);
  for (const z of zs) {
    const f = muzzleFlash(g.userData.flashSize);
    f.visible = true; // the group is the switch
    f.position.z = z;
    g.add(f);
  }
}

const CHUNK_CELLS = 24; // cells per chunk side
export const VIEW_DIST = 2400; // chunk visibility radius, meters
// The ring a struggling phone draws instead (governor.js, rung 2): six
// chunks out, not nine. With its one-chunk margin that is 225 chunks against
// the full ring's 361 parked and up to 529 in flight, a third to a half of
// the ground's draws and cull walk, and every tree on them, given back from
// the far edge of the haze. 1.8 km would round up to seven chunks and save a
// fifth.
export const VIEW_DIST_NEAR = 1700;

// "Is there a browser out there?" Node has shipped a `navigator` since v21, so
// `typeof navigator === "undefined"` stopped meaning "not a browser" and the
// headless knobs below quietly started reading the TEST RUNNER's core count
// instead. That matters more than it sounds: the clutter is real geometry the
// tests count, and the golden digests once held it — they would have passed or
// failed by how many cores the machine running them happened to have.
// `matchMedia` is the honest tell, and off-browser both knobs take a fixed
// reference profile.
const HEADLESS = typeof matchMedia === "undefined";

// How busy the cosmetic ground clutter gets (tall grass, boulders, floral
// dabs — see GroundClutter). From altitude the blocky ground reads fine, but
// parked in a tank turret at 0 m it read as an empty billiard table (user
// feedback). The clutter is pure extra geometry around the camera, so
// it scales with the machine: coarse-pointer devices and small core counts
// keep the lean classic look, a desktop with cores to spare gets the fuller
// field. Purely client-side scenery — heights, collision, and parity are
// untouched, exactly like the lone voxel trees.
const GROUND_CLUTTER = (() => {
  if (HEADLESS) return 0.6; // the reference field the tests and probes count
  const cores = navigator.hardwareConcurrency || 4;
  if (matchMedia("(pointer: coarse)").matches) return 0; // phones/tablets: every quad is battery
  return cores >= 8 ? 1 : 0.6;
})();

// Terrain LOD. The ground is a triangle surface strung between the heightfield's
// values at the 12 m grid corners (terrain_core.js groundAt) — the exact
// surface the server collides with. A chunk is a regular grid of those
// corners: full detail (k = LOD_FULL) takes every corner, so near the camera
// the drawn ground IS the collision ground, to the last bit; out where only
// a silhouette reads, a chunk takes every second corner (k 2, 24 m) or every
// fourth (k 1, 48 m). Skirts hang from every chunk edge to hide the cracks a
// coarse neighbour leaves (see chunkSkirtDepth).
//
// It replaces the blocky columns and their detail ladder (user feedback,
// many times over: "fill in the big blocks in hills", "push the res even
// further", and finally "the terrain isn't smooth"). The columns needed the
// ladder because a 6 m step was the SIZE of the pop; a smooth hill seen at
// 24 m instead of 12 m barely changes, so the shells can sit far out and the
// whole view is a fraction of the old vertex count.
//
// SHELLS, not rings: the distance is measured through the air (see
// chunkDistance), so climbing away from the ground coarsens it exactly like
// flying away from it does. Each tier is {k, dist(m)}, finest first. (There
// was a k 5 shell inside the full-detail one, the same ground grown with the
// grass, boulders and flowers; the clutter is instanced around the camera now
// — GroundClutter — and the shell went with it, along with the rebuild of
// every chunk that crossed it. A k 5 chunk still builds, as full detail.)
const LOD_FULL = 4; // detail k: corner step = LOD_FULL / k cells
const LOD_TIERS = (() => {
  const desktop = [{ k: 4, dist: 1300 }, { k: 2, dist: 2000 }];
  if (HEADLESS) return desktop; // the reference ladder the stream probe grades
  // Phones: full detail where a hull touches the ground, the silhouette beyond.
  if (matchMedia("(pointer: coarse)").matches) return [{ k: 4, dist: 600 }, { k: 2, dist: 1300 }];
  return desktop;
})();

// Slack on the way DOWN a tier: a chunk keeps its detail until it is this far
// past the boundary that granted it. Pacing back and forth across a shell
// edge — which is most of what a dogfight is — must never thrash detail⇄coarse.
const LOD_HYSTERESIS = 1.2;

// The detail shells move when you climb, not just when the map scrolls under
// the nose, so the sweep re-runs after this much vertical travel too. A dive
// from altitude has to sharpen the ground it is falling towards.
const LOD_ALT_STEP = 60; // meters

// The per-frame ration for terrain building, in milliseconds. Chunk builds are
// RESUMABLE (see chunkTask), so this is a real ceiling and not a suggestion:
// the pump stops the instant the ration is gone, mid-chunk if it has to, and
// picks the same chunk up next frame. (It used to be checked only BETWEEN
// chunks, when one ultra-detail column chunk was an 18 ms build — a
// guaranteed dropped frame every time you crossed into new ground. A smooth
// chunk is a few hundred corners from the height cache and far cheaper, but
// the pump keeps its manners: a sortie still streams hundreds of them.)
//
// A HOLE earns more of the frame than a refinement does: ground that is
// missing is a flat blue square where the landscape should be, while ground
// that is merely coarse is still ground.
const BUILD_BUDGET_MS = 3;
const BUILD_BUDGET_HOLE_MS = 6;

// The ground's colours: one per grid corner, blended by height and slope, so
// the classic tier reads as a painted relief — beaches into meadow, meadow
// into scree, a snowline on the peaks, bare earth down the steep flanks.
// Tier 2's splat (terrain_splat.js) samples the same bands from art/terrain,
// whose texture means were matched to these very colours. Two shades a band,
// picked per corner by hash: the columns' dithered field, softened into
// mottling. Colours are written into `out` — every corner in view runs this,
// so it allocates nothing.
const PAL = {
  sand: [[0.85, 0.78, 0.45], [0.8, 0.73, 0.42]],
  grass: [[0.41, 0.73, 0.29], [0.36, 0.66, 0.26]],
  rock: [[0.55, 0.56, 0.58], [0.49, 0.5, 0.52]],
  snow: [[0.92, 0.95, 0.97], [0.86, 0.9, 0.93]],
  dirt: [[0.45, 0.33, 0.2], [0.42, 0.31, 0.19]],
  // Land theaters: a dry steppe — lake-shore mud, sun-bleached grass with
  // darker meadow patches, scrub on the hill flanks, bare rock crests.
  mud: [[0.62, 0.55, 0.38], [0.57, 0.5, 0.35]],
  steppe: [[0.55, 0.62, 0.3], [0.48, 0.56, 0.27]],
  scrub: [[0.52, 0.53, 0.32], [0.46, 0.48, 0.3]],
  // A shade darker and warmer than the columns' crest top: their darker side
  // faces used to break a hill mass up, and a smooth crest in one pale grey
  // read as chalk from the air.
  crest: [[0.5, 0.48, 0.42], [0.45, 0.43, 0.38]],
  earth: [[0.44, 0.36, 0.24], [0.41, 0.34, 0.22]],
};
// 0 below edge-half, 1 above edge+half, smooth between.
function band(h, edge, half) {
  const t = (h - edge + half) / (2 * half);
  return t <= 0 ? 0 : t >= 1 ? 1 : t * t * (3 - 2 * t);
}
function mixInto(out, c, t) {
  out[0] += (c[0] - out[0]) * t;
  out[1] += (c[1] - out[1]) * t;
  out[2] += (c[2] - out[2]) * t;
}
// Patches the bands carry: sun-bleached straw through the air map's meadows,
// greener meadow hollows through the land theaters' steppe.
const STRAW = [0.62, 0.66, 0.36];
const MEADOW = [0.43, 0.58, 0.25];
/**
 * Ground colour at height h with slope `steep` (1 - normal.y), a per-corner
 * dither d in [0,1), and two low-frequency fields in [0,1] — `mv`, which
 * wanders the band edges and the brightness (so a snowline or a beach isn't
 * ruled), and `patch`, which lays straw or meadow patches across the grass:
 * the air and naval archipelagos (sand, grass, rock, snow), or the land
 * theaters' steppe when `land` is set. The same recipe as tier 2's splat.
 */
export function groundColor(out, h, steep, d, land, mv = 0.5, patch = 0.5) {
  const k = d < 0.5 ? 0 : 1;
  const w = mv - 0.5;
  // Cliffs: past ~30 degrees the flank shows its earth, rock higher up.
  const cliff = band(steep, 0.2, 0.08);
  if (land) {
    const c = PAL.mud[k];
    out[0] = c[0]; out[1] = c[1]; out[2] = c[2];
    mixInto(out, PAL.steppe[k], band(h, 1.5 + w * 1.5, 1));
    mixInto(out, MEADOW, band(patch, 0.62, 0.1) * 0.55 * (1 - band(h, 34, 6)) * band(h, 1.5, 1));
    mixInto(out, PAL.scrub[k], band(h, 34 + w * 10, 6));
    mixInto(out, PAL.crest[k], band(h, 62 + w * 10, 6));
    mixInto(out, h > 50 ? PAL.crest[k] : PAL.earth[k], cliff * 0.8);
  } else {
    const c = PAL.sand[k];
    out[0] = c[0]; out[1] = c[1]; out[2] = c[2];
    mixInto(out, PAL.grass[k], band(h, 8 + w * 6, 3));
    mixInto(out, STRAW, band(patch, 0.65, 0.1) * 0.5 * band(h, 8, 3) * (1 - band(h, 96, 8)));
    // Above the treeline the flats keep a thin, sun-burnt grass and the
    // slopes break out in scree — a whole plateau of bare stone read as a
    // snowfield from the air.
    const high = band(h, 96 + w * 18, 8);
    mixInto(out, STRAW, high * 0.55);
    mixInto(out, PAL.rock[k], high * (0.45 + 0.55 * band(steep, 0.1, 0.06)));
    mixInto(out, PAL.snow[k], band(h, 168 + w * 24, 8));
    if (h > 8) mixInto(out, h > 120 ? PAL.rock[k] : PAL.dirt[k], cliff * 0.85);
  }
  // The map-wide drift in brightness that keeps a big field from reading as
  // one flat sheet (tier 2's macro texture, in vertex colour).
  const b = 0.92 + 0.16 * mv;
  out[0] *= b; out[1] *= b; out[2] *= b;
  return out;
}

// --- roads ----------------------------------------------------------------
// A deterministic road network painted onto the terrain (heights untouched,
// so server parity is unaffected): a base-to-base highway through the island
// center, a perpendicular cross road, and a ring road around the Sky Ring.
// Geometry is derived only from the world config; the server mirrors it
// (ships.go roadDistance) for the convoys' bridge deck and gun siting, and
// the ground material paints it per pixel (roadPaintGLSL).

export const ROAD_HALF_WIDTH = 9; // meters from centerline to road edge

function segDist(px, pz, ax, az, bx, bz) {
  const dx = bx - ax, dz = bz - az;
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (pz - az) * dz) / (dx * dx + dz * dz)));
  const ex = ax + dx * t - px, ez = az + dz * t - pz;
  return Math.hypot(ex, ez);
}

/** Distance from (x, z) to the nearest road centerline. */
export function roadDistance(field, x, z) {
  const d = field.radius * 0.72 * Math.SQRT1_2; // team base corner coordinate
  return Math.min(
    segDist(x, z, -d, -d, d, d), // base-to-base highway
    segDist(x, z, -d, d, d, -d), // cross road
    Math.abs(Math.hypot(x, z) - field.radius * 0.34), // ring road
  );
}

// --- trestle bridges ------------------------------------------------------
// Where a road runs lower than the deck — out over the water and down the
// beach to it — a timber trestle carries it at waterLevel + blockHeight. The
// server's convoys ride this very deck (convoy.go truckSurfaceY: anywhere on
// the road the ground runs lower than it), and the ground climbs up to meet
// it where the shore rises past it.
//
// The bridge follows the road's own centerline, not the 12 m terrain grid:
// the roads are painted per pixel as smooth ribbons, and a deck built from
// grid cells walked the diagonal highway as a sawtooth of diamonds that
// no road ever looked like. So each road is walked in short straight pieces
// (a curve, on the ring road) and every piece lays the deck, its rails and
// the bents under it along the road's heading. The running surface is
// painted by the ground's own road shader (ROAD_PAINT_GLSL), so asphalt,
// shoulders and the worn centre line run unbroken off the land onto the
// timber.

// Deck slab: the running surface sits on a 1.2 m timber slab that overhangs
// the road's painted edge by a hand's breadth, so the rails stand on wood.
const DECK_OVERHANG = 0.4;
const DECK_THICK = 1.2;
// A bent (a row of piles under a cap beam) every 12 m: close enough that
// the deck reads as carried, far enough apart that the sea shows between.
const BENT_SPACING = 12;
// Rail posts every 3 m down both edges, a hip-high top rail and a kerb.
const RAIL_POST_SPACING = 3;
const RAIL_HEIGHT = 1.1;
// Piles across the bent, metres off the centerline: two under each lane.
const BENT_PILES = [-7, -2.3, 2.3, 7];
// The deck is judged every 1.5 m along the road (an eighth of a bent), at
// both edges, the quarter points and the centre: any of them lower than the
// deck and the deck is there, as for the server's trucks.
const DECK_PROBE_STEP = 1.5;
const DECK_PROBE_OFFSETS = [-ROAD_HALF_WIDTH, -ROAD_HALF_WIDTH / 2, 0, ROAD_HALF_WIDTH / 2, ROAD_HALF_WIDTH];

const TIMBER = [0.46, 0.35, 0.22];
const TIMBER_DARK = [0.35, 0.26, 0.16];
const TIMBER_TARRED = [0.24, 0.18, 0.12]; // the piles: creosote, wet to the waterline

/**
 * The road network as centerlines you can walk: for each road its length and
 * `at(s)`, the point and heading s metres from its start, written into `out`
 * as [x, z, ux, uz] (unit heading). The same three roads roadDistance
 * measures: the base-to-base highway, the cross road and the ring road.
 * `piece` is how long a straight stretch of it the bridge builder lays at a
 * time — the whole of a bent on the diagonals, half of one on the ring
 * (whose chord then strays 3 mm off the true circle).
 */
function roadCourses(field) {
  const d = field.radius * 0.72 * Math.SQRT1_2; // team base corner coordinate
  const R = field.radius * 0.34;
  const line = (ax, az, bx, bz) => {
    const L = Math.hypot(bx - ax, bz - az), ux = (bx - ax) / L, uz = (bz - az) / L;
    return {
      L, piece: BENT_SPACING,
      at(s, out) { out[0] = ax + ux * s; out[1] = az + uz * s; out[2] = ux; out[3] = uz; return out; },
    };
  };
  return [
    line(-d, -d, d, d),
    line(-d, d, d, -d),
    {
      L: 2 * Math.PI * R, piece: BENT_SPACING / 2,
      at(s, out) {
        const a = s / R, c = Math.cos(a), sn = Math.sin(a);
        out[0] = R * c; out[1] = R * sn; out[2] = -sn; out[3] = c;
        return out;
      },
    },
  ];
}

/**
 * One flat face, wound so it faces `n` whichever order the corners arrive in
 * (the box models' material draws front faces only).
 */
function face(g, a, b, c, d, nx, ny, nz, col) {
  const ex = b[0] - a[0], ey = b[1] - a[1], ez = b[2] - a[2];
  const fx = c[0] - a[0], fy = c[1] - a[1], fz = c[2] - a[2];
  const facing = (ey * fz - ez * fy) * nx + (ez * fx - ex * fz) * ny + (ex * fy - ey * fx) * nz;
  if (facing < 0) { const t = b; b = d; d = t; }
  g.quad(a[0], a[1], a[2], b[0], b[1], b[2], c[0], c[1], c[2], d[0], d[1], d[2], nx, ny, nz, col[0], col[1], col[2]);
}

/**
 * A timber from p0 to p1 (either way up: piles, rails, braces), `w` thick
 * along `t` — a unit vector square to the timber's run — and `h` across the
 * third axis. `ends` false leaves off the end grain where nobody can see it:
 * rails running on into the next piece's, posts under the rail, piles in the
 * mud.
 */
function beam(g, x0, y0, z0, x1, y1, z1, tx, ty, tz, w, h, col, ends = true) {
  let ax = x1 - x0, ay = y1 - y0, az = z1 - z0;
  const len = Math.hypot(ax, ay, az);
  if (len < 1e-6) return;
  ax /= len; ay /= len; az /= len;
  // b = a x t, the timber's third axis.
  const bx = ay * tz - az * ty, by = az * tx - ax * tz, bz = ax * ty - ay * tx;
  const hw = w / 2, hh = h / 2;
  const corner = (p, sw, sh) => [
    (p ? x1 : x0) + tx * hw * sw + bx * hh * sh,
    (p ? y1 : y0) + ty * hw * sw + by * hh * sh,
    (p ? z1 : z0) + tz * hw * sw + bz * hh * sh,
  ];
  const c000 = corner(0, -1, -1), c001 = corner(0, -1, 1), c010 = corner(0, 1, -1), c011 = corner(0, 1, 1);
  const c100 = corner(1, -1, -1), c101 = corner(1, -1, 1), c110 = corner(1, 1, -1), c111 = corner(1, 1, 1);
  face(g, c010, c011, c111, c110, tx, ty, tz, col);
  face(g, c000, c100, c101, c001, -tx, -ty, -tz, col);
  face(g, c001, c101, c111, c011, bx, by, bz, col);
  face(g, c000, c010, c110, c100, -bx, -by, -bz, col);
  if (!ends) return;
  face(g, c100, c110, c111, c101, ax, ay, az, col);
  face(g, c000, c001, c011, c010, -ax, -ay, -az, col);
}

/**
 * Every stretch of trestle whose piece of road starts in this chunk's square
 * [x0, x0 + span) x [z0, z0 + span). `g` takes the timber (the box models'
 * material), `paved` the running surface (drawn with the road shader), and
 * `at` is the ground this chunk's tier draws, for the piles' feet. At the
 * coarse tiers (lod < LOD_FULL) the rail posts and sway braces are left off:
 * a pixel apiece from that far out. A resumable task like the chunk it
 * serves: it yields after every piece it lays, so a long crossing streams in
 * over a few frames instead of in one slice. Returns true if it laid any
 * deck.
 */
function* buildBridges(g, paved, f, x0, z0, span, at, lod) {
  const deckTop = f.waterLevel + f.blockHeight;
  const hw = ROAD_HALF_WIDTH, edge = ROAD_HALF_WIDTH + DECK_OVERHANG;
  const under = deckTop - DECK_THICK;
  const detail = lod >= LOD_FULL;
  const p = [0, 0, 0, 0];
  const low = (s, road) => {
    road.at(s, p);
    for (const o of DECK_PROBE_OFFSETS) if (f.groundAt(p[0] - p[3] * o, p[1] + p[2] * o) < deckTop) return true;
    return false;
  };
  // Point `o` metres left of the centerline at s, `y` up.
  const side = (road, s, o, y) => { road.at(s, p); return [p[0] - p[3] * o, y, p[1] + p[2] * o]; };
  let laid = false;
  for (const road of roadCourses(f)) {
    const P = road.piece, n = Math.ceil(road.L / P), steps = P / DECK_PROBE_STEP;
    for (let k = 0; k < n; k++) {
      const a = k * P, b = Math.min(road.L, a + P);
      road.at((a + b) / 2, p);
      if (p[0] < x0 || p[0] >= x0 + span || p[1] < z0 || p[1] >= z0 + span) continue;
      // Trim the piece to where the deck is wanted, one probe step past the
      // last low one, so a span's end runs into the bank rather than stopping
      // short of it.
      let first = -1, last = -1;
      for (let j = 0; j <= steps; j++) {
        const s = Math.min(b, a + j * DECK_PROBE_STEP);
        if (low(s, road)) { if (first < 0) first = j; last = j; }
      }
      if (first < 0) continue;
      const s0 = Math.max(a, a + (first - 1) * DECK_PROBE_STEP);
      const s1 = Math.min(b, a + (last + 1) * DECK_PROBE_STEP);
      if (laid) yield;
      laid = true;

      // The deck: the road's running surface, edge to painted edge, on a
      // timber slab whose overhang shows as planking outside the kerbs.
      road.at(s0, p); const u0x = p[2], u0z = p[3];
      road.at(s1, p); const u1x = p[2], u1z = p[3];
      const top = deckTop + 0.05;
      const tl0 = side(road, s0, hw, top), tr0 = side(road, s0, -hw, top);
      const tl1 = side(road, s1, hw, top), tr1 = side(road, s1, -hw, top);
      face(paved, tl0, tl1, tr1, tr0, 0, 1, 0, TIMBER);
      const sl0 = side(road, s0, edge, deckTop), sr0 = side(road, s0, -edge, deckTop);
      const sl1 = side(road, s1, edge, deckTop), sr1 = side(road, s1, -edge, deckTop);
      face(g, side(road, s0, hw, top), side(road, s1, hw, top), sl1, sl0, 0, 1, 0, TIMBER);
      face(g, side(road, s0, -hw, top), side(road, s1, -hw, top), sr1, sr0, 0, 1, 0, TIMBER);
      const bl0 = side(road, s0, edge, under), br0 = side(road, s0, -edge, under);
      const bl1 = side(road, s1, edge, under), br1 = side(road, s1, -edge, under);
      const mx = (u0x + u1x) / 2, mz = (u0z + u1z) / 2, ml = Math.hypot(mx, mz);
      const lx = -mz / ml, lz = mx / ml; // outward, the left edge's way
      face(g, sl0, sl1, bl1, bl0, lx, 0, lz, TIMBER_DARK);
      face(g, sr0, sr1, br1, br0, -lx, 0, -lz, TIMBER_DARK);
      face(g, bl0, bl1, br1, br0, 0, -1, 0, TIMBER_TARRED);
      // End grain where the deck stops: at a bank, or at the road's end.
      if (s0 > a || s0 === 0) face(g, sl0, sr0, br0, bl0, -u0x, 0, -u0z, TIMBER_DARK);
      if (s1 < b || s1 === road.L) face(g, sl1, sr1, br1, bl1, u1x, 0, u1z, TIMBER_DARK);

      // Rails down both edges: a kerb on the deck and a top rail on posts.
      for (const sgn of [1, -1]) {
        const o = sgn * (hw - 0.1);
        const tx = sgn * lx, tz = sgn * lz;
        const k0 = side(road, s0, o, deckTop + 0.2), k1 = side(road, s1, o, deckTop + 0.2);
        beam(g, k0[0], k0[1], k0[2], k1[0], k1[1], k1[2], tx, 0, tz, 0.5, 0.4, TIMBER_DARK, false);
        const r0 = side(road, s0, o, deckTop + RAIL_HEIGHT), r1 = side(road, s1, o, deckTop + RAIL_HEIGHT);
        beam(g, r0[0], r0[1], r0[2], r1[0], r1[1], r1[2], tx, 0, tz, 0.3, 0.25, TIMBER, false);
        if (!detail) continue;
        for (let s = Math.ceil(s0 / RAIL_POST_SPACING) * RAIL_POST_SPACING; s <= s1; s += RAIL_POST_SPACING) {
          if (s >= b && b < road.L) break; // the next piece stands this one
          const q = side(road, s, o, deckTop);
          beam(g, q[0], deckTop + 0.4, q[2], q[0], deckTop + RAIL_HEIGHT - 0.1, q[2], p[2], 0, p[3], 0.3, 0.3, TIMBER_DARK, false);
        }
      }

      // The bent: a cap beam under the slab, piles down to the ground or the
      // sea floor, and an X of sway braces where they stand tall.
      const sb = Math.floor(a / BENT_SPACING) * BENT_SPACING + BENT_SPACING / 2;
      if (sb < s0 + 1 || sb > s1 - 1) continue;
      road.at(sb, p);
      const ux = p[2], uz = p[3], cx = p[0], cz = p[1];
      const cap = under - 0.4;
      const cL = side(road, sb, edge + 0.3, cap), cR = side(road, sb, -edge - 0.3, cap);
      beam(g, cL[0], cap, cL[2], cR[0], cap, cR[2], ux, 0, uz, 0.8, 0.8, TIMBER_DARK);
      const foot = [];
      for (const o of BENT_PILES) {
        const x = cx - uz * o, z = cz + ux * o;
        const gy = at(x, z);
        foot.push(gy);
        if (gy > cap - 0.8) continue; // the bank is up under the deck here
        beam(g, x, gy - 1, z, x, cap - 0.4, z, ux, 0, uz, 0.7, 0.7, TIMBER_TARRED, false);
      }
      if (!detail) continue;
      // Braces from the cap down to the waterline (or the beach), outer pile
      // to outer pile.
      const bot = Math.max(f.waterLevel + 0.4, foot[0] + 0.3, foot[3] + 0.3);
      if (cap - 0.4 - bot < 2.5) continue;
      const oL = BENT_PILES[0], oR = BENT_PILES[3];
      const hi = cap - 0.5;
      const A = [cx - uz * oL, hi, cz + ux * oL], B = [cx - uz * oR, bot, cz + ux * oR];
      const C = [cx - uz * oR, hi, cz + ux * oR], D = [cx - uz * oL, bot, cz + ux * oL];
      // Hung off the piles' seaward and landward faces so the X doesn't cut
      // through itself.
      const off = 0.5;
      beam(g, A[0] + ux * off, A[1], A[2] + uz * off, B[0] + ux * off, B[1], B[2] + uz * off, ux, 0, uz, 0.25, 0.35, TIMBER);
      beam(g, C[0] - ux * off, C[1], C[2] - uz * off, D[0] - ux * off, D[1], D[2] - uz * off, ux, 0, uz, 0.25, 0.35, TIMBER);
    }
  }
  return laid;
}

// A chunk's mesh is poured straight into the Float32Arrays the GPU will
// eventually eat, one vertex at a time, with no JS array and no per-quad
// garbage in between.
//
// It used to push into three plain arrays and hand them to
// Float32BufferAttribute, which meant every quad allocated a tuple per corner
// plus the [a,b,c,a,c,d] winding array, and every vertex boxed nine doubles
// into a growable array that reallocated as it went. That is a rounding error
// for a hut; it is not a rounding error for the ground. The innermost detail
// ring subdivides each terrain cell six ways, so ONE chunk at that tier is
// 144x144 subcolumns throwing up to five faces each — the arithmetic is
// trivial and the bookkeeping around it was costing ~35x more than the mesh.
// That cost landed on the main thread the moment you crossed into unstreamed
// terrain, which is exactly when you least want a hitch: a fast pass over a
// ridge line, the ground arriving late in a dive.
//
// So: scalars in (no tuples to allocate), a write cursor, and a capacity that
// doubles when a chunk turns out bigger than the guess. `build()` hands over
// exact-length copies so a chunk parked in the scene graph for the rest of
// the round holds its geometry and not the slack we grew past.
//
// Vertex data only — never terrain heights. The heightfield stays float64 in
// the `tops` scratch arrays, because that is parity ground shared with the Go
// server; this is the render side, which the GPU takes as float32 regardless.
const QUAD_FLOATS = 18; // 6 vertices x 3 components

// The tree species (art/vegetation/), indices into TerrainRenderer.treeModels:
// the land theaters' forests and the air and naval maps' lone trees.
const TREE_SPECIES = ["oak", "scots_pine", "spruce", "birch", "poplar"];
// Model trees are a desktop-GPU tier, like the ground clutter: a forest
// chunk draws one instanced batch per species, which on an M1 Pro was +190
// draw calls and +0.6M triangles across the land map with frame time
// unmoved (the air map's lone trees: +70–130 calls and ~+130k triangles a
// frame, unmoved again) — but on a phone's driver every draw call is
// battery, and on a software rasterizer (gfx.js softwareRenderer) a tank
// parked in a treeline fell to about one frame a second under full-detail
// oaks. A phone bakes its trees into the ground instead: zero extra draws.
// What it bakes is the model's own LOD2 (27-74 triangles a tree, poured into
// the chunk's indexed box geometry — bakeRecord), so it sees the desktop's
// species, groves and silhouettes rather than stacked green cubes, in a third
// fewer resident vertices than the voxel woods (their boxes unroll every
// corner; a model keeps its welded ones). The software rasterizer keeps the
// voxel tree: it pays per triangle, and the bake's 60-75% more of them took
// the land treeline from 1.4 frames a second to 0.2 (ground_cover_probe).
// The voxel tree is also everyone's for the seconds before the models land.
const MODEL_TREES = !HEADLESS && !matchMedia("(pointer: coarse)").matches;
const TREE_OAK = 0, TREE_PINE = 1, TREE_SPRUCE = 2, TREE_BIRCH = 3, TREE_POPLAR = 4;

// Retire a chunk: its own geometry (ground and boxes). The trees standing on
// it live in their region's batches (ForestRegions), which the chunk's
// retirement re-deals.
function disposeChunk(obj) {
  obj.geometry?.dispose(); // ocean chunks are geometry-less placeholders
  for (const c of obj.children ?? []) if (c.isMesh) c.geometry.dispose(); // the chunk's own clutter/bridge boxes
}

// Chunks per side of a forest region: 4 x 4 chunks is 1152 m of woods drawn
// as one batch per tree build. A batch per chunk was 300-470 draws a frame
// on the air and land maps, the largest single draw source the renderer had
// (docs/client-performance-audit.md); at this size the whole view ring is a
// few dozen. Bigger buys fewer draws but a looser cull — a region the
// camera stands in draws its trees behind you too — and a costlier re-deal
// whenever one of its chunks changes detail.
const FOREST_REGION = 4;

/**
 * The model forests, batched by region rather than by chunk. A chunk hands
 * over its stands when it commits (one matrix block per tree build: species
 * at that chunk's level of detail) and takes them back when it retires; a
 * region whose chunks changed re-deals its batches once, at flush — one
 * InstancedMesh per tree geometry in use there, its instances every
 * member's trees back to back. Only the container changes: every tree keeps
 * the exact matrix its chunk planted it with.
 */
export class ForestRegions {
  constructor(scene) {
    this.scene = scene;
    this.stands = new Map(); // chunk key -> [{ geometry, count, matrices }]
    this.regions = new Map(); // region key -> Map(geometry -> InstancedMesh)
    this.members = new Map(); // region key -> Set of chunk keys with stands
    this.dirty = new Set();
  }

  static regionOf(key) {
    const [cx, cz] = key.split(",").map(Number);
    return `${Math.floor(cx / FOREST_REGION)},${Math.floor(cz / FOREST_REGION)}`;
  }

  /** A chunk's trees from now on (none: it retired, or grows no models). */
  set(key, stands) {
    const had = this.stands.has(key);
    if (!stands?.length && !had) return; // treeless before and after: nothing to re-deal
    const rk = ForestRegions.regionOf(key);
    let members = this.members.get(rk);
    if (!members) this.members.set(rk, (members = new Set()));
    if (stands?.length) {
      this.stands.set(key, stands);
      members.add(key);
    } else {
      this.stands.delete(key);
      members.delete(key);
    }
    this.dirty.add(rk);
  }

  /** Re-deal every region a chunk changed in since the last flush. */
  flush() {
    for (const rk of this.dirty) this.deal(rk);
    this.dirty.clear();
  }

  deal(rk) {
    const members = this.members.get(rk);
    const counts = new Map();
    for (const key of members) {
      for (const st of this.stands.get(key)) counts.set(st.geometry, (counts.get(st.geometry) ?? 0) + st.count);
    }
    let batches = this.regions.get(rk);
    if (!batches) this.regions.set(rk, (batches = new Map()));
    for (const [geo, im] of batches) {
      if (counts.has(geo)) continue;
      this.scene.remove(im);
      im.dispose();
      batches.delete(geo);
    }
    for (const [geo, n] of counts) {
      let im = batches.get(geo);
      // Grow by a quarter on a rebuild, so the shell sweeping a region one
      // chunk at a time re-uses one buffer instead of reallocating per chunk.
      if (im && im.instanceMatrix.count < n) {
        this.scene.remove(im);
        im.dispose();
        im = null;
      }
      if (!im) {
        im = new THREE.InstancedMesh(geo, paintMaterialDSInstanced, Math.ceil(n * 1.25));
        im.matrixAutoUpdate = false;
        im.castShadow = im.receiveShadow = GFX >= 2;
        im.userData.forest = true;
        batches.set(geo, im);
        this.scene.add(im);
        settle(im); // trees don't move: the walk places the batch once (models.js WorldScene)
      }
      const arr = im.instanceMatrix.array;
      let at = 0;
      for (const key of members) {
        for (const st of this.stands.get(key)) {
          if (st.geometry !== geo) continue;
          arr.set(st.matrices, at * 16);
          at += st.count;
        }
      }
      im.count = n;
      im.instanceMatrix.clearUpdateRanges();
      im.instanceMatrix.addUpdateRange(0, n * 16);
      im.instanceMatrix.needsUpdate = true;
      // The cull sphere is the union of its stands' spheres, each found once
      // when its chunk planted it — re-walking every tree in the region on
      // every re-deal would cost a streaming frame a millisecond of woods.
      const bs = (im.boundingSphere ??= new THREE.Sphere()).makeEmpty();
      for (const key of members) for (const st of this.stands.get(key)) if (st.geometry === geo) bs.union(st.sphere);
    }
    if (!members.size) {
      this.members.delete(rk);
      this.regions.delete(rk);
    }
  }

  /** Every batch on screen, for the tests and the probes. */
  *batches() {
    for (const b of this.regions.values()) yield* b.values();
  }
}

export class GeomBuilder {
  // quadHint sizes the first allocation. Terrain passes its own estimate; the
  // default suits the small stuff (a bridge cell, a stand of trees).
  //
  // `indexed` writes a quad as its four corners and six indices instead of
  // six whole vertices: a third fewer vertices for every box, and a baked
  // tree keeps the welded vertices its model shipped with instead of
  // unrolling them three a triangle. The chunks' baked geometry runs
  // indexed — on a phone it is every tree in view — and the small stuff
  // keeps the plain layout.
  constructor(quadHint = 256, { indexed = false } = {}) {
    const cap = Math.max(1, quadHint) * QUAD_FLOATS;
    this.positions = new Float32Array(cap);
    this.normals = new Float32Array(cap);
    this.colors = new Float32Array(cap);
    this.n = 0; // floats written so far, into all three in lockstep
    this.index = indexed ? new Uint32Array(Math.max(1, quadHint) * 6) : null;
    this.ni = 0; // indices written so far
  }

  // Double until the next quad fits. Chunk sizes vary by an order of
  // magnitude between an all-ocean tile and a fine-LOD mountainside, so we
  // grow rather than trying to predict.
  _grow() {
    const cap = this.positions.length * 2;
    const p = new Float32Array(cap); p.set(this.positions); this.positions = p;
    const nr = new Float32Array(cap); nr.set(this.normals); this.normals = nr;
    const c = new Float32Array(cap); c.set(this.colors); this.colors = c;
  }

  // Room for `floats` more vertex floats and `idx` more indices.
  _reserve(floats, idx = 0) {
    while (this.n + floats > this.positions.length) this._grow();
    if (this.index && this.ni + idx > this.index.length) {
      let cap = this.index.length * 2;
      while (this.ni + idx > cap) cap *= 2;
      const ix = new Uint32Array(cap); ix.set(this.index); this.index = ix;
    }
  }

  /**
   * One quad as two triangles: a-b-c, a-c-d (counter-clockwise seen from the
   * normal). Corners, normal, and colour all arrive as loose scalars — the
   * whole point is that calling this allocates nothing.
   */
  quad(ax, ay, az, bx, by, bz, cx, cy, cz, dx, dy, dz, nx, ny, nz, cr, cg, cb) {
    if (this.index) return this._quadIndexed(ax, ay, az, bx, by, bz, cx, cy, cz, dx, dy, dz, nx, ny, nz, cr, cg, cb);
    if (this.n + QUAD_FLOATS > this.positions.length) this._grow();
    const o = this.n;
    const p = this.positions;
    p[o] = ax; p[o + 1] = ay; p[o + 2] = az;
    p[o + 3] = bx; p[o + 4] = by; p[o + 5] = bz;
    p[o + 6] = cx; p[o + 7] = cy; p[o + 8] = cz;
    p[o + 9] = ax; p[o + 10] = ay; p[o + 11] = az;
    p[o + 12] = cx; p[o + 13] = cy; p[o + 14] = cz;
    p[o + 15] = dx; p[o + 16] = dy; p[o + 17] = dz;
    // Flat-shaded and flat-coloured: all six vertices repeat the face values.
    const nr = this.normals, cc = this.colors;
    for (let i = 0; i < QUAD_FLOATS; i += 3) {
      const k = o + i;
      nr[k] = nx; nr[k + 1] = ny; nr[k + 2] = nz;
      cc[k] = cr; cc[k + 1] = cg; cc[k + 2] = cb;
    }
    this.n = o + QUAD_FLOATS;
  }

  // The same quad, indexed: four corners, the same two triangles.
  _quadIndexed(ax, ay, az, bx, by, bz, cx, cy, cz, dx, dy, dz, nx, ny, nz, cr, cg, cb) {
    this._reserve(12, 6);
    const o = this.n;
    const p = this.positions;
    p[o] = ax; p[o + 1] = ay; p[o + 2] = az;
    p[o + 3] = bx; p[o + 4] = by; p[o + 5] = bz;
    p[o + 6] = cx; p[o + 7] = cy; p[o + 8] = cz;
    p[o + 9] = dx; p[o + 10] = dy; p[o + 11] = dz;
    const nr = this.normals, cc = this.colors;
    for (let i = 0; i < 12; i += 3) {
      const k = o + i;
      nr[k] = nx; nr[k + 1] = ny; nr[k + 2] = nz;
      cc[k] = cr; cc[k + 1] = cg; cc[k + 2] = cb;
    }
    const v = o / 3, ix = this.index, i0 = this.ni;
    ix[i0] = v; ix[i0 + 1] = v + 1; ix[i0 + 2] = v + 2;
    ix[i0 + 3] = v; ix[i0 + 4] = v + 2; ix[i0 + 5] = v + 3;
    this.n = o + 12;
    this.ni = i0 + 6;
  }

  // Colour still arrives as a [r,g,b] tuple here: every caller passes a
  // palette entry it already has in hand, so there is nothing to allocate.
  box(x, y, z, w, h, d, color) {
    const x0 = x - w / 2, x1 = x + w / 2;
    const y0 = y - h / 2, y1 = y + h / 2;
    const z0 = z - d / 2, z1 = z + d / 2;
    const r = color[0], g = color[1], b = color[2];
    this.quad(x0, y1, z0, x0, y1, z1, x1, y1, z1, x1, y1, z0, 0, 1, 0, r, g, b);
    this.quad(x0, y0, z1, x0, y0, z0, x1, y0, z0, x1, y0, z1, 0, -1, 0, r, g, b);
    this.quad(x1, y0, z0, x1, y1, z0, x1, y1, z1, x1, y0, z1, 1, 0, 0, r, g, b);
    this.quad(x0, y0, z1, x0, y1, z1, x0, y1, z0, x0, y0, z0, -1, 0, 0, r, g, b);
    this.quad(x0, y0, z1, x1, y0, z1, x1, y1, z1, x0, y1, z1, 0, 0, 1, r, g, b);
    this.quad(x1, y0, z0, x0, y0, z0, x0, y1, z0, x1, y1, z0, 0, 0, -1, r, g, b);
  }

  /**
   * A baked tree (bakeRecord): its triangles turned by `yaw` about the trunk,
   * scaled, and stood at (x, y, z) — the same transform a model tree's
   * instance matrix carries (compose: translate, Y rotation, uniform scale),
   * so a baked wood stands exactly where the desktop's instanced one does.
   * An indexed builder takes the record's welded vertices and its index; a
   * plain one unrolls them, three vertices a triangle.
   */
  pour(rec, x, y, z, yaw, scale) {
    const c = Math.cos(yaw), s = Math.sin(yaw);
    const sp = rec.positions, sn = rec.normals, sc = rec.colors, si = rec.index;
    const indexed = !!this.index;
    const floats = indexed ? sp.length : si.length * 3;
    this._reserve(floats, indexed ? si.length : 0);
    const p = this.positions, nr = this.normals, cc = this.colors;
    const o = this.n;
    const put = (at, v) => {
      const i = v * 3;
      const px = sp[i], py = sp[i + 1], pz = sp[i + 2];
      p[at] = x + scale * (c * px + s * pz);
      p[at + 1] = y + scale * py;
      p[at + 2] = z + scale * (c * pz - s * px);
      const nx = sn[i], nz = sn[i + 2];
      nr[at] = c * nx + s * nz;
      nr[at + 1] = sn[i + 1];
      nr[at + 2] = c * nz - s * nx;
      cc[at] = sc[i];
      cc[at + 1] = sc[i + 1];
      cc[at + 2] = sc[i + 2];
    };
    if (indexed) {
      for (let v = 0; v < sp.length / 3; v++) put(o + v * 3, v);
      const base = o / 3, ix = this.index;
      for (let k = 0; k < si.length; k++) ix[this.ni + k] = base + si[k];
      this.ni += si.length;
    } else {
      for (let k = 0; k < si.length; k++) put(o + k * 3, si[k]);
    }
    this.n = o + floats;
  }

  /**
   * A model tree to stand on this chunk (TerrainRenderer.plantTrees turns
   * them into instances at commit): species index, trunk foot, yaw, scale.
   * Flat numbers, like the quads, so a forest allocates one growing array.
   */
  plant(species, x, y, z, yaw, scale) {
    (this.trees ??= []).push(species, x, y, z, yaw, scale);
  }

  /** True until something is drawn into it. */
  get empty() {
    return this.n === 0;
  }

  build() {
    // slice(), not subarray(): a finished chunk sits in the scene until you
    // fly out of range, and handing it a view would pin up to twice the
    // geometry's own size in growth slack for that whole time.
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(this.positions.slice(0, this.n), 3));
    geo.setAttribute("normal", new THREE.BufferAttribute(this.normals.slice(0, this.n), 3));
    geo.setAttribute("color", new THREE.BufferAttribute(this.colors.slice(0, this.n), 3));
    if (this.index) {
      // Two bytes an index wherever the vertices fit, which is nearly every chunk.
      const ix = this.index.subarray(0, this.ni);
      geo.setIndex(new THREE.BufferAttribute(this.n / 3 > 65535 ? ix.slice() : Uint16Array.from(ix), 1));
    }
    return geo;
  }
}

/**
 * A tree model, made ready to bake into a chunk (GeomBuilder.pour): its
 * vertices (position, normal, colour) and its triangle index, as plain
 * arrays the builder copies from.
 *
 * The art paints both faces of every part (paintMaterialDS); the chunk's
 * terrainMaterial paints one. The crowns are closed hulls, so their inside
 * faces are never seen and one side is all they need; but the LOD2 trunks
 * are crossed cards and a spruce's skirt is an open cone, which vanish from
 * behind under one-sided paint. So any triangle with an open edge (an edge
 * no other triangle shares, welded by position) is laid twice, the second
 * time wound the other way on copies of its vertices with the normals turned
 * round: the double-sided look for a few triangles a tree, with no second
 * material and no second draw.
 */
export function bakeRecord(geo) {
  const pos = geo.getAttribute("position"), nor = geo.getAttribute("normal"), col = geo.getAttribute("color");
  const src = geo.index ? geo.index.array : Array.from({ length: pos.count }, (_, i) => i);
  const key = (v) => `${pos.getX(v).toFixed(4)},${pos.getY(v).toFixed(4)},${pos.getZ(v).toFixed(4)}`;
  const edge = (a, b) => (a < b ? `${a}|${b}` : `${b}|${a}`);
  const edges = new Map();
  const tris = [];
  for (let t = 0; t < src.length; t += 3) {
    const v = [src[t], src[t + 1], src[t + 2]];
    const k = v.map(key);
    const e = [edge(k[0], k[1]), edge(k[1], k[2]), edge(k[2], k[0])];
    for (const id of e) edges.set(id, (edges.get(id) ?? 0) + 1);
    tris.push({ v, e });
  }
  const verts = []; // [source vertex, normal sign]
  for (let v = 0; v < pos.count; v++) verts.push([v, 1]);
  const turned = new Map(); // source vertex -> its turned-round copy
  const flip = (s) => {
    if (!turned.has(s)) turned.set(s, verts.push([s, -1]) - 1);
    return turned.get(s);
  };
  // Which way a triangle's winding faces against the way its normals point.
  const facing = ([a, b, c]) => {
    const ax = pos.getX(a), ay = pos.getY(a), az = pos.getZ(a);
    const ux = pos.getX(b) - ax, uy = pos.getY(b) - ay, uz = pos.getZ(b) - az;
    const vx = pos.getX(c) - ax, vy = pos.getY(c) - ay, vz = pos.getZ(c) - az;
    const fx = uy * vz - uz * vy, fy = uz * vx - ux * vz, fz = ux * vy - uy * vx;
    let d = 0;
    for (const s of [a, b, c]) d += fx * nor.getX(s) + fy * nor.getY(s) + fz * nor.getZ(s);
    return d;
  };
  const index = [];
  let doubled = 0;
  for (const { v, e } of tris) {
    if (!e.some((id) => edges.get(id) === 1)) {
      index.push(v[0], v[1], v[2]);
      continue;
    }
    // A card: each of its two faces lit by the normal on ITS side, the way
    // a double-sided material turns the normal for a back face — so the
    // card the art wound against its own normals isn't dark from the front.
    const fwd = facing(v) >= 0;
    const a = fwd ? v : v.map(flip);
    const b = fwd ? v.map(flip) : v;
    index.push(a[0], a[1], a[2], b[0], b[2], b[1]);
    doubled++;
  }
  const n = verts.length * 3;
  const positions = new Float32Array(n), normals = new Float32Array(n), colors = new Float32Array(n);
  verts.forEach(([v, sgn], i) => {
    const o = i * 3;
    positions[o] = pos.getX(v); positions[o + 1] = pos.getY(v); positions[o + 2] = pos.getZ(v);
    normals[o] = sgn * nor.getX(v); normals[o + 1] = sgn * nor.getY(v); normals[o + 2] = sgn * nor.getZ(v);
    colors[o] = col ? col.getX(v) : 1; colors[o + 1] = col ? col.getY(v) : 1; colors[o + 2] = col ? col.getZ(v) : 1;
  });
  return { positions, normals, colors, index: Uint32Array.from(index), triangles: index.length / 3, doubled };
}

// The box models' material: every merged-box stand-in in the game (bases,
// vehicles, towns, the chunks' own clutter and bridges) is vertex-coloured
// Lambert through this one shared instance.
const terrainMaterial = new THREE.MeshLambertMaterial({ vertexColors: true });

// --- the ground mesh -------------------------------------------------------

// The air map's roads, painted per pixel onto whatever material draws the
// ground: a 12 m corner grid can't hold an 18 m road's edges in its vertex
// colours, so the fragment measures its own distance to the network (the
// same segments as roadDistance) and lays asphalt, gravel shoulders and a
// worn centre line over the relief. Shared by the classic Lambert ground and
// tier 2's splat (terrain_splat.js).
export const ROAD_PAINT_GLSL = /* glsl */ `
uniform vec4 uRoadNet; // x: base-corner coordinate, y: ring radius, z: half width, w: bridge deck height
float roadSegDist(vec2 p, vec2 a, vec2 b) {
  vec2 ab = b - a;
  float t = clamp(dot(p - a, ab) / dot(ab, ab), 0.0, 1.0);
  return length(a + ab * t - p);
}
float roadDist(vec2 p) {
  float d = uRoadNet.x;
  return min(min(roadSegDist(p, vec2(-d, -d), vec2(d, d)), roadSegDist(p, vec2(-d, d), vec2(d, -d))),
             abs(length(p) - uRoadNet.y));
}
// rgb: the road surface here; a: how much of it (0 off the road).
vec4 roadPaint(vec3 wp) {
  float rd = roadDist(wp.xz);
  float hw = uRoadNet.z;
  float w = 1.0 - smoothstep(hw - 0.6, hw + 0.6, rd);
  w *= 1.0 - smoothstep(94.0, 98.0, wp.y); // no road over the rock line
  // Below the deck the trestle carries the road, over the water and the
  // beach alike: the sand under a bridge stays sand.
  w *= step(uRoadNet.w, wp.y);
  vec3 asphalt = vec3(0.32, 0.32, 0.33);
  vec3 gravel = vec3(0.44, 0.4, 0.33);
  vec3 c = mix(asphalt, gravel, smoothstep(hw - 3.0, hw - 2.2, rd));
  // Worn dashes of paint down the middle, a dash every 12 m.
  float dash = step(0.5, fract((wp.x + wp.z) * (1.0 / 12.0))) * (1.0 - smoothstep(0.6, 1.0, rd));
  c = mix(c, vec3(0.78, 0.74, 0.56), dash * 0.8);
  return vec4(c, w);
}
`;

/** The road network's shader uniform, from the world config. */
export function roadNetUniform(field) {
  return {
    value: new THREE.Vector4(field.radius * 0.72 * Math.SQRT1_2, field.radius * 0.34, ROAD_HALF_WIDTH, field.waterLevel + field.blockHeight),
  };
}

// The classic tier's ground: vertex colours under Lambert light (smooth
// normals now — the relief shades like a hillside, not a staircase), with
// the air map's roads painted on top. One material per renderer (the road
// uniform is the map's).
function classicGroundMaterial(roadField) {
  const mat = new THREE.MeshLambertMaterial({ vertexColors: true });
  if (!roadField) return mat;
  const uRoadNet = roadNetUniform(roadField);
  mat.onBeforeCompile = (sh) => {
    sh.uniforms.uRoadNet = uRoadNet;
    sh.vertexShader = sh.vertexShader
      .replace("#include <common>", "#include <common>\nvarying vec3 vGroundPos;")
      .replace("#include <begin_vertex>", "#include <begin_vertex>\nvGroundPos = (modelMatrix * vec4(transformed, 1.0)).xyz;");
    sh.fragmentShader = sh.fragmentShader
      .replace("#include <common>", "#include <common>\nvarying vec3 vGroundPos;\n" + ROAD_PAINT_GLSL)
      .replace(
        "#include <color_fragment>",
        "#include <color_fragment>\n{ vec4 rp = roadPaint(vGroundPos); diffuseColor.rgb = mix(diffuseColor.rgb, rp.rgb, rp.a); }",
      );
  };
  mat.customProgramCacheKey = () => "va-ground-roads";
  return mat;
}

/**
 * How deep a chunk edge's skirt must hang: whatever detail its neighbour is
 * drawn at (every corner, every second, every fourth), the crack between the
 * two edges is at most the widest gap between those three polylines along
 * the shared edge — measured here on the full-detail corners, plus a margin.
 * A flat plain skirts by a couple of metres; a hill shoulder by as many as
 * its curvature needs, so no crack ever shows the sky or the sea through it.
 */
function chunkSkirtDepth(f, ix0, iz0, dx, dz) {
  const F = chunkSkirtDepth.F ??= new Float64Array(CHUNK_CELLS + 1);
  for (let t = 0; t <= CHUNK_CELLS; t++) F[t] = f.cornerHeight(ix0 + dx * t, iz0 + dz * t);
  let worst = 0;
  for (let t = 0; t <= CHUNK_CELLS; t++) {
    const r2 = t & 1, r4 = t & 3;
    const c2 = r2 ? (F[t - 1] + F[t + 1]) * 0.5 : F[t];
    const c4 = r4 ? F[t - r4] + ((F[t - r4 + 4] - F[t - r4]) * r4) / 4 : F[t];
    worst = Math.max(worst, Math.abs(F[t] - c2), Math.abs(F[t] - c4), Math.abs(c2 - c4));
  }
  return worst + 2;
}

/**
 * A grid of ground corners as one indexed mesh: (n+1)² vertices `gstep`
 * grid cells apart starting at corner (gx0, gz0), heights from `H` — the
 * lattice with a one-corner border all round, (n+3)², which the smooth
 * normals difference across. Every quad splits on its (x0,z0)-(x1,z1)
 * diagonal, the same as terrain_core.js groundAt, so at full detail these
 * ARE the collision triangles. `skirts` ([south, east, north, west] depths)
 * hangs a curtain from each edge. Quads (and curtains) wholly under the sea
 * are left out; `dropWet` draws that line a metre down instead of a quarter,
 * for the far field, which rides a metre and a half under the true ground.
 */
export function buildGroundGeometry(f, land, H, n, gx0, gz0, gstep, skirts = null, dropWet = false) {
  const cs = f.cellSize;
  const sp = gstep * cs;
  const N = n + 3;
  const row = n + 1;
  const V0 = row * row;
  const V = V0 + (skirts ? 4 * row : 0);
  const pos = new Float32Array(V * 3), nor = new Float32Array(V * 3), col = new Float32Array(V * 3);
  const c = [0, 0, 0];
  const inv2 = 1 / (2 * sp);
  const seed = (f.seed + 77) >>> 0;
  const seedMv = (f.seed + 6203) >>> 0, seedPatch = (f.seed + 6211) >>> 0;
  for (let j = 0; j <= n; j++) {
    for (let i = 0; i <= n; i++) {
      const o = (j * row + i) * 3;
      const at = (j + 1) * N + i + 1;
      const h = H[at];
      const hx = (H[at + 1] - H[at - 1]) * inv2;
      const hz = (H[at + N] - H[at - N]) * inv2;
      const il = 1 / Math.sqrt(hx * hx + hz * hz + 1);
      const ix = gx0 + i * gstep, iz = gz0 + j * gstep;
      pos[o] = ix * cs; pos[o + 1] = h; pos[o + 2] = iz * cs;
      nor[o] = -hx * il; nor[o + 1] = il; nor[o + 2] = -hz * il;
      const x = ix * cs, z = iz * cs;
      groundColor(
        c, h, 1 - il, hash2(ix, iz, seed), land,
        fbm(x * (1 / 420) + 3.1, z * (1 / 420) - 7.3, seedMv, 2),
        fbm(x * (1 / 250) + 11.7, z * (1 / 250) - 5.9, seedPatch, 2),
      );
      col[o] = c[0]; col[o + 1] = c[1]; col[o + 2] = c[2];
    }
  }
  // The sea floor is not drawn: under the translucent sea a floor that only
  // the coastal chunks carried showed the chunk grid through the water as
  // lighter and darker squares. So a quad wholly under water is left out
  // (the sea's own floor slab lies under it all), and only the shore's
  // crossing quads reach down into the shallows — the classic look, where
  // an awash column never drew either.
  const wet = f.waterLevel - (dropWet ? 1 : 0.25);
  const idx = [];
  for (let j = 0; j < n; j++) {
    for (let i = 0; i < n; i++) {
      const a = j * row + i, b = a + 1, d = a + row, cc = d + 1;
      if (pos[a * 3 + 1] < wet && pos[b * 3 + 1] < wet && pos[cc * 3 + 1] < wet && pos[d * 3 + 1] < wet) continue;
      idx.push(a, cc, b, a, d, cc); // (00,11,10) and (00,01,11): up-facing
    }
  }
  if (skirts) {
    // Edge runs, each in increasing grid order, with whether the curtain's
    // (p, q, p') winding already faces out of the chunk.
    const edges = [
      [(t) => t, skirts[0], true], // south (z0), faces -Z
      [(t) => t * row + n, skirts[1], true], // east (x1), faces +X
      [(t) => n * row + t, skirts[2], false], // north (z1), faces +Z
      [(t) => t * row, skirts[3], false], // west (x0), faces -X
    ];
    let v = V0;
    for (const [at, depth, out] of edges) {
      const base = v;
      for (let t = 0; t <= n; t++, v++) {
        const src = at(t) * 3, o = v * 3;
        pos[o] = pos[src]; pos[o + 1] = pos[src + 1] - depth; pos[o + 2] = pos[src + 2];
        nor[o] = nor[src]; nor[o + 1] = nor[src + 1]; nor[o + 2] = nor[src + 2];
        col[o] = col[src]; col[o + 1] = col[src + 1]; col[o + 2] = col[src + 2];
      }
      for (let t = 0; t < n; t++) {
        const p = at(t), q = at(t + 1), p2 = base + t, q2 = base + t + 1;
        if (pos[p * 3 + 1] < wet && pos[q * 3 + 1] < wet) continue; // a curtain under the sea
        if (out) idx.push(p, q, p2, q, q2, p2);
        else idx.push(p, p2, q, q, p2, q2);
      }
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
  geo.setAttribute("normal", new THREE.BufferAttribute(nor, 3));
  geo.setAttribute("color", new THREE.BufferAttribute(col, 3));
  geo.setIndex(new THREE.BufferAttribute(V > 65535 ? new Uint32Array(idx) : new Uint16Array(idx), 1));
  return geo;
}

/**
 * The ground a chunk at detail k actually draws under (x, z): the same
 * triangle rule as terrain_core.js groundAt, on corners `step` cells apart.
 * At full detail (step 1) it IS groundAt, float for float.
 */
function groundAtStep(f, x, z, step) {
  const S = step * f.cellSize;
  const gx = x / S, gz = z / S;
  const fx = Math.floor(gx), fz = Math.floor(gz);
  const u = gx - fx, v = gz - fz;
  const h00 = f.cornerHeight(fx * step, fz * step);
  const h11 = f.cornerHeight((fx + 1) * step, (fz + 1) * step);
  if (u >= v) {
    const h10 = f.cornerHeight((fx + 1) * step, fz * step);
    return h00 + (h10 - h00) * u + (h11 - h10) * v;
  }
  const h01 = f.cornerHeight(fx * step, (fz + 1) * step);
  return h00 + (h11 - h01) * u + (h01 - h00) * v;
}

export class TerrainRenderer {
  constructor(scene, field) {
    this.scene = scene;
    this.field = field;
    // The naval theater has no road network — and so no trestle bridges where
    // roads would cross water. They belong to the air map's land war. The
    // land-combat theater likewise skips them: its towns and forests come
    // from the shared deterministic terrain functions instead.
    this.naval = field.theater === "naval";
    // The combined-arms Brick Gulf renders with the land theater's steppe
    // palette and deterministic forests (its sea reads as sea because the
    // water slab covers everything below the waterline); like naval/land it
    // has no road network, so no bridges.
    this.land = field.theater === "land" || field.theater === "armada";
    // A disc the lone trees keep out of: Brickton's streets (main.js sets
    // it from config.brickton before the first chunk builds). Null = none.
    this.clearing = null;
    this.chunks = new Map(); // "cx,cz" -> Mesh (or a marker for all-ocean)
    this.queue = [];
    this.queued = new Set();
    this.chunkSpan = CHUNK_CELLS * field.cellSize;
    // How far the ring reaches: VIEW_DIST, or VIEW_DIST_NEAR while the frame
    // governor has pulled it in (setViewDist).
    this.viewDist = VIEW_DIST;
    // Only the air map paints a road network (and bridges it over the water).
    this.roads = !this.naval && !this.land;
    // The ground's material: the classic vertex-coloured relief until tier 2
    // hands over its splat (setGroundMaterial).
    this.groundMaterial = classicGroundMaterial(this.roads ? field : null);
    this.farMesh = null;
    this.lastCx = null;
    this.lastCz = null;
    this.lastY = 0;
    // The chunk build currently in flight, kept across frames: a resumable
    // generator plus the key it will commit to when it finishes.
    this.active = null;
    this.activeKey = null;
    this.activeHole = false;
    // Ground height at each chunk's center, for the through-the-air detail
    // distance. The heightfield never changes, so one sample per chunk lasts
    // the round.
    this.groundY = new Map();
    // The Gulf is a 9 km gunnery map, but detailed chunks stream only within
    // VIEW_DIST — so from a ship's bridge Fort Brick was fort walls standing
    // in open water until you closed to 2.4 km. A one-time coarse far-field
    // grid gives every distant landmass its silhouette (sitting a little
    // below true height, so the streamed chunks pave over it as you close).
    if (field.theater === "armada") this.buildFarField();
    // Every theater's trees stand as models (Part 6d) once the five species
    // are in — the land theaters' forests and the lone trees on the air and
    // naval maps' grass alike; until then — and under ?boxes — the voxel trees.
    this.treeModels = null;
    // A phone's and a software rasterizer's trees: each species' LOD2,
    // flattened for baking into the chunks (bakeRecord) once it lands.
    this.treeBake = null;
    this.forests = new ForestRegions(scene);
    // The metre-scale ground clutter, instanced around the camera.
    this.clutter = GROUND_CLUTTER > 0 ? new GroundClutter(scene, field, this.land, this.roads) : null;
    const trees = TREES_FORCED ?? (softwareRenderer() ? "voxel" : MODEL_TREES ? "model" : "baked");
    if (trees === "model" && modelsEnabled()) this.loadTrees();
    else if (trees === "baked" && modelsEnabled()) this.loadBakedTrees();
  }

  /** The lean tiers' trees: only the five LOD2 files, a few KB each. */
  loadBakedTrees() {
    Promise.all(TREE_SPECIES.map((s) => loadModel(`${s}_lod2`).catch(() => null))).then((tpls) => {
      if (tpls.some((t) => !t)) return; // a species never came: boxes it is
      this.treesBaked(tpls.map((t) => bakeRecord(bakedGeometry(t).geometry)));
    });
  }

  /**
   * The baked species are in (`recs[species]`, bakeRecord's): ground built
   * with voxel trees before they landed rebuilds now, exactly as the
   * instanced models replant (treesLanded).
   */
  treesBaked(recs) {
    this.treeBake = recs;
    this.replantBoxTrees();
  }

  /** Queue a rebuild of every chunk still standing voxel trees. */
  replantBoxTrees() {
    for (const [key, mesh] of this.chunks) {
      if (!mesh.userData?.boxTrees || this.queued.has(key) || key === this.activeKey) continue;
      const [cx, cz] = key.split(",").map(Number);
      this.queued.add(key);
      this.queue.push([cx, cz, key]);
    }
  }

  loadTrees() {
    Promise.all(TREE_SPECIES.map((s) => loadLevels(s))).then((all) => {
      if (all.some((l) => !l)) return; // a species never came: boxes it is
      this.treesLanded(
        all.map((tpls) =>
          [0, 1, 2].map((lv) => {
            let use = lv;
            while (use > 0 && !tpls[use]) use--;
            return bakedGeometry(tpls[use]).geometry;
          }),
        ),
      );
    });
  }

  /**
   * The five species are in: `models[species][level]` is each one's LOD
   * geometry. Ground built with box trees before they landed replants now,
   * each chunk swapped whole on the frame its rebuild finishes. (The chunk
   * in flight queues its own replant when it lands: chunkTask.)
   */
  treesLanded(models) {
    this.treeModels = models;
    this.replantBoxTrees();
  }

  /**
   * Turn a finished chunk's planted trees (GeomBuilder.plant) into its
   * stands: one block of instance matrices per species, kept on the chunk
   * (userData.trees) for its forest region to batch when it commits. Their
   * detail follows the chunk's: full trees in the finest shells, the
   * simplified crowns beyond (Part 3.1).
   */
  plantTrees(mesh, g, lod) {
    const T = this.treeModels;
    const rec = g.trees;
    if (!T || !rec?.length) return;
    const lv = lod >= LOD_FULL ? 0 : lod >= 2 ? 1 : 2;
    const counts = new Array(T.length).fill(0);
    for (let i = 0; i < rec.length; i += 6) counts[rec[i]]++;
    const blocks = counts.map((n) => (n ? new Float32Array(n * 16) : null));
    const fill = new Array(T.length).fill(0);
    const m = new THREE.Matrix4(), q = new THREE.Quaternion(), p = new THREE.Vector3(), sc = new THREE.Vector3();
    const up = new THREE.Vector3(0, 1, 0);
    for (let i = 0; i < rec.length; i += 6) {
      const sp = rec[i];
      m.compose(p.set(rec[i + 1], rec[i + 2], rec[i + 3]), q.setFromAxisAngle(up, rec[i + 4]), sc.setScalar(rec[i + 5]));
      m.toArray(blocks[sp], 16 * fill[sp]++);
    }
    mesh.userData.trees = [];
    counts.forEach((n, sp) => {
      if (!n) return;
      const geo = T[sp][lv];
      if (!geo.boundingSphere) geo.computeBoundingSphere();
      const sphere = new THREE.Sphere(), one = new THREE.Sphere();
      for (let i = 0; i < n; i++) sphere.union(one.copy(geo.boundingSphere).applyMatrix4(m.fromArray(blocks[sp], 16 * i)));
      mesh.userData.trees.push({ species: sp, level: lv, geometry: geo, count: n, matrices: blocks[sp], sphere });
    });
  }

  /**
   * The armada's far field: one coarse grid of the whole map (corners 120 m
   * apart, open sea left out) under the streamed chunks, so every distant
   * landmass has its silhouette from a ship's bridge.
   */
  buildFarField() {
    const f = this.field;
    const S = 10; // grid cells per far-field corner step: 120 m
    const n = 2 * (Math.ceil(f.radius / (S * f.cellSize)) + 1);
    const g0 = -(n / 2) * S; // corner index of the far field's first corner
    const N = n + 3;
    const sink = 1.5; // ride under the real chunks, never z-fight them
    const H = new Float64Array(N * N);
    for (let j = 0; j < N; j++) {
      for (let i = 0; i < N; i++) {
        H[j * N + i] = f.heightAt((g0 + (i - 1) * S) * f.cellSize, (g0 + (j - 1) * S) * f.cellSize) - sink;
      }
    }
    const mesh = new THREE.Mesh(buildGroundGeometry(f, this.land, H, n, g0, g0, S, null, true), this.groundMaterial);
    mesh.matrixAutoUpdate = false;
    mesh.receiveShadow = GFX >= 2;
    this.farMesh = mesh;
    this.scene.add(mesh);
    settle(mesh);
  }

  /** Tier 2 hands over its splat: every chunk on screen (and the far field) switches. */
  setGroundMaterial(mat) {
    this.groundMaterial = mat;
    for (const m of this.chunks.values()) {
      if (!m.isMesh) continue;
      m.material = mat;
      for (const c of m.children) if (c.userData.deck) c.material = mat; // the bridges' running surface
    }
    if (this.farMesh) this.farMesh.material = mat;
  }

  /**
   * Ground height at a chunk's center, cached forever. The heightfield is a
   * pure function of the seed, so this is one sample per chunk for the round.
   */
  chunkGroundY(cx, cz, key) {
    let y = this.groundY.get(key);
    if (y === undefined) {
      const span = this.chunkSpan;
      // Clamp to the waterline: a chunk of open sea is ground you fly OVER at
      // sea level, not ground 30 m down on the sea floor.
      y = Math.max(
        this.field.waterLevel,
        this.field.groundAt(cx * span + span / 2, cz * span + span / 2),
      );
      this.groundY.set(key, y);
    }
    return y;
  }

  /**
   * How far the camera is from a chunk, in meters, THROUGH THE AIR: the
   * nearest point of the chunk's footprint, and the camera's height above the
   * ground there. This is what the detail shells measure, so detail tracks
   * how big a block would actually look rather than how many chunks away it
   * sits — a tank at 2 m and a bomber at 2 km do not want the same ground.
   */
  chunkDistance(cx, cz, key, p) {
    const span = this.chunkSpan;
    const x0 = cx * span, z0 = cz * span;
    const dx = p.x < x0 ? x0 - p.x : p.x > x0 + span ? p.x - x0 - span : 0;
    const dz = p.z < z0 ? z0 - p.z : p.z > z0 + span ? p.z - z0 - span : 0;
    const dy = (p.y ?? 0) - this.chunkGroundY(cx, cz, key);
    return Math.sqrt(dx * dx + dz * dz + dy * dy);
  }

  /** Detail tier for a chunk that far away: the innermost shell reached wins. */
  lodAtDistance(d) {
    for (const t of LOD_TIERS) if (d <= t.dist) return t.k;
    return 1;
  }

  update(center) {
    const span = this.chunkSpan;
    const ccx = Math.floor(center.x / span);
    const ccz = Math.floor(center.z / span);
    const r = Math.ceil(this.viewDist / span);
    // Chunks are kept two past the ring, so flying back over a boundary
    // doesn't rebuild what just left. A ring pulled in to shed draws keeps
    // one: a two-chunk margin would keep most of what it was pulled in to shed.
    const keep = r + (this.viewDist < VIEW_DIST ? 1 : 2);
    const cy = center.y ?? 0;

    // Scan, sort, and cull only when the camera crosses a chunk boundary or
    // changes altitude enough to move the shells; doing the full walk + queue
    // sort every frame costs steady jitter.
    if (ccx !== this.lastCx || ccz !== this.lastCz || Math.abs(cy - this.lastY) > LOD_ALT_STEP) {
      this.lastCx = ccx;
      this.lastCz = ccz;
      this.lastY = cy;
      for (let dz = -r; dz <= r; dz++) {
        for (let dx = -r; dx <= r; dx++) {
          const key = `${ccx + dx},${ccz + dz}`;
          // Not on screen, not already waiting, and not the one half-built in
          // this frame's hands — a chunk in flight is not in `chunks` yet.
          if (!this.chunks.has(key) && !this.queued.has(key) && key !== this.activeKey) {
            this.queued.add(key);
            this.queue.push([ccx + dx, ccz + dz, key]);
          }
        }
      }
      // Detail sweep: a cached chunk whose shell changed queues a rebuild —
      // and KEEPS ITS CURRENT GEOMETRY ON SCREEN until the replacement is
      // finished (see commitChunk). Dropping it first, which is what this
      // used to do, opened a 288 m square of nothing down onto the ocean slab
      // for as long as the rebuild took; from a cockpit that reads as the
      // landscape popping out of a flat blue tile, which is the whole bug.
      // Ocean chunks are the same at any detail and never rebuild.
      if (LOD_TIERS.length) {
        for (const [key, mesh] of this.chunks) {
          if (mesh.userData?.ocean || this.queued.has(key) || key === this.activeKey) continue;
          const [cx, cz] = key.split(",").map(Number);
          const d = this.chunkDistance(cx, cz, key, center);
          const want = this.lodAtDistance(d);
          const have = mesh.userData?.lod ?? 1;
          if (want === have) continue;
          // Coarsening waits until the chunk is comfortably past the shell
          // that granted its detail, so a boundary never thrashes.
          if (want < have && this.lodAtDistance(d / LOD_HYSTERESIS) >= have) continue;
          this.queued.add(key);
          this.queue.push([cx, cz, key]);
        }
      }
      // Holes first, then refinements, each nearest-first. A missing chunk is
      // a flat square where the ground should be; a coarse one is already
      // ground. Never make a pilot wait on the second while the first is open.
      const rank = (e) =>
        Math.abs(e[0] - ccx) + Math.abs(e[1] - ccz) + (this.chunks.has(e[2]) ? 1e4 : 0);
      this.queue.sort((a, b) => rank(a) - rank(b));
      // Drop chunks well outside the view ring.
      for (const [key, mesh] of this.chunks) {
        const [cx, cz] = key.split(",").map(Number);
        if (Math.abs(cx - ccx) > keep || Math.abs(cz - ccz) > keep) {
          this.scene.remove(mesh);
          disposeChunk(mesh);
          this.chunks.delete(key);
          this.forests.set(key, null);
          if (key === this.activeKey) this.abandonActive(); // it flew out from under us
        }
      }
    }

    // Build under a strict per-frame ration. The build in flight is resumable,
    // so the pump stops the instant the ration is gone — mid-chunk if it has
    // to — and no single chunk can cost a frame however dense it is.
    const holeFirst =
      this.active ? this.activeHole : !!this.queue.length && !this.chunks.has(this.queue[0][2]);
    const budget = holeFirst ? BUILD_BUDGET_HOLE_MS : BUILD_BUDGET_MS;
    const t0 = performance.now();
    while (performance.now() - t0 < budget) {
      if (!this.active) {
        const next = this.queue.shift();
        if (!next) break;
        const [cx, cz, key] = next;
        this.queued.delete(key);
        if (Math.abs(cx - ccx) > r || Math.abs(cz - ccz) > r) continue;
        const have = this.chunks.get(key);
        const want = this.lodAtDistance(this.chunkDistance(cx, cz, key, center));
        // The camera has moved since this was queued: skip a rebuild the
        // world no longer wants (an upgrade you already flew back out of).
        const replant = have?.userData.boxTrees && (this.treeModels || this.treeBake);
        if (have && (have.userData.ocean || ((have.userData.lod ?? 1) === want && !replant))) continue;
        this.active = this.chunkTask(cx, cz, key, want);
        this.activeKey = key;
        this.activeHole = !have;
      }
      if (this.active.next().done) this.abandonActive();
    }
    // Every region a commit or a retirement touched this frame re-deals its
    // batches once, however many of its chunks changed.
    this.forests.flush();
    this.clutter?.update(center);
  }

  /**
   * Reach the ring out to `m` metres. The next update rescans as if the
   * camera had crossed a chunk boundary: a narrower ring retires its outer
   * chunks (and re-deals their forests) at once, a wider one queues them
   * behind any holes, nearest first, on the usual build ration.
   */
  setViewDist(m) {
    if (m === this.viewDist) return;
    this.viewDist = m;
    this.lastCx = null;
  }

  /** Forget the build in flight — finished, or overtaken by the camera. */
  abandonActive() {
    this.active = null;
    this.activeKey = null;
    this.activeHole = false;
  }

  /**
   * Put a freshly built chunk on screen, retiring whatever stood there.
   *
   * The swap is the point: a chunk changing detail is replaced in a single
   * step, on the frame its new geometry is ready, so there is never a moment
   * where that square of the world is simply absent.
   */
  commitChunk(key, obj) {
    const prev = this.chunks.get(key);
    if (prev) {
      if (prev.parent) this.scene.remove(prev);
      disposeChunk(prev);
    }
    this.chunks.set(key, obj);
    this.forests.set(key, obj.userData.trees);
    if (obj.isMesh) {
      obj.receiveShadow = GFX >= 2; // tier 2's sun shadow lands on the ground
      this.scene.add(obj);
      settle(obj); // the ground doesn't move: the walk places it once (models.js WorldScene)
    }
  }

  /**
   * Build one chunk to completion, right now. The streamer runs the
   * generator underneath instead, a slice of a frame at a time; this is the
   * front door for the tests and for anything that wants ground immediately.
   */
  buildChunk(chunkX, chunkZ, key, lod = 1) {
    const task = this.chunkTask(chunkX, chunkZ, key, lod);
    while (!task.next().done);
    this.forests.flush();
  }

  /**
   * One chunk, as a RESUMABLE task: it yields between rows, so the streamer
   * can spend exactly the milliseconds it has and come back for the rest
   * next frame. `lod` is the detail k (LOD_FULL = every grid corner, the
   * collision ground itself, as is anything finer; 2 and 1 take every second
   * and fourth corner).
   * The ground is one grid mesh with its skirts; the baked trees ride the
   * 12 m cells on a child mesh, standing on the ground this tier draws, and
   * the bridges' timber shares it (their running surface is a second child,
   * in the ground's own material).
   */
  *chunkTask(chunkX, chunkZ, key, lod = 1) {
    const f = this.field;
    const cs = f.cellSize;
    const step = LOD_FULL / Math.min(lod, LOD_FULL);
    const n = CHUNK_CELLS / step;
    const ix0 = chunkX * CHUNK_CELLS, iz0 = chunkZ * CHUNK_CELLS;
    const wl = f.waterLevel;

    // Dry land anywhere in the chunk, judged on EVERY corner whatever the
    // detail — an islet between the coarse corners must not mark its chunk
    // as open sea, which is never rebuilt.
    let dry = false;
    for (let j = 0; j <= CHUNK_CELLS && !dry; j++) {
      for (let i = 0; i <= CHUNK_CELLS; i++) {
        if (f.cornerHeight(ix0 + i, iz0 + j) > wl) { dry = true; break; }
      }
    }
    yield;

    // The corner lattice at this detail, with a one-corner border for the
    // normals.
    const N = n + 3;
    const H = new Float64Array(N * N);
    if (dry) {
      for (let j = -1; j <= n + 1; j++) {
        for (let i = -1; i <= n + 1; i++) H[(j + 1) * N + i + 1] = f.cornerHeight(ix0 + i * step, iz0 + j * step);
        yield;
      }
    }

    // The bridges on this square, where a road runs lower than the deck;
    // then clutter and trees on the dry cells.
    const g = new GeomBuilder(64, { indexed: true });
    const paved = new GeomBuilder(16);
    const at = (x, z) => groundAtStep(f, x, z, step);
    const bridged = this.roads && (yield* buildBridges(g, paved, f, ix0 * cs, iz0 * cs, CHUNK_CELLS * cs, at, lod));
    yield;
    for (let lz = 0; lz < CHUNK_CELLS; lz++) {
      for (let lx = 0; lx < CHUNK_CELLS; lx++) {
        const cx = ix0 + lx, cz = iz0 + lz;
        const x0 = cx * cs, z0 = cz * cs;
        const xm = x0 + cs / 2, zm = z0 + cs / 2;
        if (!dry) continue;
        const rd = this.roads ? roadDistance(f, xm, zm) : Infinity;
        const top = f.groundAt(xm, zm);
        if (top <= wl) continue;
        this.decorateCell(g, cx, cz, x0, z0, top, rd, at);
      }
      yield;
    }

    if (!dry && !bridged) {
      // All-ocean chunk: remember it with a dummy entry so we skip rebuilds.
      const dummy = new THREE.Object3D();
      dummy.userData.ocean = true; // same at any LOD: never rebuild
      this.commitChunk(key, dummy);
      return;
    }
    const skirts = dry
      ? [
          chunkSkirtDepth(f, ix0, iz0, 1, 0),
          chunkSkirtDepth(f, ix0 + CHUNK_CELLS, iz0, 0, 1),
          chunkSkirtDepth(f, ix0, iz0 + CHUNK_CELLS, 1, 0),
          chunkSkirtDepth(f, ix0, iz0, 0, 1),
        ]
      : null;
    const mesh = new THREE.Mesh(
      dry ? buildGroundGeometry(f, this.land, H, n, ix0, iz0, step, skirts) : new THREE.BufferGeometry(),
      this.groundMaterial,
    );
    mesh.matrixAutoUpdate = false;
    mesh.userData.lod = lod;
    mesh.userData.boxTrees = !!g.boxTrees;
    if (!g.empty) {
      const deco = new THREE.Mesh(g.build(), terrainMaterial);
      deco.matrixAutoUpdate = false;
      deco.receiveShadow = GFX >= 2;
      mesh.add(deco);
    }
    if (!paved.empty) {
      // The bridges' running surface, drawn by the ground's own material so
      // the road's paint and light carry on over the water unchanged.
      const deck = new THREE.Mesh(paved.build(), this.groundMaterial);
      deck.matrixAutoUpdate = false;
      deck.userData.deck = true;
      deck.receiveShadow = GFX >= 2;
      mesh.add(deck);
    }
    this.plantTrees(mesh, g, lod);
    this.commitChunk(key, mesh);
    // The models landed while this chunk was still going up, so it stands
    // with voxel trees — and the replant sweep (treesLanded) skips the chunk
    // in flight. Queue its replant now, or it keeps a 288 m square of box
    // trees until its detail next changes.
    if (g.boxTrees && (this.treeModels || this.treeBake) && !this.queued.has(key)) {
      this.queued.add(key);
      this.queue.push([chunkX, chunkZ, key]);
    }
  }

  /**
   * The trees for one 12 m terrain cell. Shared by every detail tier — each
   * ELIGIBILITY roll reads `top`, the ground at the cell's centre, so a chunk
   * rebuilt at another tier grows exactly the same trees in exactly the same
   * cells (nothing pops when a shell sweeps past). Only the BASE height
   * moves: groundAt(x, z) is the ground that tier actually draws under a
   * plant's feet, so trunks stand flush on it. A tree goes in one of three
   * ways, the same placement every time: an instanced model (plant), the
   * model's LOD2 baked into the chunk (pour), or the voxel tree. (The grass,
   * boulders and flowers are GroundClutter's, around the camera.)
   */
  decorateCell(g, cx, cz, x0, z0, top, rd, groundAt) {
    const f = this.field;
    const cs = f.cellSize;
    const at = groundAt ?? (() => top);
    const stand = (sp, x, z, yaw, s) => {
      if (this.treeModels) g.plant(sp, x, at(x, z), z, yaw, s);
      else g.pour(this.treeBake[sp], x, at(x, z), z, yaw, s);
    };

    // Land theater: the forest blocks come from the shared deterministic
    // mask (the server collides with the same trees), planted densely so
    // a treeline reads as the wall it actually is, plus rare lone trees
    // out on the open steppe (scenery only — the server ignores those).
    if (this.land) {
      const xm = x0 + cs / 2, zm = z0 + cs / 2;
      const inForest = f.landForestAt(xm, zm);
      // Lone steppe trees keep out of the towns and off the bare plateaus
      // (the airfield apron, the gun pads): the forest mask already does.
      const lone =
        !inForest && hash2(cx, cz, (f.seed + 999) >>> 0) < 0.004 && top > 1.5 && top <= 60 &&
        !f.townPlan().some((t) => (xm - t.x) * (xm - t.x) + (zm - t.z) * (zm - t.z) < t.r * t.r);
      if ((inForest || lone) && (this.treeModels || this.treeBake)) {
        // Model trees (Part 6d). Species by grove, not by cell, so a stand
        // reads as a spruce block or an oak wood rather than confetti; the
        // lone steppe trees are oaks and roadside poplars. A 0.85–1.15 size
        // keeps the crowns round the 13 m canopy the sightlines assume.
        const jx = (hash2(cx, cz, (f.seed + 555) >>> 0) - 0.5) * (cs - 5);
        const jz = (hash2(cx, cz, (f.seed + 556) >>> 0) - 0.5) * (cs - 5);
        const pick = hash2(cx, cz, (f.seed + 4324) >>> 0);
        let sp;
        if (!inForest) sp = pick < 0.6 ? TREE_OAK : TREE_POPLAR;
        else if (fbm(xm / 900 + 17, zm / 900 - 5, (f.seed + 6151) >>> 0, 2) < 0.45) sp = pick < 0.6 ? TREE_SPRUCE : TREE_PINE;
        else sp = pick < 0.7 ? TREE_OAK : TREE_BIRCH;
        const s = 0.85 + hash2(cx, cz, (f.seed + 4321) >>> 0) * 0.3;
        stand(sp, xm + jx, zm + jz, hash2(cx, cz, (f.seed + 4322) >>> 0) * Math.PI * 2, s);
        if (inForest && hash2(cx, cz, (f.seed + 557) >>> 0) < 0.5) {
          // A second, younger tree in the cell thickens the canopy wall.
          stand(sp, xm - jx, zm - jz, hash2(cx, cz, (f.seed + 4325) >>> 0) * Math.PI * 2, 0.8 * s);
        }
      } else if (inForest || lone) {
        g.boxTrees = true;
        const jx = (hash2(cx, cz, (f.seed + 555) >>> 0) - 0.5) * (cs - 5);
        const jz = (hash2(cx, cz, (f.seed + 556) >>> 0) - 0.5) * (cs - 5);
        const tx = xm + jx, tz = zm + jz;
        const base = at(tx, tz);
        const th = 7 + Math.floor(hash2(cx, cz, (f.seed + 1234) >>> 0) * 5);
        const shade = 0.85 + hash2(cx, cz, (f.seed + 4321) >>> 0) * 0.3;
        const leaf = [0.16 * shade, 0.4 * shade, 0.15 * shade];
        g.box(tx, base + th / 2, tz, 1.8, th, 1.8, [0.4, 0.29, 0.17]);
        g.box(tx, base + th + 1.6, tz, 7.6, 3.6, 7.6, leaf);
        g.box(tx, base + th + 4.6, tz, 4.8, 2.8, 4.8, leaf);
        if (inForest && hash2(cx, cz, (f.seed + 557) >>> 0) < 0.5) {
          // A second trunk in the cell thickens the canopy wall.
          const bx = xm - jx, bz = zm - jz;
          const base2 = at(bx, bz);
          g.box(bx, base2 + (th - 2) / 2, bz, 1.6, th - 2, 1.6, [0.37, 0.27, 0.16]);
          g.box(bx, base2 + th - 0.6, bz, 6.4, 3.2, 6.4, leaf);
        }
      }
      return;
    }
    // Lone trees on the grass, deterministic per column — kept clear of the
    // roads (and their shoulders) so ground vehicles get a clean lane.
    const cl = this.clearing;
    const inClearing = cl && (x0 + cs / 2 - cl.x) ** 2 + (z0 + cs / 2 - cl.z) ** 2 < cl.r * cl.r;
    if (!inClearing && top > 8 && top <= 96 && rd > ROAD_HALF_WIDTH + 5 && hash2(cx, cz, (f.seed + 999) >>> 0) < 0.022) {
      const tx = x0 + cs / 2;
      const tz = z0 + cs / 2;
      if (this.treeModels || this.treeBake) {
        // Model trees on the archipelago (Part 6d's air and naval half):
        // the same cells, now oaks and birches down in the meadows and
        // spruce and Scots pine thickening up the slopes toward the scree,
        // by grove so a hillside reads as one wood rather than confetti.
        // The trees that stand by the roads are poplars, the way a country
        // highway ought to be lined. Nudged off the cell centre so neighbours
        // don't stand on the 12 m grid — but never out onto a road shoulder.
        let px = tx + (hash2(cx, cz, (f.seed + 555) >>> 0) - 0.5) * (cs - 5);
        let pz = tz + (hash2(cx, cz, (f.seed + 556) >>> 0) - 0.5) * (cs - 5);
        if (this.roads && roadDistance(f, px, pz) <= ROAD_HALF_WIDTH + 5) { px = tx; pz = tz; }
        const upland = (top - 8) / 88; // 0 on the beach grass, 1 at the treeline
        const pick = hash2(cx, cz, (f.seed + 4324) >>> 0);
        let sp;
        if (rd < ROAD_HALF_WIDTH + 40) sp = TREE_POPLAR;
        else if (fbm(tx / 900 + 17, tz / 900 - 5, (f.seed + 6151) >>> 0, 2) < 0.26 + 0.34 * upland) sp = pick < 0.6 ? TREE_SPRUCE : TREE_PINE;
        else sp = pick < 0.7 ? TREE_OAK : TREE_BIRCH;
        const s = 0.85 + hash2(cx, cz, (f.seed + 4321) >>> 0) * 0.3;
        stand(sp, px, pz, hash2(cx, cz, (f.seed + 4322) >>> 0) * Math.PI * 2, s);
        return;
      }
      // The voxel tree: everyone's until the models land (the chunk replants
      // then), and a software rasterizer's and ?boxes's for good.
      g.boxTrees = true;
      const base = at(tx, tz);
      const th = 7 + Math.floor(hash2(cx, cz, (f.seed + 1234) >>> 0) * 5);
      const round = hash2(cx, cz, (f.seed + 4321) >>> 0) < 0.5;
      g.box(tx, base + th / 2, tz, 2.2, th, 2.2, [0.42, 0.3, 0.18]);
      const leaf = [0.22, 0.5, 0.2];
      if (round) {
        g.box(tx, base + th + 2.5, tz, 7.5, 6, 7.5, leaf);
      } else {
        g.box(tx, base + th + 1.5, tz, 8.5, 3.4, 8.5, leaf);
        g.box(tx, base + th + 4.6, tz, 5.5, 3, 5.5, leaf);
        g.box(tx, base + th + 7.2, tz, 2.6, 2.6, 2.6, leaf);
      }
    }
  }

}

// --- ground clutter ----------------------------------------------------------

// The metre-scale ground clutter — tufts of tall grass, the odd boulder, a
// rare floral dab — used to be baked into every chunk of the innermost detail
// shell, 550 m out: on the land map a million vertices kept resident (35 MB
// of vertex buffer) for blades that are a pixel or two past a couple of
// hundred metres, and a whole tier of chunk rebuilds just to grow them. Now
// it is two instanced boxes drawn around the camera, out of 48 m tiles grown
// as you move: the grass to CLUTTER_GRASS_M, the boulders and flowers (big
// enough to read as landmarks) to CLUTTER_STONE_M. Near the rim each piece
// sinks into the ground in the vertex shader rather than popping, so the
// field fills in ahead of a hull like grass coming up, not a ring sweeping
// over it. Same cells, same rolls, same colours as the baked field.
const CLUTTER_TILE = 4; // cells per tile side: 48 m
const CLUTTER_GRASS_M = 260; // where the last tuft has sunk away
const CLUTTER_GRASS_FADE_M = 90; // ... and over how much of that it sinks
const CLUTTER_STONE_M = 620; // the boulders and flowers: the old shell's reach
const CLUTTER_STONE_FADE_M = 120;
// Tile growth per frame, ms: a spawn grows the whole field over a handful of
// frames; flying on grows a leading edge of tiles every 48 m.
const CLUTTER_BUDGET_MS = 1.5;

// The clutter's paint: the chunks' Lambert, instanced, with the rim sink. A
// unit box sits on its foot (y -0.5) and is squashed toward it by how far its
// instance stands from the camera, so a piece at the rim is a sliver in the
// ground and one well inside is full height.
function clutterMaterial(far, fade) {
  const m = new THREE.MeshLambertMaterial({ color: 0xffffff });
  m.name = `clutter-${far}`;
  m.onBeforeCompile = (sh) => {
    sh.uniforms.uClutterRim = { value: new THREE.Vector2(far, fade) };
    sh.vertexShader = sh.vertexShader
      .replace("#include <common>", "#include <common>\nuniform vec2 uClutterRim;")
      .replace(
        "#include <begin_vertex>",
        `#include <begin_vertex>
#ifdef USE_INSTANCING
  float clutterD = distance((modelMatrix * instanceMatrix[3]).xyz, cameraPosition);
  float clutterS = clamp((uClutterRim.x - clutterD) / uClutterRim.y, 0.0, 1.0);
  transformed.y = (transformed.y + 0.5) * clutterS - 0.5;
#endif`,
      );
  };
  m.customProgramCacheKey = () => "ground-clutter";
  return m;
}

/**
 * The ground clutter around the camera: tiles of instance data grown under a
 * per-frame ration and dropped behind, packed into one InstancedMesh for the
 * grass and one for the stones and flowers. Pure scenery, like the trees.
 */
export class GroundClutter {
  constructor(scene, field, land, roads) {
    this.scene = scene;
    this.field = field;
    this.land = land;
    this.roads = roads;
    this.tiles = new Map(); // tile key -> { grass, stone } (Float32Array: 16 matrix + 3 colour a piece)
    this.box = new THREE.BoxGeometry(1, 1, 1);
    this.grass = this.batch(clutterMaterial(CLUTTER_GRASS_M, CLUTTER_GRASS_FADE_M));
    this.stone = this.batch(clutterMaterial(CLUTTER_STONE_M, CLUTTER_STONE_FADE_M));
    this.want = []; // tile keys to grow, nearest first
    this.lastKey = null;
    this.dirty = false;
    this.center = new THREE.Vector3();
  }

  batch(material) {
    const im = new THREE.InstancedMesh(this.box, material, 1);
    im.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(3), 3);
    im.count = 0;
    im.matrixAutoUpdate = false;
    im.receiveShadow = GFX >= 2;
    im.frustumCulled = true;
    im.boundingSphere = new THREE.Sphere();
    im.userData.clutter = true;
    this.scene.add(im);
    settle(im);
    return im;
  }

  /** Both batches, for the tests and the probes. */
  *batches() {
    yield this.grass;
    yield this.stone;
  }

  /** Follow the camera: keep the tiles in reach, grow the missing ones. */
  update(p) {
    const span = CLUTTER_TILE * this.field.cellSize;
    const tx = Math.floor(p.x / span), tz = Math.floor(p.z / span);
    // Only the stones' rim matters for which tiles exist; a camera higher
    // above the ground than that sees none of it (the sink would hide it all).
    const agl = p.y - Math.max(this.field.waterLevel, this.field.groundAt(p.x, p.z));
    const reach = agl < CLUTTER_STONE_M ? Math.sqrt(CLUTTER_STONE_M * CLUTTER_STONE_M - Math.max(0, agl) * agl) : -1;
    const key = `${tx},${tz},${Math.round(reach / span)}`;
    if (key !== this.lastKey) {
      this.lastKey = key;
      this.center.set(p.x, p.y, p.z);
      const r = Math.ceil(reach / span) + 1;
      const keep = new Set();
      this.want = [];
      for (let dz = -r; dz <= r; dz++) {
        for (let dx = -r; dx <= r; dx++) {
          // Nearest point of the tile, horizontally.
          const ex = Math.max(0, Math.abs(dx) - 1) * span, ez = Math.max(0, Math.abs(dz) - 1) * span;
          if (reach < 0 || ex * ex + ez * ez > reach * reach) continue;
          const k = `${tx + dx},${tz + dz}`;
          keep.add(k);
          if (!this.tiles.has(k)) this.want.push([dx * dx + dz * dz, tx + dx, tz + dz, k]);
        }
      }
      this.want.sort((a, b) => a[0] - b[0]);
      for (const k of this.tiles.keys()) if (!keep.has(k)) { this.tiles.delete(k); this.dirty = true; }
      this.grassReach = Math.max(0, CLUTTER_GRASS_M * CLUTTER_GRASS_M - Math.max(0, agl) * agl);
    }
    const t0 = performance.now();
    while (this.want.length && performance.now() - t0 < CLUTTER_BUDGET_MS) {
      const [, ix, iz, k] = this.want.shift();
      this.tiles.set(k, this.grow(ix, iz));
      this.dirty = true;
    }
    if (this.dirty) this.pack();
  }

  /** Repack both batches from the tiles: the grass only from the tiles in its reach. */
  pack() {
    this.dirty = false;
    const span = CLUTTER_TILE * this.field.cellSize;
    const c = this.center;
    const g2 = this.grassReach ?? 0;
    let ng = 0, ns = 0;
    const near = [];
    for (const [k, t] of this.tiles) {
      const [ix, iz] = k.split(",").map(Number);
      const ex = Math.max(0, Math.abs(ix * span + span / 2 - c.x) - span / 2);
      const ez = Math.max(0, Math.abs(iz * span + span / 2 - c.z) - span / 2);
      const grassy = ex * ex + ez * ez <= g2;
      if (grassy) { near.push(t); ng += t.grass.length / 19; }
      ns += t.stone.length / 19;
    }
    this.fill(this.grass, near.map((t) => t.grass), ng, CLUTTER_GRASS_M);
    this.fill(this.stone, [...this.tiles.values()].map((t) => t.stone), ns, CLUTTER_STONE_M);
  }

  fill(im, parts, n, radius) {
    if (im.instanceMatrix.count < n) {
      // Grow by half again, so the field's ebb and flow re-uses one buffer.
      const cap = Math.ceil(n * 1.5);
      im.dispose(); // frees the old buffers' GL side; the new ones upload on the next draw
      im.instanceMatrix = new THREE.InstancedBufferAttribute(new Float32Array(cap * 16), 16);
      im.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(cap * 3), 3);
    }
    const m = im.instanceMatrix.array, col = im.instanceColor?.array;
    let o = 0, oc = 0;
    for (const a of parts) {
      for (let i = 0; i < a.length; i += 19) {
        for (let j = 0; j < 16; j++) m[o + j] = a[i + j];
        col[oc] = a[i + 16];
        col[oc + 1] = a[i + 17];
        col[oc + 2] = a[i + 18];
        o += 16;
        oc += 3;
      }
    }
    im.count = n;
    if (n) {
      im.instanceMatrix.needsUpdate = true;
      im.instanceColor.needsUpdate = true;
    }
    im.boundingSphere.set(this.center, radius + 40);
  }

  /**
   * One tile's clutter: every cell's roll, exactly the baked field's — a
   * patch of tall grass (two to four clumps of three blades), a half-buried
   * boulder, or a dab of wildflowers. Deterministic per cell so every client
   * grows the same field; kept off the roads and their shoulders like the
   * trees. Each piece is 19 floats: its box's matrix and its colour.
   */
  grow(ix, iz) {
    const f = this.field;
    const cs = f.cellSize;
    const grass = [], stone = [];
    const box = (out, x, y, z, w, h, d, c0, c1, c2) => out.push(w, 0, 0, 0, 0, h, 0, 0, 0, 0, d, 0, x, y, z, 1, c0, c1, c2);
    const at = (x, z) => f.groundAt(x, z);
    for (let lz = 0; lz < CLUTTER_TILE; lz++) {
      for (let lx = 0; lx < CLUTTER_TILE; lx++) {
        const cx = ix * CLUTTER_TILE + lx, cz = iz * CLUTTER_TILE + lz;
        const x0 = cx * cs, z0 = cz * cs;
        const xm = x0 + cs / 2, zm = z0 + cs / 2;
        const top = f.groundAt(xm, zm);
        if (top <= f.waterLevel + 1) continue;
        if (this.roads && roadDistance(f, xm, zm) <= ROAD_HALF_WIDTH + 3) continue;
        const grassy = this.land ? top > 1.5 && top <= 62 : top > 8 && top <= 96;
        const rocky = this.land ? top > 62 : top > 96 && top <= 168;
        const roll = hash2(cx, cz, (f.seed + 7001) >>> 0);
        const shade = 0.78 + hash2(cx, cz, (f.seed + 7004) >>> 0) * 0.5;
        if (grassy && roll < 0.3 * GROUND_CLUTTER) {
          // A patch of tall grass: a few clumps in the cell, each a sheaf of
          // slender blades a shade off the ground they grow from. Cells are
          // big (12 m), so a lone blade per hit vanished — the clump is what
          // reads as "grass" from a hull at speed.
          const t0 = this.land ? 0.5 * shade : 0.34 * shade, t1 = this.land ? 0.58 * shade : 0.62 * shade;
          const t2 = this.land ? 0.27 * shade : 0.24 * shade;
          const clumps = 2 + (hash2(cx, cz, (f.seed + 7005) >>> 0) * 2.999) | 0;
          for (let b = 0; b < clumps; b++) {
            const bx = 1 + hash2(cx, cz, (f.seed + 7010 + b) >>> 0) * (cs - 2);
            const bz = 1 + hash2(cx, cz, (f.seed + 7020 + b) >>> 0) * (cs - 2);
            const gh = 0.6 + hash2(cx, cz, (f.seed + 7030 + b) >>> 0) * 0.9;
            const foot = at(x0 + bx, z0 + bz) - 0.15; // rooted, not perched on a slope
            for (let k = 0; k < 3; k++) {
              const ox = (hash2(cx + k, cz, (f.seed + 7040 + b) >>> 0) - 0.5) * 0.8;
              const oz = (hash2(cx, cz + k, (f.seed + 7050 + b) >>> 0) - 0.5) * 0.8;
              const bh = gh * (0.7 + 0.3 * k / 2);
              box(grass, x0 + bx + ox, foot + bh / 2, z0 + bz + oz, 0.22, bh, 0.22, t0, t1, t2);
            }
          }
        } else if ((grassy || rocky) && roll > 0.86 && roll < 0.86 + 0.05 * GROUND_CLUTTER) {
          // A half-buried boulder shrugging out of the field, sunk to half
          // its height so a boulder on a slope sits IN the hill.
          const ox = 1 + hash2(cx, cz, (f.seed + 7002) >>> 0) * (cs - 2);
          const oz = 1 + hash2(cx, cz, (f.seed + 7003) >>> 0) * (cs - 2);
          const bs = 1.1 + hash2(cx, cz, (f.seed + 7006) >>> 0) * 1.6;
          box(stone, x0 + ox, at(x0 + ox, z0 + oz) + bs * 0.05, z0 + oz, bs, bs * 0.7, bs * 0.9, 0.5 * shade, 0.5 * shade, 0.52 * shade);
        } else if (grassy && roll > 0.95 && roll < 0.95 + 0.03 * GROUND_CLUTTER) {
          // A dab of wildflowers — rare enough to be a landmark, not a lawn:
          // goldenrod or clover pink.
          const ox = 1 + hash2(cx, cz, (f.seed + 7002) >>> 0) * (cs - 2);
          const oz = 1 + hash2(cx, cz, (f.seed + 7003) >>> 0) * (cs - 2);
          const gold = hash2(cx, cz, (f.seed + 7007) >>> 0) < 0.5;
          box(stone, x0 + ox, at(x0 + ox, z0 + oz) + 0.2, z0 + oz, 0.8, 0.6, 0.8, gold ? 0.95 : 0.9, gold ? 0.8 : 0.42, gold ? 0.3 : 0.5);
        }
      }
    }
    return { grass: Float32Array.from(grass), stone: Float32Array.from(stone) };
  }
}

// --- sky: shared day/night cycle -------------------------------------------
// The sun rides the server clock (snapshot `now`), so every pilot sees the
// same time of day. One full cycle every DAY_SECONDS: a golden sunset glow
// chases the sun to the horizon, then the moon, stars, and the Milky Way
// come out. Per-round weather (clear/dawn/storm) layers a mood on top.

const DAY_SECONDS = 1200; // 20-minute days: a 12-minute round spans dawn→dusk

const smooth01 = (t) => {
  const x = t < 0 ? 0 : t > 1 ? 1 : t;
  return x * x * (3 - 2 * x);
};

// Time-of-day anchor palettes (clear weather).
const TOD_DAY = {
  sky: new THREE.Color(0x8ec9ff), fog: new THREE.Color(0x9fd0f5),
  sun: new THREE.Color(0xfff3da), hemi: new THREE.Color(0xbcdcff),
};
const TOD_NIGHT = {
  sky: new THREE.Color(0x0c1422), fog: new THREE.Color(0x16202f),
  sun: new THREE.Color(0x93acd8), hemi: new THREE.Color(0x2c3c58),
};
const TOD_SUNSET = {
  sky: new THREE.Color(0xe08a52), fog: new THREE.Color(0xdf9c6a),
  sun: new THREE.Color(0xffb066), hemi: new THREE.Color(0xc08a6a),
};
const MOOD_GRAY = new THREE.Color(0x6d7a89);
const MOOD_WARM = new THREE.Color(0xe9b287);
const MOON_LIGHT = new THREE.Color(0x9db8e0);
const LIGHTNING_TINT = new THREE.Color(0xd8e4ff); // the storm's camera flash

// Weather moods layered over the time-of-day palette. "dawn" rounds are a
// golden haze all day. Storm rounds used to be a uniform 700/2500 knife-fog;
// now that the weather is a marching front (storm.js), the round-wide haze
// relaxes so the thunderhead wall is visible coming from across the map —
// the knife-fight fog still happens, but only INSIDE a cell (storm.js
// squeezes fog.near/far by how deep the camera sits in the weather).
const WEATHER_MOODS = {
  clear: { fogNear: 1500, fogFar: 4200, light: 1.0, gray: 0, warm: 0 },
  dawn: { fogNear: 1150, fogFar: 3400, light: 0.92, gray: 0.08, warm: 0.4 },
  storm: { fogNear: 1200, fogFar: 3800, light: 0.55, gray: 0.62, warm: 0 },
};

// A soft radial sprite — once the sun's sunset glow, now the storm front's
// lightning glows (storm.js).
export function makeGlowTexture() {
  const c = document.createElement("canvas");
  c.width = c.height = 128;
  const ctx = c.getContext("2d");
  const g = ctx.createRadialGradient(64, 64, 0, 64, 64, 64);
  g.addColorStop(0, "rgba(255,255,255,0.85)");
  g.addColorStop(0.35, "rgba(255,255,255,0.32)");
  g.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 128, 128);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

export function buildSky(scene, radius, naval = false, land = false) {
  const sun = new THREE.DirectionalLight(0xfff3da, 2.4);
  scene.add(sun);
  const hemi = new THREE.HemisphereLight(0xbcdcff, 0x49603e, 0.9);
  scene.add(hemi);
  const ambient = new THREE.AmbientLight(0xffffff, 0.25);
  scene.add(ambient);

  // The sun: a round, limb-darkened disc in its own glare that reddens as it
  // sinks (sun.js). Both tiers draw it; tier 2 turns it up into the bloom.
  const sunDisc = buildSun(scene);

  // The moon, its real face waxing and waning over a lunar month of game
  // days (moon.js), and the stars wheeling about their pole with the Milky
  // Way's glow across them (stars.js).
  const moonDisc = buildMoon(scene);
  const stars = buildStars(scene);

  // The sea is its own module now (water.js): a lit sheet that paints its own
  // shallows, laid in by main.js beside this sky.
  //
  // The see-through backdrop goes down before anything see-through hangs in
  // front of it. three orders those by how far off each one's middle is —
  // for the stars that's the zenith and for the sea the map's centre, which
  // from half the seats in the sky come out NEARER than a cloud a kilometre
  // off, and the stars shone through the cumulus at night (a sea drawn after
  // a cloud paints over it just the same). The Milky Way's glow, the stars,
  // then the sun and the moon — whose dark side blots out the stars behind
  // it — after tier 2's scattered sky (gfx2.js, -10); then the sea (water.js,
  // -8), which they set behind; then the rest.
  stars.band.renderOrder = -9.6;
  stars.points.renderOrder = -9.5;
  sunDisc.mesh.renderOrder = -9;
  moonDisc.mesh.renderOrder = -9;

  scene.background = new THREE.Color(0x8ec9ff);
  scene.fog = new THREE.Fog(0x9fd0f5, 1500, 4200);

  const sd = new THREE.Vector3();
  const md = new THREE.Vector3();
  const cSky = new THREE.Color(), cFog = new THREE.Color(), cSun = new THREE.Color();
  const cHemi = new THREE.Color();
  const mix = (out, k, dayK, duskK) =>
    out.copy(TOD_NIGHT[k]).lerp(TOD_DAY[k], dayK).lerp(TOD_SUNSET[k], duskK);

  const sky = {
    weather: "clear",
    timeShift: 0, // debug/test hook: shifts the cycle without touching net time
    set(name) {
      this.weather = WEATHER_MOODS[name] ? name : "clear";
    },
    update(svrT) {
      const mood = WEATHER_MOODS[this.weather];
      const t = svrT + this.timeShift;
      this.lastT = t; // debug/test introspection
      const frac = ((t / DAY_SECONDS) % 1 + 1) % 1; // 0 = midnight, 0.5 = noon
      const ang = (frac - 0.25) * Math.PI * 2;
      // Sun path, biased upward a touch so nights stay snappy.
      sd.set(Math.cos(ang) * 0.95, Math.sin(ang) + 0.14, 0.34).normalize();
      const e = sd.y;

      let dayK = smooth01((e + 0.25) / 0.6); // master night → day blend
      // P2a: the naval theater's night has no city glow or flares of its own to
      // read hulls by, and ~half of rounds run after dark, so clamp the master
      // darkness (1 - dayK) to <= 0.82 — the sea and horizon stay legible at
      // night without flattening the day/dusk cycle. Client visual only; the
      // nightK parity pair (star shells / missions) is untouched.
      if (naval || land) dayK = Math.max(dayK, 0.18);
      const duskK = Math.exp(-((e - 0.04) ** 2) / (2 * 0.13 ** 2)); // golden-hour bell
      const nightK = 1 - smooth01((e + 0.3) / 0.32);
      // Expose the night/day blends so ambient dressing (lighthouse beam,
      // control-tower windows — roadmap #42/#43) reuses the sky's own state
      // instead of recomputing the cycle.
      this.nightK = nightK;
      this.dayK = dayK;
      this.duskK = duskK; // the clouds catch the low sun (clouds.js)
      const grayK = mood.gray * (0.3 + 0.7 * dayK); // storms stay dark at night

      mix(cSky, "sky", dayK, duskK * 0.78);
      mix(cFog, "fog", dayK, duskK * 0.78);
      mix(cSun, "sun", dayK, duskK * 0.85);
      mix(cHemi, "hemi", dayK, duskK * 0.6);
      cSky.lerp(MOOD_GRAY, grayK);
      cFog.lerp(MOOD_GRAY, grayK);
      if (mood.warm) {
        cSky.lerp(MOOD_WARM, mood.warm * dayK * 0.5);
        cFog.lerp(MOOD_WARM, mood.warm * dayK * 0.6);
      }
      // OVERTIME finale (roadmap #16): a hot golden-hour cast with a slow
      // ~0.5 Hz breathing pulse — scaled by dayK so night rounds keep their
      // dark, and subtle enough not to fight the weather moods.
      if (this.overtime) {
        const pulse = 0.3 + 0.1 * Math.sin(svrT * Math.PI);
        cSky.lerp(MOOD_WARM, pulse * dayK);
        cFog.lerp(MOOD_WARM, pulse * dayK * 0.8);
      }
      // Lightning (storm.js writes this.lightning 0..1 every frame it's
      // active): a cold blue-white flood across sky and fog — a frame of
      // daylight snapped into the storm, gone before the eye settles.
      const bolt = this.lightning ?? 0;
      if (bolt > 0.01) {
        cSky.lerp(LIGHTNING_TINT, bolt * 0.4);
        cFog.lerp(LIGHTNING_TINT, bolt * 0.5);
      }

      scene.background.copy(cSky);
      scene.fog.color.copy(cFog);
      const squeeze = 1 - nightK * 0.15;
      // The naval theater fights a gunnery duel at 8–13 km, so the sea horizon
      // has to sit beyond the guns' reach — a spotted battleship at 12 km must
      // be a silhouette you can lay on, not a wall of fog (the air game keeps
      // its close, cosy haze). Storm and night still tint and dim, but the far
      // plane is floored past the longest guns: if a bot can shoot you, you
      // can see it.
      if (naval) {
        scene.fog.near = Math.max(mood.fogNear * squeeze * 3.4, 4000);
        scene.fog.far = Math.max(mood.fogFar * squeeze * 3.4, 14000);
      } else if (land) {
        // Tank duels run to ~4 km: the haze has to sit past the guns' reach.
        scene.fog.near = Math.max(mood.fogNear * squeeze * 1.9, 2200);
        scene.fog.far = Math.max(mood.fogFar * squeeze * 1.9, 7600);
      } else {
        scene.fog.near = mood.fogNear * squeeze;
        scene.fog.far = mood.fogFar * squeeze;
      }

      // Lights: the sun hands the key light to the moon overnight.
      md.copy(sd).multiplyScalar(-1);
      const moonKey = nightK > 0.6;
      sun.position.copy(moonKey ? md : sd).multiplyScalar(2000);
      sun.intensity = (0.3 + 2.1 * dayK) * mood.light + bolt * 1.8;
      sun.color.copy(moonKey ? MOON_LIGHT : cSun);
      hemi.intensity = (0.32 + 0.58 * dayK) * (0.6 + 0.4 * mood.light) + bolt * 0.7;
      hemi.color.copy(cHemi);
      ambient.intensity = 0.1 + 0.15 * dayK;

      // The sun, reddening and swelling in its glare as it goes down.
      sunDisc.update({ dir: sd, dayK, duskK, gray: grayK });

      // The moon, and the stars wheeling round the pole with the sun's own
      // turn — a bright moon washes out the faintest of them.
      moonDisc.update({ dir: md, t, daySec: DAY_SECONDS, nightK, gray: mood.gray, sky: cSky });
      stars.update({ t, turn: ang, nightK, moon: moonDisc.lit, gray: mood.gray, sky: cSky });

      // Tier 2 (gfx2.js) layers its sky, haze and sun shadow on this state.
      this.gfx?.({ sunDir: sd, lightDir: moonKey ? md : sd, dayK, duskK, nightK, gray: grayK });
    },
  };
  sky.parts = { sun, sunDisc, moonDisc, stars, hemi, ambient };
  sky.update(DAY_SECONDS * 0.4); // boot mid-morning until the server clock arrives
  return sky;
}

export class ZoneMarker {
  constructor(scene, match, opts = {}) {
    // The naval theater's objective is a capture zone on the sea, not the air
    // game's floating Sky Ring — a gold ring laid flat on the water with a faint
    // column of light, colored by who holds it.
    if (opts.naval) {
      // Naval/land domination: a flat gold ring laid at each capture zone,
      // coloured by who holds it. Centres arrive in the welcome (setZones); no
      // light column, which from inside a zone would flood the view. The land
      // theater passes groundAt so each ring sits on its town's plateau.
      this.naval = true;
      this.scene = scene;
      this.zoneR = opts.radiusM ?? 650;
      this.zoneY = (opts.waterLevelM ?? 0) + 3;
      this.groundAt = opts.groundAt ?? null;
      this.rings = [];
      return;
    }
    // Air game: the Sky Ring is hidden in modes without it (build nothing).
    this.hidden = !!opts.hidden;
    if (this.hidden) return;
    const height = match.zoneMaxAltM - match.zoneMinAltM;
    const mid = (match.zoneMaxAltM + match.zoneMinAltM) / 2;
    const cx = opts.centerX ?? 0;
    const cz = opts.centerZ ?? 0;
    this.wall = new THREE.Mesh(
      new THREE.CylinderGeometry(match.zoneRadiusM, match.zoneRadiusM, height, 32, 1, true),
      new THREE.MeshBasicMaterial({
        color: 0xf4c542,
        transparent: true,
        opacity: 0.07,
        side: THREE.DoubleSide,
        depthWrite: false,
      }),
    );
    this.wall.position.set(cx, mid, cz);
    scene.add(this.wall);

    // A rotating ring of blocks at mid-altitude sells the objective.
    const ringBlocks = 26;
    this.ring = new THREE.InstancedMesh(
      new THREE.BoxGeometry(26, 26, 26),
      new THREE.MeshLambertMaterial({ color: 0xf4c542, emissive: 0x7a5a12 }),
      ringBlocks,
    );
    this.ring.position.set(cx, 0, cz);
    const dummy = new THREE.Object3D();
    for (let i = 0; i < ringBlocks; i++) {
      const a = (i / ringBlocks) * Math.PI * 2;
      dummy.position.set(Math.cos(a) * match.zoneRadiusM, mid, Math.sin(a) * match.zoneRadiusM);
      dummy.rotation.y = a;
      dummy.updateMatrix();
      this.ring.setMatrixAt(i, dummy.matrix);
    }
    scene.add(this.ring);
  }

  // (Re)build a flat ring at each naval capture-zone centre (from the welcome).
  // The rings are deliberately faint — the minimap carries the objective
  // picture; in-world they only need to whisper "you are inside/near a zone"
  // without walling off the gunsight's view of the sea.
  setZones(centers) {
    if (!this.naval) return;
    for (const r of this.rings) this.scene.remove(r);
    this.rings = (centers ?? []).map((c) => {
      const ring = new THREE.Mesh(
        new THREE.TorusGeometry(c.r > 0 ? c.r : this.zoneR, 3, 8, 80),
        new THREE.MeshBasicMaterial({ color: 0xf4c542, transparent: true, opacity: 0.16, depthWrite: false }),
      );
      ring.rotation.x = Math.PI / 2; // lay it flat on the water
      const y = this.groundAt ? this.groundAt(c.x ?? 0, c.z ?? 0) + 4 : this.zoneY;
      ring.position.set(c.x ?? 0, y, c.z ?? 0);
      ring.visible = !this.sightHidden;
      this.scene.add(ring);
      return ring;
    });
  }

  /** Hide the in-world rings entirely while the player is on the gunsight —
   *  under magnification even a faint band across the sea blocks the lay. */
  setSightHidden(hidden) {
    if (!this.naval || this.sightHidden === !!hidden) return;
    this.sightHidden = !!hidden;
    for (const r of this.rings) r.visible = !this.sightHidden;
  }

  update(dt, holder) {
    if (this.hidden) return;
    const colors = { "-1": 0xf4c542, 0: 0xe84a4a, 1: 0x4a7de8, 2: 0xc78ae0 };
    if (this.naval) {
      // holder is an array (one per zone) in the naval domination objective.
      const hs = Array.isArray(holder) ? holder : holder == null ? [] : [holder];
      this.rings.forEach((ring, i) => ring.material.color.setHex(colors[hs[i] ?? -1] ?? 0xf4c542));
      return;
    }
    this.ring.rotation.y += dt * 0.08; // air ring spins
    this.wall?.material.color.setHex(colors[holder] ?? 0xf4c542);
    this.ring.material.color.setHex(colors[holder] ?? 0xf4c542);
  }
}

/** Blocky flak emplacements, driven by server snapshots. */
// Point a mount: the box mount and barrel (which carry the muzzle flash and
// anchor the gunner's camera and the salvo blast) and, once a model is
// mounted, every level's _Mount and _Gun nodes — whichever LOD is showing
// points the same way.
function aimGun(g, yaw, pitch) {
  g.mount.rotation.y = yaw;
  g.barrel.rotation.z = pitch;
  // A model node yaws in its own parent's frame: an emplacement's is the
  // unrotated world, a warship turret's is the hull (yawOffset = -hull yaw).
  // set() also clears the half-turn an aft-built turret carries at rest.
  for (const l of g.model?.levels ?? []) {
    l.mount.rotation.set(0, yaw + (l.yawOffset ?? 0), 0);
    l.gun.rotation.z = pitch;
  }
}

// The Iowa's main-battery geometry, off the model (Part 5.2): each turret's
// _Guns pivot sits 5.72 m forward of and 1.95 m above the turret pivot, the
// three 16"/50s run 19.8 m along it, and the salvo leaves the two outer
// tubes (the server fires two shells per salvo).
const IOWA_GUNS = { x: 5.72, y: 1.95, muzzle: 19.8, tubes: [2.08, -2.08] };

export class AAGuns {
  constructor(scene) {
    this.scene = scene;
    this.guns = new Map(); // id -> {root, mount, barrel, flash, wreck, alive, ...}
    this.localAimId = 0; // gun manned by the local player: snapshots skip it
  }

  /** Build visuals from the welcome's static site list. field (optional)
   * grounds the barrage-balloon tethers. */
  build(sites, field = null) {
    for (const g of this.guns.values()) g.root.removeFromParent(); // a deck mount rides its hull
    this.guns.clear();
    this.lastField = field;
    for (const s of sites) this.buildOne(s, field);
    for (const g of this.guns.values()) this._settleSite(g);
  }

  // An installation never moves once it's placed, only its gun does: settle
  // everything on the site but the box mount the snapshot trains (the
  // model's own mount and gun were handed out by node(), so settle() leaves
  // them walked). The root stays walked too: an upgrade rescales it, and a
  // deck mount rides its hull. Run again after each upgrade adds a piece.
  _settleSite(g) {
    for (const c of g.root.children) if (c !== g.mount) settle(c);
  }

  /** Build one emplacement's visuals (split out so a single mount can be
   * rebuilt when its flag changes — Fort Brick's garrison, combined arms). */
  buildOne(s, field = null) {
      const t = TEAM_INFO[s.team];
      // A Brickton city block (kind 8, brickton.js): a terrace of houses
      // with no gun on it. HP rides the AA rows; rubble when it falls.
      if (s.kind === CITY_KIND) {
        let g = null;
        const blk = buildCityBlock(s, field, {
          pitch: this.cityPitch ?? 62,
          material: terrainMaterial,
          onDressed: (intact) => {
            if (!g || this.guns.get(s.id) !== g) return;
            g.root.remove(g.baseGroup);
            g.root.add(intact);
            g.baseGroup = intact;
            intact.visible = g.alive;
            this._settleSite(g);
          },
        });
        this.scene.add(blk.root);
        g = {
          site: s,
          root: blk.root, mount: new THREE.Group(), barrel: new THREE.Group(),
          flash: { visible: false }, wreck: blk.wreck, baseGroup: blk.intact,
          alive: true, team: s.team, kind: CITY_KIND, sub: s.sub ?? 0, pivotY: 4,
          occupied: false, srvYaw: s.yaw ?? 0, srvPitch: 0,
        };
        this.guns.set(s.id, g);
        return;
      }
      // Barrage balloons (kind 4): a blocky blimp on a lethal tether. The
      // site position is the balloon; the cable drops to the terrain (or a
      // barge at sea level) below it.
      if (s.kind === 4) {
        const root = new THREE.Group();
        root.position.set(s.x, s.y, s.z);
        root.rotation.y = s.yaw ?? 0;
        const body = new THREE.Group();
        body.add(
          new THREE.Mesh(
            mergeBoxes([
              // Envelope with a blunt nose, tail cone, and team band.
              { x: 0, y: 0, z: 0, w: 11, h: 5.5, d: 5.5, color: 0xb8bcc2 },
              { x: 5.4, y: 0, z: 0, w: 2.6, h: 4, d: 4, color: 0xa9adb4 },
              { x: -6.4, y: 0, z: 0, w: 2.6, h: 3.6, d: 3.6, color: 0xa9adb4 },
              { x: 0, y: -0.6, z: 0, w: 11.4, h: 0.9, d: 5.9, color: t.color },
              // Tail fins.
              { x: -7.4, y: 1.6, z: 0, w: 2.8, h: 2, d: 0.7, color: 0x8e9298 },
              { x: -7.4, y: 0, z: 0, w: 2.8, h: 0.7, d: 4.6, color: 0x8e9298 },
            ]),
            terrainMaterial,
          ),
        );
        // surfaceAt clamps to the waterline, so barge tethers ground right.
        const groundY = field ? field.surfaceAt(s.x, s.z) : s.y - 400;
        const len = Math.max(4, s.y - groundY);
        const cable = new THREE.Mesh(
          new THREE.BoxGeometry(0.35, len, 0.35),
          new THREE.MeshBasicMaterial({ color: 0x14161a }),
        );
        cable.position.y = -len / 2;
        body.add(cable);
        root.add(body);
        this.scene.add(root);
        const wreck = new THREE.Group();
        root.add(wreck);
        this.guns.set(s.id, {
        site: s,
          root, mount: new THREE.Group(), barrel: new THREE.Group(),
          flash: { visible: false }, wreck, baseGroup: body,
          alive: true, team: s.team, kind: 4, sub: 0, pivotY: 0,
          occupied: false, srvYaw: s.yaw ?? 0, srvPitch: 0,
        });
        this._upgradeBalloon(s.id, s.y - groundY);
        return;
      }
      // Shore installations (kind 3): unarmed bombardment targets — a radar
      // station (radiates while standing, see "radardown" events) or a fuel
      // depot, with a burned-out wreck.
      if (s.kind === 3) {
        const radar = s.sub === 1;
        const root = new THREE.Group();
        root.position.set(s.x, s.y - 3, s.z);
        root.rotation.y = s.yaw ?? 0;
        root.scale.setScalar(1.6); // installations should read from altitude
        const intact = new THREE.Mesh(
          mergeBoxes(
            radar
              ? [
                  // Radar station: blockhouse, lattice tower, dish.
                  { x: 0, y: 1.5, z: 0, w: 12, h: 3, d: 9, color: 0x6f7174 },
                  { x: 0, y: 3.3, z: 0, w: 12.4, h: 0.8, d: 9.4, color: t.color },
                  { x: -2, y: 7, z: 0, w: 2.2, h: 8, d: 2.2, color: 0x4a4d52 },
                  { x: -2, y: 11.6, z: 0, w: 3, h: 1.2, d: 3, color: 0x4a4d52 },
                  { x: -0.5, y: 13.2, z: 0, w: 0.8, h: 4.6, d: 7.5, color: 0xd8d2bc },
                  { x: -1.4, y: 13.2, z: 0, w: 1.2, h: 1.2, d: 1.2, color: 0x2b2f36 },
                  { x: 4, y: 4.6, z: 2.5, w: 3, h: 2.4, d: 3, color: 0x5d6168 },
                ]
              : [
                  // Fuel depot: tank farm with a pump house.
                  { x: -3, y: 3, z: -3, w: 6, h: 6, d: 6, color: 0x8a8d90 },
                  { x: 4, y: 2.6, z: -2.5, w: 5, h: 5.2, d: 5, color: 0x8a8d90 },
                  { x: 0.5, y: 2.2, z: 4, w: 4.4, h: 4.4, d: 4.4, color: 0x8a8d90 },
                  { x: -3, y: 6.3, z: -3, w: 6.4, h: 0.7, d: 6.4, color: t.color },
                  { x: 4, y: 5.5, z: -2.5, w: 5.4, h: 0.7, d: 5.4, color: t.color },
                  { x: 5.5, y: 1.2, z: 4, w: 3.6, h: 2.4, d: 3, color: 0x6f7174 },
                  { x: 0, y: 0.4, z: 0, w: 15, h: 0.8, d: 13, color: 0x4d4a44 },
                ],
          ),
          terrainMaterial,
        );
        root.add(intact);
        const wreck = new THREE.Mesh(
          mergeBoxes([
            { x: 0, y: 0.8, z: 0, w: 12, h: 1.6, d: 10, color: 0x3a3a3a },
            { x: -2, y: 2.4, z: -1, w: 5, h: 2.4, d: 4, color: 0x222222 },
            { x: 3, y: 1.8, z: 2.5, w: 3.4, h: 1.6, d: 3, color: 0x2e2a26 },
          ]),
          terrainMaterial,
        );
        wreck.visible = false;
        root.add(wreck);
        this.scene.add(root);
        this.guns.set(s.id, {
        site: s,
          root, mount: new THREE.Group(), barrel: new THREE.Group(),
          flash: { visible: false }, wreck, baseGroup: intact,
          alive: true, team: s.team, kind: 3, sub: s.sub ?? 0, pivotY: 3,
          occupied: false, srvYaw: s.yaw ?? 0, srvPitch: 0,
        });
        this._upgradeStructure(s.id);
        return;
      }
      // Jets-era SAM battery (kind 7, docs/early-jets-era.md): a radar van
      // with a lattice dish and a sloped launch rail carrying the pole. The
      // MOUNT is the rail assembly so the snapshot's yaw/pitch train it onto
      // whoever it's tracking — the visible slew IS the RWR warning made
      // flesh. Dead: a cratered pad and the toppled rail.
      if (s.kind === 7) {
        const root = new THREE.Group();
        root.position.set(s.x, s.y, s.z);
        const base = new THREE.Mesh(
          mergeBoxes([
            // Revetment pad + the guidance van and its lattice dish.
            { x: 0, y: 0.4, z: 0, w: 16, h: 0.8, d: 14, color: 0x4d4a44 },
            { x: -4.5, y: 2.0, z: -4, w: 5.5, h: 3.2, d: 3.2, color: 0x5d6168 },
            { x: -4.5, y: 4.0, z: -4, w: 5.9, h: 0.7, d: 3.6, color: t.color },
            { x: -4.5, y: 5.6, z: -4, w: 1.2, h: 2.6, d: 1.2, color: 0x4a4d52 },
            { x: -4.5, y: 7.2, z: -4, w: 0.7, h: 3.4, d: 4.8, color: 0xd8d2bc },
          ]),
          terrainMaterial,
        );
        root.add(base);
        const mount = new THREE.Group();
        mount.position.y = 1.4;
        const barrel = new THREE.Group(); // the elevating rail + missile
        barrel.add(
          new THREE.Mesh(
            mergeBoxes([
              // Launch rail with the pole on it: booster band in team color.
              { x: 2.2, y: 0, z: 0, w: 9.5, h: 0.6, d: 1.1, color: 0x4a4d52 },
              { x: 2.6, y: 0.85, z: 0, w: 8.6, h: 0.8, d: 0.8, color: 0xd8d2bc },
              { x: -1.2, y: 0.85, z: 0, w: 1.6, h: 1.0, d: 1.0, color: t.color },
              { x: 7.1, y: 0.85, z: 0, w: 1.4, h: 0.5, d: 0.5, color: 0x2b2f36 },
            ]),
            terrainMaterial,
          ),
        );
        const pedestal = new THREE.Mesh(
          mergeBoxes([{ x: 0, y: 0.5, z: 0, w: 2.6, h: 1.6, d: 2.6, color: 0x5d6168 }]),
          terrainMaterial,
        );
        barrel.position.y = 1.4;
        mount.add(pedestal, barrel);
        root.add(mount);
        // The booster lighting off the front of the rail as the pole leaves.
        const flash = flashGroup(5);
        flash.position.set(7.8, 0.85, 0);
        barrel.add(flash);
        const wreck = new THREE.Mesh(
          mergeBoxes([
            { x: 0, y: 0.6, z: 0, w: 14, h: 1.2, d: 12, color: 0x3a3a3a },
            { x: 1.5, y: 1.4, z: 0.5, w: 8, h: 1.0, d: 1.4, color: 0x222222 }, // the toppled rail
            { x: -4.5, y: 1.6, z: -4, w: 4.5, h: 1.8, d: 2.6, color: 0x2e2a26 },
          ]),
          terrainMaterial,
        );
        wreck.visible = false;
        root.add(wreck);
        this.scene.add(root);
        this.guns.set(s.id, {
          site: s,
          root, mount, barrel, flash, wreck, baseGroup: base,
          alive: true, team: s.team, kind: 7, sub: 0, pivotY: 1.4,
          occupied: false, srvYaw: s.yaw ?? 0, srvPitch: 0.5,
        });
        return;
      }
      // Parked reserve aircraft (kind 6, roadmap #43): a non-flyable plane of
      // type s.sub sitting on its gear off the strip — unarmed, strafeable,
      // worth points. Swaps to a burnt wreck when the snapshot marks it dead.
      if (s.kind === 6) {
        const type = s.sub | 0;
        const root = new THREE.Group();
        // Server Pos is the deck surface; lift the airframe so its gear rests
        // on the deck rather than sinking into it.
        root.position.set(s.x, s.y + 1.7, s.z);
        root.rotation.y = s.yaw ?? 0;
        const intact = new THREE.Group();
        intact.add(new THREE.Mesh(planeGeometry(type), terrainMaterial));
        // Simple undercarriage so it reads as parked, not floating.
        intact.add(
          new THREE.Mesh(
            mergeBoxes([
              { x: 1.4, y: -1.0, z: 1.0, w: 0.3, h: 1.4, d: 0.3, color: 0x2b2f36 },
              { x: 1.4, y: -1.0, z: -1.0, w: 0.3, h: 1.4, d: 0.3, color: 0x2b2f36 },
              { x: 1.4, y: -1.7, z: 1.0, w: 0.7, h: 0.7, d: 0.4, color: 0x16171a },
              { x: 1.4, y: -1.7, z: -1.0, w: 0.7, h: 0.7, d: 0.4, color: 0x16171a },
              { x: -3.4, y: -0.7, z: 0, w: 0.28, h: 0.9, d: 0.28, color: 0x2b2f36 },
              { x: -3.4, y: -1.1, z: 0, w: 0.5, h: 0.5, d: 0.3, color: 0x16171a },
            ]),
            terrainMaterial,
          ),
        );
        root.add(intact);
        // Chocked, tied-down look aside, a strafed reserve plane collapses into
        // a charred, broken-backed hulk with the wings sheared off.
        const wreck = new THREE.Mesh(
          mergeBoxes([
            { x: 0, y: -0.6, z: 0, w: 6.5, h: 1.0, d: 1.6, color: 0x22201d },
            { x: 1.2, y: 0.0, z: 0, w: 2.4, h: 1.2, d: 1.4, color: 0x14110e },
            { x: -1.8, y: -0.3, z: 1.4, w: 2.2, h: 0.4, d: 2.0, color: 0x2a2724 },
            { x: -2.2, y: -0.3, z: -1.2, w: 1.6, h: 0.4, d: 1.4, color: 0x2a2724 },
            { x: -3.2, y: 0.2, z: 0, w: 1.2, h: 1.0, d: 0.3, color: 0x1a1815 },
          ]),
          terrainMaterial,
        );
        wreck.visible = false;
        root.add(wreck);
        this.scene.add(root);
        this.guns.set(s.id, {
        site: s,
          root, mount: new THREE.Group(), barrel: new THREE.Group(),
          flash: { visible: false }, wreck, baseGroup: intact,
          alive: true, team: s.team, kind: 6, sub: type, pivotY: 0,
          occupied: false, srvYaw: s.yaw ?? 0, srvPitch: 0,
        });
        this._upgradeParked(s.id, type, s.team);
        return;
      }
      const naval = s.kind === 1; // twin-barreled carrier deck mount
      const main = s.kind === 2; // battleship main-battery turret
      // Pivot height above the base. Shore flak pivots at the Flak 88's
      // trunnion (the server's landFlakPivotM); the others at their box mounts.
      // A shore main battery (Fort Brick's crown, a Gulf coast pad) is the
      // coastal battery model on its trunnion; a warship's is its turret.
      const pivotY = main
        ? s.ship ? 3 : EMPLACEMENTS.coastal.pivotY
        : naval ? EMPLACEMENTS.bofors40.pivotY : EMPLACEMENTS.flak88.pivotY;
      const root = new THREE.Group();
      root.position.set(s.x, s.y - pivotY, s.z);

      const base = new THREE.Mesh(
        mergeBoxes(
          main
            ? [
                // Barbette ring the turret house sits on.
                { x: 0, y: 1.0, z: 0, w: 9, h: 2, d: 9, color: 0x4a4e55 },
                { x: 0, y: 2.4, z: 0, w: 7.4, h: 0.8, d: 7.4, color: t.color },
              ]
            : naval
              ? [
                  // Deck sponson: a compact pedestal, no ground slab.
                  { x: 0, y: 0.5, z: 0, w: 4.4, h: 1, d: 4.4, color: 0x4a4e55 },
                  { x: 0, y: 1.4, z: 0, w: 3, h: 0.9, d: 3, color: t.color },
                ]
              : [
                  { x: 0, y: 1, z: 0, w: 9, h: 2, d: 9, color: 0x6f7174 },
                  { x: 0, y: 2.6, z: 0, w: 6, h: 1.4, d: 6, color: t.color },
                ],
        ),
        terrainMaterial,
      );
      root.add(base);

      const mount = new THREE.Group();
      mount.position.y = pivotY;
      mount.rotation.y = s.yaw ?? 0;
      root.add(mount);
      mount.add(
        new THREE.Mesh(
          mergeBoxes(
            main
              ? [
                  // Armored turret house with a sloped face.
                  { x: 0, y: 0.6, z: 0, w: 8, h: 3.2, d: 7, color: 0x3f444c },
                  { x: 4.2, y: 0.3, z: 0, w: 1.8, h: 2.4, d: 6.2, color: 0x363b42 },
                  { x: -0.5, y: 2.5, z: 0, w: 6.4, h: 0.6, d: 6.2, color: t.color },
                ]
              : [
                  { x: 0, y: 0.2, z: 0, w: 2.6, h: 2.2, d: 2.2, color: 0x3a3e45 },
                  { x: -1.6, y: 0.4, z: 0, w: 1.2, h: 1.2, d: 1.6, color: 0x2b2f36 },
                ],
          ),
          terrainMaterial,
        ),
      );

      const barrel = new THREE.Group();
      barrel.position.set(main ? 3.4 : 0.6, main ? 1.2 : 0.6, 0);
      mount.add(barrel);
      // Main battery carries twin long rifles; naval mounts twin short
      // barrels (mirroring the server's two rounds per shot); land flak
      // keeps the single tube.
      const tubes = main
        ? [
            // Long, slender 16"/50 Mark 7 rifles (Iowa class): the barrels run
            // far out past the gunhouse and sit close together.
            { x: 8.0, y: 0, z: 1.2, w: 16, h: 0.6, d: 0.6, color: 0x1c1e22 },
            { x: 8.0, y: 0, z: -1.2, w: 16, h: 0.6, d: 0.6, color: 0x1c1e22 },
            { x: 15.4, y: 0, z: 1.2, w: 1.4, h: 0.78, d: 0.78, color: 0x111316 },
            { x: 15.4, y: 0, z: -1.2, w: 1.4, h: 0.78, d: 0.78, color: 0x111316 },
          ]
        : naval
          ? [
              { x: 2.4, y: 0, z: 0.55, w: 4.8, h: 0.42, d: 0.42, color: 0x1c1e22 },
              { x: 2.4, y: 0, z: -0.55, w: 4.8, h: 0.42, d: 0.42, color: 0x1c1e22 },
              { x: 4.6, y: 0, z: 0.55, w: 0.7, h: 0.6, d: 0.6, color: 0x111316 },
              { x: 4.6, y: 0, z: -0.55, w: 0.7, h: 0.6, d: 0.6, color: 0x111316 },
            ]
          : [
              { x: 2.6, y: 0, z: 0, w: 5.2, h: 0.5, d: 0.5, color: 0x1c1e22 },
              { x: 5.0, y: 0, z: 0, w: 0.8, h: 0.8, d: 0.8, color: 0x111316 },
            ];
      barrel.add(new THREE.Mesh(mergeBoxes(tubes), terrainMaterial));

      // A flash off each tube's muzzle: the rifles' own twin tubes, the naval
      // mount's pair, the land flak's single barrel.
      const flash = main
        ? flashGroup(14, [1.2, -1.2])
        : naval
          ? flashGroup(3.6, [0.55, -0.55])
          : flashGroup(4.2);
      flash.position.set(main ? 16.1 : naval ? 4.95 : 5.4, 0, 0);
      barrel.add(flash);

      const wreck = new THREE.Mesh(
        mergeBoxes(
          main
            ? [
                { x: 0, y: 1.0, z: 0, w: 9, h: 2, d: 9, color: 0x3a3a3a },
                { x: 0.6, y: 3.0, z: 0.5, w: 6, h: 2.2, d: 5.4, color: 0x222222 },
                { x: 5.5, y: 2.6, z: -1.2, w: 8, h: 0.55, d: 0.55, color: 0x2e2a26 },
              ]
            : naval
              ? [
                  { x: 0, y: 0.5, z: 0, w: 4.4, h: 1, d: 4.4, color: 0x3a3a3a },
                  { x: 0.3, y: 1.6, z: 0.3, w: 2.4, h: 1.4, d: 2.4, color: 0x222222 },
                ]
              : [
                  { x: 0, y: 1, z: 0, w: 9, h: 2, d: 9, color: 0x3a3a3a },
                  { x: 0.5, y: 2.6, z: 0.4, w: 4, h: 1.6, d: 4, color: 0x222222 },
                  { x: -1.2, y: 2.2, z: -1.0, w: 2, h: 2.6, d: 1.6, color: 0x2e2a26 },
                ],
        ),
        terrainMaterial,
      );
      wreck.visible = false;
      root.add(wreck);

      this.scene.add(root);
      this.guns.set(s.id, {
        site: s,
        root, mount, barrel, flash, wreck, baseGroup: base,
        alive: true, team: s.team, kind: s.kind ?? 0, pivotY,
        occupied: false, srvYaw: s.yaw ?? 0, srvPitch: 0.5,
        muzzleX: main ? 16 : 5, // along the barrel, for the salvo blast
      });
      if ((s.kind ?? 0) === 0) this._upgradeEmplacement(s.id, EMPLACEMENTS.flak88);
      else if (s.kind === 1) this._upgradeEmplacement(s.id, EMPLACEMENTS.bofors40);
      else if (s.kind === 2 && !s.ship) this._upgradeEmplacement(s.id, EMPLACEMENTS.coastal);
  }

  // Swap a mount's boxes for its emplacement model: the model's base,
  // mount and gun take over (every level's mount and gun follow the aim —
  // see aimGun), the box barrel group moves onto the model's trunnion to keep
  // carrying the muzzle flash, and the wreck becomes the model's own.
  _upgradeEmplacement(id, spec) {
    const g = this.guns.get(id);
    emplacementOf(spec, g.team).then((m) => {
      if (!m || this.guns.get(id) !== g) return;
      // The box parts leave the scene graph entirely (respawns toggle
      // baseGroup/mount visibility, which must not bring them back).
      g.root.remove(g.baseGroup);
      for (const c of [...g.mount.children]) if (c.isMesh) g.mount.remove(c);
      for (const c of [...g.barrel.children]) if (c.isMesh && c !== g.flash) g.barrel.remove(c);
      // The model sits on the root (the ground or the tub floor) with its
      // trunnion exactly pivotY above it, where the server fires from.
      m.lod.position.y = g.pivotY - spec.gunY;
      g.root.add(m.lod);
      g.baseGroup = m.lod;
      g.model = m;
      g.barrel.position.set(spec.gunX, 0, 0);
      g.flash.position.x = spec.muzzle;
      g.muzzleX = spec.muzzle;
      if (spec.tubes) {
        g.muzzleZs = spec.tubes;
        setFlashTubes(g.flash, spec.tubes);
      }
      m.lod.visible = g.alive;
      aimGun(g, g.mount.rotation.y, g.barrel.rotation.z);
      // The earthwork under a shore pit (the server's flak pad, seated on
      // the highest ground across the ring): down past the lowest ground
      // under the ring, so on a slope the pit stands on a mound dug into
      // the hillside, not on air.
      const f = this.lastField;
      if (spec.plinthR && f) {
        const { x, y, z } = g.root.position;
        let low = Infinity;
        for (const [dx, dz] of [[0, 0], [1, 1], [1, -1], [-1, 1], [-1, -1]]) {
          low = Math.min(low, f.surfaceAt(x + dx * spec.plinthR * 0.7, z + dz * spec.plinthR * 0.7));
        }
        const base = y + g.pivotY - spec.gunY;
        const depth = base - (low - 1.5);
        if (depth > 0.2) {
          const plinth = plinthMesh(spec.plinthR, depth, spec.plinthColor);
          plinth.position.y = g.pivotY - spec.gunY;
          g.root.add(plinth);
        }
      }
      this._settleSite(g);
    });
    wreckOf(spec.stem, g.team).then((w) => {
      if (!w || this.guns.get(id) !== g) return;
      w.root.position.y = g.pivotY - spec.gunY;
      w.root.visible = !g.alive;
      g.root.remove(g.wreck);
      g.root.add(w.root);
      g.wreck = w.root;
      this._settleSite(g);
    });
  }

  /**
   * Seat every warship-mounted gun on its hull (after Ships.build): the gun's
   * root joins the hull's group so it sinks and lists with the ship, and a
   * main-battery mount hands its aim to the hull model's own turret and
   * _Guns nodes once the model is up — the box turret house leaves the scene.
   */
  linkShips(ships) {
    this.ships = ships;
    for (const [id, g] of this.guns) {
      const shipId = g.site?.ship;
      const hull = shipId ? ships.ships.get(shipId) : null;
      if (!hull) continue;
      hull.mesh.attach(g.root); // keeps its world pose; now rides the hull
      if (g.kind !== 2) continue;
      ships.turretLevels(shipId, g.site).then((levels) => {
        if (!levels || this.guns.get(id) !== g) return;
        g.root.remove(g.baseGroup);
        for (const c of [...g.mount.children]) if (c.isMesh) g.mount.remove(c);
        for (const c of [...g.barrel.children]) if (c.isMesh && c !== g.flash) g.barrel.remove(c);
        // A knocked-out turret stays part of the ship: no box wreck beside it.
        g.root.remove(g.wreck);
        g.wreck = new THREE.Group();
        g.baseGroup = new THREE.Group();
        g.model = { levels };
        g.barrel.position.set(IOWA_GUNS.x, IOWA_GUNS.y, 0);
        g.flash.position.x = IOWA_GUNS.muzzle;
        g.muzzleX = IOWA_GUNS.muzzle;
        g.muzzleZs = IOWA_GUNS.tubes;
        setFlashTubes(g.flash, IOWA_GUNS.tubes);
        aimGun(g, g.mount.rotation.y, g.barrel.rotation.z);
      });
    }
  }

  // A shore installation's model at real size on the server's pad, with a
  // concrete plinth down to the coarse ground, and its own burnt-out wreck.
  _upgradeStructure(id) {
    const g = this.guns.get(id);
    const spec = STRUCTURES[g.sub] ?? STRUCTURES[0];
    loadLevels(spec.stem).then((tpls) => {
      if (!tpls || this.guns.get(id) !== g) return;
      const { lod, levels } = lodOf(tpls, { team: g.team, dists: LOD_DIST.building });
      g.root.scale.setScalar(1); // the box stand-in's 1.6x is for boxes only
      g.root.remove(g.baseGroup);
      g.root.add(lod);
      g.baseGroup = lod;
      lod.visible = g.alive;
      // The radar's sweeping mount (and the dish's rest tilt, kept).
      g.sweep = levels.map((l) => l.nodes(/_Mount$/)[0]).filter(Boolean);
      this._plinth(g, spec);
      this._settleSite(g);
    });
    wreckOf(spec.stem, g.team).then((w) => {
      if (!w || this.guns.get(id) !== g) return;
      w.root.visible = !g.alive;
      g.root.remove(g.wreck);
      g.root.add(w.root);
      g.wreck = w.root;
      this._settleSite(g);
    });
  }

  // Concrete (or earth) under a pad-seated model, down past the lowest
  // ground its footprint covers — the pad sits on the highest (the server's
  // padY), so on a slope the downhill side shows a plinth rather than a gap.
  _plinth(g, spec) {
    const f = this.lastField;
    if (!f || !spec.plinthR) return;
    const p = g.root.getWorldPosition(new THREE.Vector3());
    let low = Infinity;
    for (const [dx, dz] of [[0, 0], [1, 1], [1, -1], [-1, 1], [-1, -1]]) {
      low = Math.min(low, f.surfaceAt(p.x + dx * spec.plinthR * 0.7, p.z + dz * spec.plinthR * 0.7));
    }
    const depth = p.y - (low - 1.5);
    if (depth <= 0.2) return;
    g.root.add(plinthMesh(spec.plinthR, depth, spec.plinthColor));
  }

  // The barrage balloon's model: the balloon node at the site (the root), the
  // winch on the ground `alt` metres below, the unit cable stretched between;
  // shot down, the envelope lies deflated beside the winch.
  _upgradeBalloon(id, alt) {
    const g = this.guns.get(id);
    loadModel(BALLOON.stem).then((t) => {
      if (this.guns.get(id) !== g) return;
      const inst = new ModelInstance(t, { team: g.team });
      const balloon = inst.node("Balloon");
      const cable = inst.node("Cable");
      if (!balloon || !cable) return;
      // Model origin (the winch) on the ground under the balloon.
      inst.root.position.y = -alt;
      balloon.position.y = alt;
      cable.scale.y = Math.max(1, alt - BALLOON.ringDrop - BALLOON.cableBase);
      g.root.remove(g.baseGroup);
      g.root.add(inst.root);
      g.baseGroup = inst.root;
      inst.root.visible = g.alive;
      this._settleSite(g);
    }, () => {});
    wreckOf(BALLOON.stem, g.team).then((w) => {
      if (!w || this.guns.get(id) !== g) return;
      w.root.position.y = -alt;
      w.root.visible = !g.alive;
      g.root.remove(g.wreck);
      g.root.add(w.root);
      g.wreck = w.root;
      this._settleSite(g);
    });
  }

  /** Per-frame life on the installations: the standing radars sweep. */
  update(dt) {
    for (const g of this.guns.values()) {
      if (!g.sweep || !g.alive) continue;
      for (const m of g.sweep) m.rotation.y += RADAR_SWEEP_RAD_S * dt;
    }
  }

  // A parked reserve plane grows into its class's model once the model lands
  // (the box airframe stays on as the far level of detail), stands on gear
  // sized to the model, and burns down to the model's own crash hulk. The
  // server never knows: same site, same hit sphere, same points.
  _upgradeParked(id, type, team) {
    const spec = modelForClass(type, CLASS_MODELS);
    if (!spec) return;
    const g = this.guns.get(id);
    loadLevels(spec.stem).then((tpls) => {
      if (!tpls || this.guns.get(id) !== g) return;
      const [box, gear] = g.baseGroup.children;
      const { lod } = lodOf(tpls, { team, dists: LOD_DIST.aircraft, far: box });
      g.baseGroup.add(lod);
      const a = anchorsFor(spec.stem, tpls[0]);
      if (gear) gear.geometry = mergeBoxes(parkedGearBoxes(a));
      if (a.wing.tipZ <= REVETMENT_HALF_SPAN) this._revetment(g, team);
    });
    wreckOf(spec.stem, team).then((w) => {
      if (!w || this.guns.get(id) !== g) return;
      // The hulk lies on the deck the airframe was parked 1.7 m above.
      w.root.position.y = -1.7;
      w.root.visible = !g.alive;
      g.root.remove(g.wreck);
      g.root.add(w.root);
      g.wreck = w.root;
      this._settleSite(g);
    });
  }

  // Each reserve fighter sits in its own earth revetment (art/airbase/
  // revetment.glb, Part 7): a U of berms around an 18 m concrete pad, open
  // end to the strip the plane's nose already points at (aaa.go parks them
  // that way). Child of the site's root, so it stays when the plane burns.
  _revetment(g, team) {
    loadLevels("revetment").then((tpls) => {
      if (!tpls || this.guns.get(g.site.id) !== g) return;
      const { lod } = lodOf(tpls, { team, dists: LOD_DIST.building });
      lod.position.y = -1.7; // the deck, under the lifted airframe
      g.root.add(lod);
      this._settleSite(g);
    });
  }

  /** Combined arms: re-flag mounts whose team changed mid-round — Fort
   * Brick's garrison joins whoever holds zone D. `teams` is the snapshot's
   * trailing gun-team section, index-aligned with the aa rows. */
  applyTeams(rows, teams) {
    if (!teams) return;
    for (let i = 0; i < teams.length && i < rows.length; i++) {
      const id = rows[i][0];
      const g = this.guns.get(id);
      if (!g || g.team === teams[i] || !g.site) continue;
      if (g.kind === CITY_KIND) {
        // Brickton's houses fly no colors: a new defender is just a new
        // owner (no rebuild of 37 blocks at the bell).
        g.team = teams[i];
        g.site = { ...g.site, team: teams[i] };
        continue;
      }
      g.root.removeFromParent();
      this.guns.delete(id);
      this.buildOne({ ...g.site, team: teams[i] }, this.lastField);
      this._settleSite(this.guns.get(id));
    }
  }

  /** Apply a snapshot's AA rows: [id, hp, yaw, pitch, firing, manned]. */
  applySnapshot(rows) {
    for (const [id, hp, yaw, pitch, firing, manned] of rows) {
      const g = this.guns.get(id);
      if (!g) continue;
      g.occupied = manned === 1;
      g.srvYaw = yaw;
      g.srvPitch = pitch;
      g.hp = hp; // a Brickton block smokes as it's hurt (brickton.js CityFires)
      const alive = hp > 0;
      if (alive !== g.alive) {
        g.alive = alive;
        g.mount.visible = alive;
        g.baseGroup.visible = alive;
        g.wreck.visible = !alive;
      }
      if (!alive) continue;
      // The local gunner's mount follows their predicted aim (setLocalAim),
      // not the lagged snapshot; reconciliation happens in main.js.
      if (id !== this.localAimId) {
        aimGun(g, yaw, pitch);
        g.flash.visible = firing === 1 && Math.random() > 0.5;
      }
    }
  }

  /** Drive one mount from the local gunner's predicted aim. */
  setLocalAim(id, yaw, pitch) {
    this.localAimId = id;
    const g = this.guns.get(id);
    if (!g) return;
    aimGun(g, yaw, pitch);
  }

  clearLocalAim() {
    this.localAimId = 0;
  }

  /**
   * Pose one mount by hand (the screenshot director, the marketing films):
   * a WORLD bearing and elevation, laid on the box parts and every model
   * level alike. Writing g.mount / g.barrel directly moves only the hidden box
   * anchors — the flash and the tracers' origin swing round while the model
   * you can see stays where the last snapshot left it.
   */
  aim(id, yaw, pitch) {
    const g = this.guns.get(id);
    if (g) aimGun(g, yaw, pitch);
  }

  /** Live slot state for the spawn picker. */
  statusOf(id) {
    const g = this.guns.get(id);
    return g ? { alive: g.alive, occupied: g.occupied } : null;
  }

  /** World position of the barrel pivot (camera anchor while manning). A
   * deck mount's root rides its hull (linkShips), so ask for the world pose. */
  pivotOf(id) {
    const g = this.guns.get(id);
    if (!g) return null;
    return g.mount.getWorldPosition(new THREE.Vector3());
  }

  positionOf(id) {
    return this.guns.get(id)?.root.getWorldPosition(new THREE.Vector3()) ?? null;
  }

  /** Nearest live crewed-type mount to a world position. Gunners are parked
   * exactly on their mount's pivot, so this maps a gunner back to their gun
   * (the spectator cam uses it; 0 = nothing close enough). */
  nearestTo(pos, maxDist = 12) {
    let bestId = 0;
    let best = maxDist * maxDist;
    for (const [id, g] of this.guns) {
      if (!g.alive || g.kind === 3 || g.kind === 6 || g.kind === CITY_KIND) continue;
      // The site is the server's pivot, in world space even for a deck mount
      // whose root rides its hull.
      const p = g.site ?? g.root.position;
      const dx = pos.x - p.x;
      const dy = pos.y - (g.site ? p.y : p.y + g.pivotY);
      const dz = pos.z - p.z;
      const d2 = dx * dx + dy * dy + dz * dz;
      if (d2 < best) {
        best = d2;
        bestId = id;
      }
    }
    return bestId;
  }
}

/**
 * Supply trucks driving the highway, driven by server snapshots: a blocky
 * cab + canvas-bed lorry wearing the team color. A lorry is drawn on the
 * snapshot clock the way the hulls are (hullpath.js, at the render clock
 * INTERP_DELAY behind the newest row), so the 20 Hz rows read as one steady
 * drive — chasing the newest row surged her twenty times a second and
 * clicked her round a bend like a clock hand (flightsim-79a).
 */
// The server carries a hull 1 m over the ground it rides (landcombat.go /
// vehicle.go: truckSurfaceY + 1.0) and a convoy lorry 1.2 m over the road
// (convoy.go). The models stand on the ground (the art contract's y 0), so
// both are drawn that far below the snapshot's anchor.
const HULL_ANCHOR_M = 1.0;
const TRUCK_ANCHOR_M = 1.2;
// Scratch for the land hulls' tilt and world-true aim (GroundVehicles).
const _aimDir = new THREE.Vector3();
const _aimQ = new THREE.Quaternion();
const _tiltE = new THREE.Euler();
const _tiltQ = new THREE.Quaternion();
const _UP = new THREE.Vector3(0, 1, 0);

export class Trucks {
  constructor(scene) {
    this.scene = scene;
    this.field = null; // main.js hands over the terrain: lorries pitch with the road
    this.trucks = new Map(); // id -> {mesh, hist, target:{x,y,z,yaw} (the drawn pose), seen}
    this.geoCache = new Map(); // team -> geometry
  }

  geometry(team) {
    if (this.geoCache.has(team)) return this.geoCache.get(team);
    const t = TEAM_INFO[team] ?? TEAM_INFO[0];
    const geo = mergeBoxes([
      // Chassis, cab with glass, canvas bed with the team band.
      { x: 0, y: 0.6, z: 0, w: 7.6, h: 1.0, d: 3.0, color: 0x2b2f36 },
      { x: 2.6, y: 1.9, z: 0, w: 2.4, h: 1.8, d: 2.8, color: 0x4a4d52 },
      { x: 3.6, y: 2.2, z: 0, w: 0.4, h: 1.0, d: 2.4, color: 0x9fd8f0 },
      { x: -1.1, y: 2.2, z: 0, w: 4.8, h: 2.2, d: 3.0, color: 0x6b5b40 },
      { x: -1.1, y: 3.35, z: 0, w: 4.9, h: 0.5, d: 3.1, color: t.color },
      // Wheels.
      { x: 2.6, y: 0.5, z: 1.5, w: 1.1, h: 1.1, d: 0.5, color: 0x16171a },
      { x: 2.6, y: 0.5, z: -1.5, w: 1.1, h: 1.1, d: 0.5, color: 0x16171a },
      { x: -2.2, y: 0.5, z: 1.5, w: 1.1, h: 1.1, d: 0.5, color: 0x16171a },
      { x: -2.2, y: 0.5, z: -1.5, w: 1.1, h: 1.1, d: 0.5, color: 0x16171a },
    ]);
    this.geoCache.set(team, geo);
    return geo;
  }

  /** Apply a snapshot's truck rows: [id, x, y, z, yaw, hp, team]. The row's
   * y is the server's anchor, TRUCK_ANCHOR_M over the road; the lorry is drawn
   * on the road itself. */
  applySnapshot(rows, now) {
    for (const [id, x, sy, z, yaw, , team] of rows) {
      const y = sy - TRUCK_ANCHOR_M;
      let t = this.trucks.get(id);
      if (!t) {
        // A group, so the CCKW model can take the box lorry's place when it
        // lands (Part 6b); the box stays as the far level of detail.
        const mesh = new THREE.Group();
        const box = new THREE.Mesh(this.geometry(team), terrainMaterial);
        mesh.add(box);
        t = { mesh, box, hist: [], target: { x, y, z, yaw }, team };
        t.mesh.position.set(x, y, z);
        t.mesh.rotation.y = yaw;
        this.scene.add(t.mesh);
        this.trucks.set(id, t);
        this._upgrade(t);
      }
      // The row joins the lorry's history; update() poses her between the
      // rows either side of the render clock.
      pushHullSample(t.hist, now, x, y, z, yaw, 0, 0);
      t.seen = now;
    }
    for (const [id, t] of this.trucks) {
      if (now - (t.seen ?? 0) > 1.5) {
        this.scene.remove(t.mesh);
        this.trucks.delete(id);
      }
    }
  }

  // The GMC CCKW for the convoy's lorries, the box kept as the far LOD.
  _upgrade(t) {
    loadLevels("cckw_truck").then((tpls) => {
      if (!tpls || !t.mesh.parent) return;
      const { lod } = lodOf(tpls, { team: t.team, dists: LOD_DIST.vehicle, far: t.box });
      t.mesh.add(lod);
    });
  }

  /** A strafed lorry burns where it stopped (the model's `_wreck`). */
  leaveWreck(id) {
    const t = this.trucks.get(id);
    if (!t) return;
    const at = t.mesh.position.clone();
    const yaw = t.mesh.rotation.y;
    wreckOf("cckw_truck", t.team).then((w) => {
      if (!w) return;
      w.root.position.copy(at);
      w.root.rotation.y = yaw;
      this.scene.add(w.root);
      this.hulks ??= [];
      this.hulks.push({ root: w.root, t: 0 });
      while (this.hulks.length > 16) this.scene.remove(this.hulks.shift().root);
    });
  }

  /** Pose every lorry at `renderT`, the remote-interpolation clock (0 — no
   * clock yet — draws the newest row). */
  update(dt, renderT = 0) {
    const k = 1 - Math.exp(-10 * dt);
    const f = this.field;
    for (const t of this.trucks.values()) {
      if (t.hist.length) hullPoseAt(t.hist, renderT > 0 ? renderT : t.hist[t.hist.length - 1].t, t.target);
      t.mesh.position.set(t.target.x, t.target.y, t.target.z);
      // Nose up the grade the lorry is climbing (level on the bridge deck,
      // whose water below reads as far off the road's own height).
      let pitch = 0;
      if (f) {
        const c = Math.cos(t.target.yaw), s = Math.sin(t.target.yaw);
        const g0 = t.target.y;
        const at = (px, pz) => {
          const h = f.surfaceAt(px, pz);
          return h - g0 > 2.5 || g0 - h > 2.5 ? g0 : h;
        };
        pitch = Math.atan2(at(t.target.x + c * 3.5, t.target.z - s * 3.5) - at(t.target.x - c * 3.5, t.target.z + s * 3.5), 7);
      }
      t.pitch = (t.pitch ?? pitch) + (pitch - (t.pitch ?? pitch)) * k;
      t.mesh.rotation.set(0, t.target.yaw, t.pitch, "YZX");
    }
    // Burnt-out lorries clear after a couple of minutes.
    for (let i = (this.hulks?.length ?? 0) - 1; i >= 0; i--) {
      const h = this.hulks[i];
      if ((h.t += dt) > 120) {
        this.scene.remove(h.root);
        this.hulks.splice(i, 1);
      }
    }
  }

  positionOf(id) {
    return this.trucks.get(id)?.mesh.position ?? null;
  }

  /** Minimap blips: [{x, z, team}]. */
  blips() {
    const out = [];
    for (const t of this.trucks.values()) {
      out.push({ x: t.mesh.position.x, z: t.mesh.position.z, team: t.team ?? 0 });
    }
    return out;
  }
}

/** Darken a hex color by factor k. */
function shadeHex(hex, k) {
  return new THREE.Color(hex).multiplyScalar(k).getHex();
}

/**
 * Supply ships — the naval convoys — steaming their sea lanes, driven by
 * server snapshots: a Liberty ship (Part 5.4 of art/model-integration.md),
 * 134.6 m of welded freighter with the team band on her funnel, floating at
 * the waterline. The naval counterpart to the highway Trucks, and drawn the
 * same way: on the snapshot clock (hullpath.js), so the 20 Hz rows read as
 * smooth steaming, with a gentle bob on the swell. The old blocky freighter — hull, deck cargo
 * wearing the team band, aft bridge house, funnel and derricks — stays as the
 * far level of detail and the no-models fallback, stretched to the Liberty's
 * length and beam so the hull the server shoots at is the hull you see.
 */
// The box freighter was built 46 × 9.4 m; the Liberty (and the server's keel,
// supplyship.go supplyShipHalf) is 134.6 × 17.3 with ~8 m of freeboard.
const FREIGHTER_BOX_SCALE = [134.6 / 46, 2.0, 17.3 / 9.4];
// Where the smoke leaves each silhouette's funnel (ship-local x, y).
const LIBERTY_FUNNEL = [-4.3, 21.6];
const FREIGHTER_BOX_FUNNEL = [-16 * FREIGHTER_BOX_SCALE[0], 13.8 * FREIGHTER_BOX_SCALE[1]];
// A sunk Liberty: the wreck model is already broken-backed; it settles by the
// stern and slips under over this long, then clears.
const LIBERTY_SINK_SEC = 45;

export class SupplyShips {
  constructor(scene, particles = null) {
    this.scene = scene;
    this.particles = particles;
    this.ships = new Map(); // id -> {mesh, box, hist, target:{x,y,z,yaw} (the drawn pose), seen, bob, smokeAt, funnel}
    this.geoCache = new Map(); // team -> geometry
    this.hulks = []; // sinking wrecks: {root, t, y0}
    this.t = 0;
  }

  geometry(team) {
    if (this.geoCache.has(team)) return this.geoCache.get(team);
    const t = TEAM_INFO[team] ?? TEAM_INFO[0];
    const hull = 0x55606b, hullDark = 0x3f464e, deck = 0x6a5f4a;
    const hatch = 0x4a4d52, crate = 0x7a6a4a, sup = 0x6d727b;
    const glass = 0x9fd8f0, funnelDark = 0x2b2f36, gunmetal = 0x1c1e22;
    const navRed = 0xff4444, navGreen = 0x35e06a;
    const geo = mergeBoxes([
      // Hull with a darker boot line at the waterline, tapered bow and stern.
      { x: 0, y: 1.4, z: 0, w: 46, h: 5, d: 9, color: hull },
      { x: 0, y: -1.2, z: 0, w: 46, h: 3, d: 9.4, color: hullDark },
      { x: 23, y: 1.9, z: 0, w: 6, h: 4.2, d: 6.4, color: hull },
      { x: -22, y: 1.7, z: 0, w: 5, h: 4.0, d: 7.0, color: hull },
      // Weather deck and forward cargo hatch covers.
      { x: 4, y: 3.9, z: 0, w: 40, h: 0.6, d: 8.4, color: deck },
      { x: 10, y: 4.6, z: 0, w: 9, h: 1.2, d: 6.4, color: hatch },
      { x: 0, y: 4.6, z: 0, w: 8, h: 1.2, d: 6.4, color: hatch },
      // Deck cargo: a stack of crates with the team recognition band.
      { x: 5, y: 6.0, z: 0, w: 7, h: 2.0, d: 5.0, color: crate },
      { x: 5, y: 7.35, z: 0, w: 7.2, h: 0.6, d: 5.2, color: t.color },
      // Aft superstructure: bridge house, glass and a team stripe.
      { x: -13, y: 6.2, z: 0, w: 9, h: 5, d: 7.6, color: sup },
      { x: -13, y: 9.0, z: 0, w: 6.5, h: 2.4, d: 6.6, color: sup },
      { x: -10.6, y: 9.3, z: 0, w: 1.0, h: 1.4, d: 5.6, color: glass },
      { x: -13, y: 7.75, z: 0, w: 9.2, h: 0.5, d: 7.8, color: shadeHex(t.color, 0.9) },
      // Funnel with the team band, mast above the bridge.
      { x: -16, y: 11.2, z: 0, w: 3.2, h: 3.4, d: 3.0, color: funnelDark },
      { x: -16, y: 12.6, z: 0, w: 3.4, h: 0.9, d: 3.2, color: t.color },
      { x: -13, y: 11.6, z: 0, w: 0.6, h: 3.6, d: 0.6, color: gunmetal },
      // Cargo derricks (kingposts) fore and aft of the holds.
      { x: 17, y: 6.0, z: 0, w: 0.7, h: 5.0, d: 0.7, color: gunmetal },
      { x: 17, y: 8.3, z: 0, w: 0.6, h: 0.6, d: 5.4, color: gunmetal },
      { x: -6, y: 6.0, z: 0, w: 0.7, h: 5.0, d: 0.7, color: gunmetal },
      { x: -6, y: 8.3, z: 0, w: 0.6, h: 0.6, d: 5.4, color: gunmetal },
      // Nav lights at the bow.
      { x: 25, y: 3.6, z: 3.0, w: 0.7, h: 0.7, d: 0.7, color: navGreen },
      { x: 25, y: 3.6, z: -3.0, w: 0.7, h: 0.7, d: 0.7, color: navRed },
    ]);
    this.geoCache.set(team, geo);
    return geo;
  }

  /** Apply a snapshot's supply-ship rows: [id, x, y, z, yaw, hp, team]. */
  applySnapshot(rows, now) {
    for (const [id, x, y, z, yaw, , team] of rows) {
      let s = this.ships.get(id);
      if (!s) {
        // A group, so the Liberty can take the box freighter's place when it
        // lands; the box stays as the far level of detail.
        const mesh = new THREE.Group();
        const box = new THREE.Mesh(this.geometry(team), terrainMaterial);
        box.scale.set(...FREIGHTER_BOX_SCALE);
        mesh.add(box);
        s = { mesh, box, hist: [], target: { x, y, z, yaw }, team, bob: Math.random() * Math.PI * 2, smokeAt: 0, funnel: FREIGHTER_BOX_FUNNEL };
        s.mesh.position.set(x, y, z);
        s.mesh.rotation.y = yaw;
        this.scene.add(s.mesh);
        this.ships.set(id, s);
        this._upgrade(s);
      }
      pushHullSample(s.hist, now, x, y, z, yaw, 0, 0);
      s.seen = now;
    }
    for (const [id, s] of this.ships) {
      if (now - (s.seen ?? 0) > 1.5) {
        this.scene.remove(s.mesh);
        this.ships.delete(id);
      }
    }
  }

  // The Liberty ship, the box freighter kept as the far LOD.
  _upgrade(s) {
    loadLevels("liberty_ship", { lods: false }).then((tpls) => {
      if (!tpls || !s.mesh.parent) return;
      const { lod } = lodOf(tpls, { team: s.team, dists: LOD_DIST.ship, far: s.box });
      s.mesh.add(lod);
      s.funnel = LIBERTY_FUNNEL;
    });
  }

  /**
   * A sunk freighter goes down where she was hit: the Liberty's `_wreck`,
   * already broken-backed at the well deck, settles by the stern and slips
   * under the swell over LIBERTY_SINK_SEC.
   */
  leaveWreck(id) {
    const s = this.ships.get(id);
    if (!s) return;
    const at = s.mesh.position.clone();
    const yaw = s.mesh.rotation.y;
    wreckOf("liberty_ship", s.team).then((w) => {
      if (!w) return;
      w.root.position.copy(at);
      w.root.rotation.y = yaw;
      this.scene.add(w.root);
      this.hulks.push({ root: w.root, t: 0, y0: at.y });
      while (this.hulks.length > 6) this.scene.remove(this.hulks.shift().root);
    });
  }

  /** Pose every freighter at `renderT`, the remote-interpolation clock (0 —
   * no clock yet — draws the newest row). */
  update(dt, renderT = 0) {
    this.t += dt;
    for (const s of this.ships.values()) {
      if (s.hist.length) hullPoseAt(s.hist, renderT > 0 ? renderT : s.hist[s.hist.length - 1].t, s.target);
      // Gentle bob and roll on the swell so the hull reads as afloat.
      const ph = this.t * 0.9 + s.bob;
      s.mesh.position.set(s.target.x, s.target.y + Math.sin(ph) * 0.3, s.target.z);
      s.mesh.rotation.y = s.target.yaw;
      s.mesh.rotation.z = Math.sin(ph * 0.7) * 0.025;
      this.emitSmoke(s, dt);
    }
    // Wrecks settle by the stern and go under: slow at first while she
    // floods, then faster once the well deck is awash.
    for (let i = this.hulks.length - 1; i >= 0; i--) {
      const h = this.hulks[i];
      h.t += dt;
      const f = Math.min(1, h.t / LIBERTY_SINK_SEC);
      h.root.position.y = h.y0 - 34 * f * f;
      h.root.rotation.z = 0.12 * f; // stern first
      if (h.t > LIBERTY_SINK_SEC) {
        this.scene.remove(h.root);
        this.hulks.splice(i, 1);
      }
    }
  }

  // A light coal-smoke plume off the funnel — climbs tall and streams astern
  // to show the ship making way, billowing wider as it rises (skinny at the
  // stack, thicker aloft) so the freighters are easy to pick out against the
  // open sea without reading as a fire. Leaves whichever funnel is drawn
  // (s.funnel: the Liberty's, or the stretched box freighter's).
  emitSmoke(s, dt) {
    if (!this.particles) return;
    s.smokeAt -= dt;
    if (s.smokeAt > 0) return;
    // A soft puff billows to four times the blocks' size and lives five
    // seconds, so a few a second keep the column whole.
    s.smokeAt = this.particles.soft ? 0.35 : 0.09;
    const yaw = s.mesh.rotation.y;
    const [lx, ly] = s.funnel;
    const p = s.mesh.position;
    // Astern (opposite the bow) plus a touch of lift: the fresh smoke is blown
    // back over the wake, then gravity carries the column up. A tight spread
    // keeps it a coherent column; grow widens it with age (skinny at the
    // stack, billowing aloft) rather than scattering it like debris.
    const dir = new THREE.Vector3(-Math.cos(yaw), 0.45, Math.sin(yaw)).normalize();
    this.particles.puff(
      new THREE.Vector3(
        p.x + lx * Math.cos(yaw) + (Math.random() - 0.5) * 0.4,
        p.y + ly,
        p.z - lx * Math.sin(yaw) + (Math.random() - 0.5) * 0.4,
      ),
      // A light grey plume, skinny at the stack and billowing aloft.
      {
        size: 2.4, grow: 2.2, speed: 0.5, alpha: 0.65, tint: [1.3, 1.3, 1.32],
        vel: { x: dir.x * 4.7, y: dir.y * 4.7, z: dir.z * 4.7 }, rise: 1.2, drag: 0.6,
      },
      {
        count: 1, speed: 4.0, life: 4.2, size: 0.85,
        colors: [0xb4b4b4, 0xcfcfcf, 0xa6a6a6, 0xe0e0e0],
        gravity: -4.0, drag: 0.6, dir, spread: 0.28, grow: true,
      },
    );
  }

  positionOf(id) {
    return this.ships.get(id)?.mesh.position ?? null;
  }

  /** Minimap blips: [{x, z, team}]. */
  blips() {
    const out = [];
    for (const s of this.ships.values()) {
      out.push({ x: s.mesh.position.x, z: s.mesh.position.z, team: s.team ?? 0 });
    }
    return out;
  }
}

// The widest airframe (half-span, m) a revetment's 17.8 m-wide pad takes:
// fighters and the Avenger, not the Fortress.
const REVETMENT_HALF_SPAN = 8.6;

// Local height of each vehicle type's mount above the hull origin (meters):
// tank turret ring, half-track flak bed, scout-car cupola.
const VEHICLE_TURRET_Y = [1.9, 1.37, 1.55]; // the Sherman's ring, the halftrack's turntable, the M8's ring
// Land-combat theater mount pivots: Abrams turret ring, Bradley turret,
// Sd.Kfz. 7/1 flak bed, MUTT pintle. Mirrors the 2.2 m server mount height
// closely enough that shells appear to leave the barrels.
const LAND_TURRET_Y = [1.95, 2.3, 1.37, 1.35]; // M1, M2, the flak halftrack, the MUTT's pedestal
// Barrel-local muzzle tips for the land vehicles' firing flash.
const LAND_MUZZLES = [
  [{ x: 6.6, y: 0.5, z: 0 }], // Abrams 120mm
  [{ x: 2.6, y: 0.45, z: 0 }], // Bradley 25mm
  [
    { x: 2.9, y: 0.3, z: 0.35 }, { x: 2.9, y: 0.3, z: -0.35 },
    { x: 2.9, y: 0.7, z: 0.35 }, { x: 2.9, y: 0.7, z: -0.35 },
  ], // Sd.Kfz. 7/1 quad 20
  [{ x: 4.4, y: 0.55, z: 0 }], // MUTT 106mm recoilless
];
// The box class each named hull draws as (shipModels and every SHIP_* table
// below are indexed by it): the roster slot once named the class, but the WW2
// and jet-age rosters disagree past slot 3 — the premium Yamato and the Osa
// both sail as hull 4 — so the name decides. The Yamato borrows the battleship
// silhouette, which was always built as her analogue, stretched to her 263 m.
// A hull with no entry keeps its slot (the Osa's 5 is past every table, as
// her 4 was before the Yamato took it).
const SHIP_BOX_CLASS = { Fletcher: 0, County: 1, Iowa: 2, "Des Moines": 3, Yamato: 4, Osa: 5 };
// Naval theater: the main-battery mount rides higher, up on the forecastle deck.
// Index by box class: destroyer, cruiser, battleship, Des Moines (heavy
// cruiser), Yamato (the battleship's boxes).
const SHIP_TURRET_Y = [2.9, 3.6, 4.8, 3.7, 4.8];
// Forward main-battery mount offset toward the bow (local +x metres). The turret
// rides up on the forecastle, not amidships, so this offset is rotated by the
// hull heading each frame (a fixed root-space offset would swing off the side as
// the ship turns). Mirrors navalMuzzle() in internal/game/navalcombat.go.
const SHIP_TURRET_X = [20, 26, 40, 30, 40];
// Mount heights for each hull's FULL turret layout, one entry per turret in
// mainGun.turrets (shared/config.json) in the same order: the lead mount on the
// forecastle, a superfiring mount raised on its barbette, the aft mount on the
// quarterdeck / deckhouse. Keel positions and arcs come from the config so the
// visuals can never drift from what the server actually fires.
const SHIP_MOUNT_Y = [
  [2.9, 2.6], // destroyer: A forward, X aft
  [3.6, 4.4], // cruiser: A forward, X on the aft deckhouse
  [4.8, 7.5, 4.6], // battleship: A, B superfiring, C aft
  [3.7, 6.1, 4.5], // Des Moines: A, B superfiring, X on the aft deckhouse
  [4.8, 7.5, 4.6], // Yamato: the battleship's mounts, stretched with her hull
];
// How fast a mount visually trains between its stowed facing and the aim
// (rad/s). Fire control (which guns actually shoot) is the server's arc check;
// this is just the turret swinging to match.
const SHIP_MOUNT_SLEW = 1.6;
// The captain's views, scaled to the real-scale hull she's crewing (Part 5.4).
// The chase boom trails the pivot by her half-length plus the same ~93 m of sea
// the 64 m box destroyer always showed astern, and climbs with her masthead so
// an Iowa's fighting top never walls off the view ahead. The gunsight eye —
// which the rangefinder ray shares, so the range is the exact spot under the
// crosshair — rides 4 m over her own masthead, floored at the 34 m every sight
// pitch was tuned at: from there the scope looks out past the director instead
// of from inside it, and the height spreads the far ranges across more sight
// pitch, so laying 8–13 km stays controllable.
const SHIP_CHASE_BACK_M = 93;
const SHIP_CHASE_UP_M = 60;
const NAVAL_DIRECTOR_MIN_Y = 34;
// The turret arc rule (shipTurretBears, SHIP_TURRET_ARC_DEG) lives in
// gunsight.js, beside the salvo flight that shares it.
// An angle folded into −π..π (a mount's train state accumulates as it slews).
const wrapPi = (a) => Math.atan2(Math.sin(a), Math.cos(a));

// Muzzle tips of the trainable main battery in barrel-local metres (one per
// tube, matching each class's barrel geometry below), and the relative weight
// of the blast each class throws — the Iowa gets the full broadside
// fireball, a destroyer's twin 5in a squib by comparison.
const SHIP_MUZZLES = [
  [{ x: 7.9, y: 0.8, z: 0 }],
  [{ x: 11.4, y: 1.1, z: 1.4 }, { x: 11.4, y: 1.1, z: -1.4 }],
  [{ x: 19.5, y: 1.4, z: 2.6 }, { x: 19.5, y: 1.4, z: 0 }, { x: 19.5, y: 1.4, z: -2.6 }],
  [{ x: 12.4, y: 1.05, z: 2.2 }, { x: 12.4, y: 1.05, z: 0 }, { x: 12.4, y: 1.05, z: -2.2 }],
  [{ x: 19.5, y: 1.4, z: 2.6 }, { x: 19.5, y: 1.4, z: 0 }, { x: 19.5, y: 1.4, z: -2.6 }],
];
// The Yamato's 46 cm rifles throw the biggest fireball afloat — a step past
// the Iowa's full broadside, so the premium's salvo reads from across the map.
const SHIP_BLAST_K = [0.45, 0.7, 1.0, 0.6, 1.25];

/**
 * Armored ground vehicles (heavy tank, quad-20 AA half-track, light scout car)
 * rolling the highway, driven by server snapshots. Each is a box-built hull
 * wearing the team color with a turret/mount that tracks the gun's aim and a
 * barrel that elevates with it. Positions and aim ease toward the latest
 * snapshot so the 20 Hz updates read as smooth driving.
 */
// The founder: how long a killed warship takes to slip under, and how far she
// drops. 13 s is tuned to the killcam — the clip ships killcamDelaySec (2.5 s)
// after death and orbits the wreck until ~11 s, so the whole replay watches
// her go down and the last beat is the masts disappearing. Depth clears the
// tallest superstructure with the heel folded in — the floor for every hull;
// a real-scale model's masthead (NAVAL_MODELS top) takes a big ship deeper.
const HULL_SINK_SEC = 13;
const HULL_SINK_DEPTH_M = 36;

export class GroundVehicles {
  constructor(scene, theater = "", types = [], particles = null) {
    this.field = null; // main.js hands over the terrain: land hulls tilt to it (_tilt)
    this.scene = scene;
    this.particles = particles; // steam + secondaries as a foundering hull goes down
    this.naval = theater === "naval";
    this.land = theater === "land";
    this.types = types; // config.groundVehicles.types: turret layouts ride along
    // Combined arms fields a MIXED roster (warships first, then armor), so
    // every model/mount decision dispatches on the TYPE's class flag, with
    // the theater as the fallback for rosters that don't carry flags (the
    // air game's column vehicles). The per-class art tables are indexed
    // within their class, so land types are offset by the first land index.
    this.landBase = 0;
    for (let i = 0; i < types.length; i++) {
      if (types[i]?.land) {
        this.landBase = i;
        break;
      }
    }
    this.vehicles = new Map(); // id -> {root, hull, mounts, target, team, type, seen}
    this.wrecks = new Map(); // id -> foundering hulls riding out the settle-and-list (beginSink)
    this.geoCache = new Map(); // `${type}:${team}` -> {hull, turret, barrel}
    this.localAimId = 0; // vehicle driven by the local player: aim skips snapshots
  }

  shipType(type) {
    const t = this.types?.[type];
    return t ? !!t.naval : this.naval;
  }

  landType(type) {
    const t = this.types?.[type];
    return t ? !!t.land : this.land;
  }

  landIdx(type) {
    return type - this.landBase;
  }

  /** A warship type's box class (SHIP_BOX_CLASS): its name's, else its slot. */
  shipArt(type) {
    return SHIP_BOX_CLASS[this.types?.[type]?.name] ?? type;
  }

  models(type, team) {
    const key = `${type}:${team}`;
    if (this.geoCache.has(key)) return this.geoCache.get(key);
    const t = TEAM_INFO[team] ?? TEAM_INFO[0];
    if (this.shipType(type)) {
      const m = this.shipModels(this.shipArt(type), t);
      // The box stand-in stretched over the real-scale hull (Part 5.4): the
      // server's hull box and gun pivots are the model's whether or not the
      // model has arrived, so the boxes have to cover the same water. Height
      // goes by the lesser stretch — the old silhouettes were built short and
      // slim, not flat — and a hull smaller than its silhouette (the Osa
      // borrowed the destroyer's) shrinks its gun house to match.
      const ty = this.types?.[type];
      if (ty?.hullHalfLenM > 0 && ty?.hullHalfBeamM > 0) {
        m.hull.computeBoundingBox();
        const b = m.hull.boundingBox;
        const sx = (2 * ty.hullHalfLenM) / (b.max.x - b.min.x);
        const sz = (2 * ty.hullHalfBeamM) / (b.max.z - b.min.z);
        const sy = Math.min(sx, sz);
        m.hull.scale(sx, sy, sz);
        if (sy < 1) {
          m.turret.scale(sy, sy, sy);
          m.barrel.scale(sy, sy, sy);
        }
        m.stretch = { sx, sy, sz };
      }
      this.geoCache.set(key, m);
      return m;
    }
    if (this.landType(type)) {
      const m = this.landModels(this.landIdx(type), t);
      this.geoCache.set(key, m);
      return m;
    }
    let hull, turret, barrel;
    if (type === 0) {
      // Heavy tank: long low hull with track guards and a big turret + main gun.
      hull = mergeBoxes([
        { x: 0, y: 0.8, z: 0, w: 7.4, h: 1.3, d: 3.0, color: 0x3a4038 },
        { x: 0, y: 0.5, z: 1.85, w: 7.6, h: 1.5, d: 0.6, color: 0x16171a },
        { x: 0, y: 0.5, z: -1.85, w: 7.6, h: 1.5, d: 0.6, color: 0x16171a },
        { x: -0.4, y: 1.55, z: 0, w: 4.6, h: 0.4, d: 2.8, color: t.color },
      ]);
      turret = mergeBoxes([{ x: -0.3, y: 0.45, z: 0, w: 3.4, h: 1.0, d: 2.6, color: 0x474d44 }]);
      barrel = mergeBoxes([{ x: 3.0, y: 0.5, z: 0, w: 4.6, h: 0.42, d: 0.42, color: 0x2c302b }]);
    } else if (type === 1) {
      // Quad 20 mm AA half-track: cab forward, open flak bed aft, four tubes.
      hull = mergeBoxes([
        { x: 1.6, y: 0.85, z: 0, w: 3.2, h: 1.4, d: 2.8, color: 0x3b4a3b },
        { x: 2.5, y: 2.0, z: 0, w: 1.7, h: 1.4, d: 2.6, color: 0x4a5a4a },
        { x: 2.9, y: 2.2, z: 0, w: 0.3, h: 0.8, d: 2.0, color: 0x9fd8f0 },
        { x: -1.8, y: 0.75, z: 0, w: 4.4, h: 1.0, d: 3.0, color: 0x2f3a2f },
        { x: -1.8, y: 1.35, z: 0, w: 4.5, h: 0.4, d: 3.1, color: t.color },
        { x: 2.9, y: 0.45, z: 1.5, w: 1.1, h: 1.1, d: 0.5, color: 0x16171a },
        { x: 2.9, y: 0.45, z: -1.5, w: 1.1, h: 1.1, d: 0.5, color: 0x16171a },
      ]);
      turret = mergeBoxes([{ x: -1.8, y: 0.4, z: 0, w: 1.4, h: 0.8, d: 1.4, color: 0x55654f }]);
      barrel = mergeBoxes([
        { x: 1.2, y: 0.32, z: 0.3, w: 2.9, h: 0.16, d: 0.16, color: 0x20241e },
        { x: 1.2, y: 0.32, z: -0.3, w: 2.9, h: 0.16, d: 0.16, color: 0x20241e },
        { x: 1.2, y: 0.66, z: 0.3, w: 2.9, h: 0.16, d: 0.16, color: 0x20241e },
        { x: 1.2, y: 0.66, z: -0.3, w: 2.9, h: 0.16, d: 0.16, color: 0x20241e },
      ]);
    } else {
      // Light armored scout car: small four-wheel hull, MG cupola.
      hull = mergeBoxes([
        { x: 0, y: 0.8, z: 0, w: 5.0, h: 1.1, d: 2.4, color: 0x4a4032 },
        { x: -0.4, y: 1.7, z: 0, w: 2.8, h: 1.0, d: 2.1, color: 0x5a4f3e },
        { x: -0.4, y: 2.35, z: 0, w: 2.6, h: 0.4, d: 1.9, color: t.color },
        { x: 1.6, y: 0.45, z: 1.2, w: 1.0, h: 1.0, d: 0.4, color: 0x16171a },
        { x: 1.6, y: 0.45, z: -1.2, w: 1.0, h: 1.0, d: 0.4, color: 0x16171a },
        { x: -1.6, y: 0.45, z: 1.2, w: 1.0, h: 1.0, d: 0.4, color: 0x16171a },
        { x: -1.6, y: 0.45, z: -1.2, w: 1.0, h: 1.0, d: 0.4, color: 0x16171a },
      ]);
      turret = mergeBoxes([{ x: -0.4, y: 0.4, z: 0, w: 1.2, h: 0.7, d: 1.2, color: 0x5f5440 }]);
      barrel = mergeBoxes([{ x: 1.1, y: 0.34, z: 0, w: 2.0, h: 0.14, d: 0.14, color: 0x20242a }]);
    }
    const m = { hull, turret, barrel };
    this.geoCache.set(key, m);
    return m;
  }

  // landModels builds the land theater's armored vehicles: 0 the Abrams MBT
  // (low flat hull, side skirts, big angular turret, long 120mm),
  // 1 the Bradley IFV (tall sloped hull, small two-man turret
  // with a boxy missile launcher on its flank), 2 the Sd.Kfz. 7/1 AAA
  // half-track (cab forward, quad-20 flak bed), 3 the MUTT gun truck (a
  // fast soft-skin with a pedestal recoilless rifle in the bed). Local +x is
  // forward, y up from the ground contact, z starboard. The turret + barrel
  // come back separate so they train live.
  landModels(type, t) {
    const trackCol = 0x191b1d;
    const wheelCol = 0x232527;
    const dark = 0x2b2f36;
    const gun = 0x24282c;
    let hull, turret, barrel;
    if (type === 0) {
      // Abrams MBT.
      const armor = 0x4f5647;
      const armorHi = 0x5a6252;
      hull = mergeBoxes([
        { x: 0, y: 1.35, z: 0, w: 7.8, h: 1.1, d: 3.2, color: armor }, // hull box
        { x: 3.6, y: 1.55, z: 0, w: 1.4, h: 0.7, d: 3.0, color: armorHi }, // glacis lip
        { x: 0, y: 1.95, z: 0, w: 6.6, h: 0.24, d: 3.0, color: armorHi }, // deck
        { x: -3.4, y: 1.6, z: 0, w: 1.2, h: 0.8, d: 2.8, color: 0x40463a }, // engine deck
        { x: -3.9, y: 1.4, z: 0, w: 0.25, h: 0.9, d: 2.2, color: 0x1f2321 }, // exhaust grille
        { x: 0, y: 0.95, z: 1.95, w: 7.9, h: 1.5, d: 0.55, color: trackCol }, // tracks
        { x: 0, y: 0.95, z: -1.95, w: 7.9, h: 1.5, d: 0.55, color: trackCol },
        { x: 0, y: 1.45, z: 1.95, w: 7.4, h: 0.5, d: 0.62, color: armor }, // side skirts
        { x: 0, y: 1.45, z: -1.95, w: 7.4, h: 0.5, d: 0.62, color: armor },
        { x: 0.4, y: 1.98, z: 0, w: 3.4, h: 0.3, d: 2.6, color: t.color }, // team band
      ]);
      turret = mergeBoxes([
        { x: 0.2, y: 0.55, z: 0, w: 3.9, h: 1.05, d: 3.2, color: armor }, // turret box
        { x: 1.9, y: 0.45, z: 0, w: 1.2, h: 0.8, d: 2.6, color: armorHi }, // sloped face
        { x: -1.6, y: 0.7, z: 0, w: 1.4, h: 0.9, d: 3.0, color: 0x454c3e }, // bustle rack
        { x: 0.4, y: 1.15, z: 0.85, w: 1.0, h: 0.35, d: 0.9, color: dark }, // commander sight
        { x: 0.9, y: 1.15, z: -0.7, w: 0.7, h: 0.3, d: 0.7, color: dark }, // gunner sight
        { x: -0.4, y: 1.25, z: -1.1, w: 0.5, h: 0.5, d: 0.5, color: gun }, // MG
      ]);
      barrel = mergeBoxes([
        { x: 3.4, y: 0.5, z: 0, w: 5.4, h: 0.36, d: 0.36, color: gun },
        { x: 4.2, y: 0.5, z: 0, w: 1.1, h: 0.5, d: 0.5, color: dark }, // bore evacuator
        { x: 6.3, y: 0.5, z: 0, w: 0.5, h: 0.46, d: 0.46, color: 0x14161a }, // muzzle
      ]);
    } else if (type === 1) {
      // Bradley IFV.
      const armor = 0x55584a;
      const armorHi = 0x62655a;
      hull = mergeBoxes([
        { x: 0, y: 1.5, z: 0, w: 6.4, h: 1.5, d: 3.1, color: armor },
        { x: 2.7, y: 1.7, z: 0, w: 1.4, h: 1.1, d: 2.9, color: armorHi }, // sloped bow
        { x: 0, y: 2.3, z: 0, w: 5.6, h: 0.25, d: 2.9, color: armorHi }, // deck
        { x: -2.9, y: 2.05, z: 0, w: 0.7, h: 0.7, d: 2.6, color: 0x3c4034 }, // rear ramp
        { x: 0, y: 0.9, z: 1.9, w: 6.5, h: 1.4, d: 0.5, color: trackCol },
        { x: 0, y: 0.9, z: -1.9, w: 6.5, h: 1.4, d: 0.5, color: trackCol },
        { x: 0, y: 1.55, z: 1.9, w: 6.0, h: 0.45, d: 0.56, color: armor }, // skirts
        { x: 0, y: 1.55, z: -1.9, w: 6.0, h: 0.45, d: 0.56, color: armor },
        { x: -0.6, y: 2.36, z: 0, w: 2.8, h: 0.26, d: 2.6, color: t.color }, // team band
      ]);
      turret = mergeBoxes([
        { x: 0.3, y: 0.5, z: -0.3, w: 2.2, h: 0.95, d: 1.8, color: armor }, // turret
        { x: 1.2, y: 0.42, z: -0.3, w: 0.7, h: 0.7, d: 1.5, color: armorHi }, // face
        { x: 0.2, y: 1.05, z: -0.6, w: 0.7, h: 0.35, d: 0.7, color: dark }, // sight
        // The boxy twin-tube missile launcher on the left flank.
        { x: -0.3, y: 0.75, z: 1.15, w: 1.7, h: 0.75, d: 0.85, color: 0x3e4238 },
        { x: 0.6, y: 0.75, z: 1.15, w: 0.2, h: 0.6, d: 0.7, color: 0x14161a }, // tube mouths
      ]);
      barrel = mergeBoxes([
        { x: 1.6, y: 0.45, z: -0.3, w: 2.3, h: 0.22, d: 0.22, color: gun },
        { x: 2.7, y: 0.45, z: -0.3, w: 0.35, h: 0.3, d: 0.3, color: 0x14161a },
      ]);
    } else if (type === 2) {
      // Sd.Kfz. 7/1 AAA half-track.
      const body = 0x4e5340;
      hull = mergeBoxes([
        { x: 2.2, y: 1.25, z: 0, w: 2.6, h: 1.1, d: 2.6, color: body }, // engine hood
        { x: 1.1, y: 1.9, z: 0, w: 1.3, h: 1.3, d: 2.5, color: 0x596049 }, // cab
        { x: 1.55, y: 2.1, z: 0, w: 0.25, h: 0.7, d: 2.0, color: 0x9fd8f0 }, // windshield
        { x: -1.3, y: 1.15, z: 0, w: 4.2, h: 0.9, d: 2.9, color: 0x434936 }, // flak bed
        { x: -1.3, y: 1.68, z: 1.35, w: 4.2, h: 0.5, d: 0.2, color: body }, // bed walls
        { x: -1.3, y: 1.68, z: -1.35, w: 4.2, h: 0.5, d: 0.2, color: body },
        { x: -3.3, y: 1.68, z: 0, w: 0.2, h: 0.5, d: 2.9, color: body },
        { x: -1.3, y: 1.95, z: 0, w: 4.3, h: 0.14, d: 3.0, color: t.color }, // team rim
        { x: 2.6, y: 0.75, z: 1.35, w: 1.2, h: 1.2, d: 0.5, color: wheelCol }, // front wheels
        { x: 2.6, y: 0.75, z: -1.35, w: 1.2, h: 1.2, d: 0.5, color: wheelCol },
        { x: -1.4, y: 0.8, z: 1.45, w: 3.8, h: 1.0, d: 0.5, color: trackCol }, // rear tracks
        { x: -1.4, y: 0.8, z: -1.45, w: 3.8, h: 1.0, d: 0.5, color: trackCol },
      ]);
      turret = mergeBoxes([
        { x: 0, y: 0.4, z: 0, w: 1.5, h: 0.8, d: 1.5, color: 0x565e44 }, // mount cradle
        { x: -0.7, y: 0.75, z: 0, w: 0.6, h: 0.9, d: 1.0, color: dark }, // gunner seat/shield
      ]);
      barrel = mergeBoxes([
        { x: 1.3, y: 0.3, z: 0.35, w: 3.0, h: 0.17, d: 0.17, color: gun },
        { x: 1.3, y: 0.3, z: -0.35, w: 3.0, h: 0.17, d: 0.17, color: gun },
        { x: 1.3, y: 0.7, z: 0.35, w: 3.0, h: 0.17, d: 0.17, color: gun },
        { x: 1.3, y: 0.7, z: -0.35, w: 3.0, h: 0.17, d: 0.17, color: gun },
        { x: 0.1, y: 0.5, z: 0, w: 0.9, h: 0.9, d: 1.1, color: dark }, // breech cluster
      ]);
    } else {
      // MUTT gun truck.
      const body = 0x6b5f46;
      hull = mergeBoxes([
        { x: 1.9, y: 1.15, z: 0, w: 2.0, h: 0.9, d: 2.3, color: body }, // hood
        { x: 0.7, y: 1.7, z: 0, w: 1.2, h: 1.2, d: 2.3, color: 0x776a4e }, // cab
        { x: 1.15, y: 1.9, z: 0, w: 0.22, h: 0.65, d: 1.9, color: 0x9fd8f0 }, // windshield
        { x: -1.5, y: 1.0, z: 0, w: 3.2, h: 0.6, d: 2.5, color: 0x5c5140 }, // bed
        { x: -1.5, y: 1.4, z: 1.15, w: 3.2, h: 0.35, d: 0.15, color: body }, // bed rails
        { x: -1.5, y: 1.4, z: -1.15, w: 3.2, h: 0.35, d: 0.15, color: body },
        { x: -0.2, y: 1.75, z: 0, w: 1.6, h: 0.16, d: 2.2, color: t.color }, // team stripe
        { x: 2.1, y: 0.6, z: 1.15, w: 1.05, h: 1.05, d: 0.45, color: wheelCol },
        { x: 2.1, y: 0.6, z: -1.15, w: 1.05, h: 1.05, d: 0.45, color: wheelCol },
        { x: -2.0, y: 0.6, z: 1.15, w: 1.05, h: 1.05, d: 0.45, color: wheelCol },
        { x: -2.0, y: 0.6, z: -1.15, w: 1.05, h: 1.05, d: 0.45, color: wheelCol },
        { x: -0.4, y: 0.85, z: 0, w: 0.5, h: 0.5, d: 2.3, color: wheelCol }, // axle hump
      ]);
      turret = mergeBoxes([
        { x: 0, y: 0.35, z: 0, w: 0.6, h: 0.9, d: 0.6, color: dark }, // pedestal
        { x: 0, y: 0.85, z: 0, w: 0.9, h: 0.35, d: 0.9, color: 0x4a4335 }, // cradle
      ]);
      barrel = mergeBoxes([
        { x: 1.7, y: 0.55, z: 0, w: 4.6, h: 0.3, d: 0.3, color: gun }, // long recoilless tube
        { x: 4.15, y: 0.55, z: 0, w: 0.45, h: 0.4, d: 0.4, color: 0x14161a }, // muzzle
        { x: -0.7, y: 0.55, z: 0, w: 0.8, h: 0.45, d: 0.45, color: dark }, // venturi
        { x: 0.9, y: 0.85, z: 0.1, w: 0.7, h: 0.25, d: 0.25, color: dark }, // spotting rifle
      ]);
    }
    return { hull, turret, barrel };
  }

  // shipModels builds a blocky warship hull for the naval theater, by box class
  // (shipArt): 0 a lean destroyer, 1 a cruiser, 2 a battleship (4, the Yamato,
  // sails under the same boxes), 3 the Des Moines heavy cruiser. Local
  // +x is the bow, y rises from the waterline (the root sits at sea level), z is
  // starboard. The rotating main-battery turret + barrel are returned separately
  // so the player can train them independently of the hull's heading. The hulls
  // are deliberately blocky but densely detailed — stepped sheer and a waterline
  // strake, deck-edge rails, secondary and AA mounts, funnel caps and bands,
  // masts with yards, and modeled secondary turrets — assembled from helpers so
  // the box count is high without the code repeating itself.
  shipModels(type, t) {
    const hullCol = 0x4a5159;
    const hullDark = 0x363b41; // lower hull / shadow strake
    const boot = 0x2b2e33; // boot-topping at the waterline
    const steelDeck = 0x6b7280; // steel weather deck
    const woodDeck = 0x8a7a55; // planked weather deck (IJN/RN/USN wood)
    const woodDark = 0x73643f; // plank seams
    const ssCol = 0x5a626b;
    const ssHi = 0x6a727c;
    const funnelCol = 0x33373c;
    const dark = 0x2a2d31;
    const steel = 0x545b63; // turrets and mounts
    const hullHi = 0x5b636d; // sheer-strake / roof highlight (catches light)
    const antifoul = 0x6f3b34; // dull red anti-fouling stripe at the waterline
    const railCol = 0x3c4147;
    const porthole = 0x20242a;

    // Hull half-beam at a given x (matches seg's taper), so deck fittings can be
    // kept inboard of the side instead of poking out over the water where the
    // hull narrows toward the bow and stern. HB is set per ship below; clampZ
    // pulls a side offset in so a box of beam-depth dz still sits on the hull.
    let HB = () => Infinity;
    const halfBeamFn = (beam, len, bowFrac, sternFrac) => (x) => {
      const f = (x + len / 2) / len, bs = 1 - bowFrac;
      let b = beam;
      if (f > bs) b = Math.max(beam * 0.16, beam * (1 - 0.86 * ((f - bs) / bowFrac)));
      else if (f < sternFrac) b = beam * (0.64 + 0.36 * (f / sternFrac));
      return b / 2;
    };
    const clampZ = (x, zH, dz) => Math.min(zH, Math.max(0.2, HB(x) - dz / 2 - 0.25));

    // --- parametric blocky detailing: loops push hundreds of greebles so each
    // hull reads like its real-life analogue without the code repeating itself.
    // A tapered, stepped hull — many short segments whose beam narrows toward the
    // bow and stern, each carrying a lower strake, a waterline boot-topping and a
    // deck plate (wood or steel), with a little sheer lifting the bow.
    const seg = (B, P) => {
      const { len, beam, fb, deckY, wood, bowFrac = 0.24, sternFrac = 0.16 } = P;
      // Fine segmentation (~every 1.7 m) so the tapered hull reads as a smooth
      // blocky curve rather than a few coarse steps.
      const half = len / 2, N = Math.max(24, Math.round(len / 1.7));
      const beamAt = (f) => {
        const bs = 1 - bowFrac;
        if (f > bs) return Math.max(beam * 0.16, beam * (1 - 0.86 * ((f - bs) / bowFrac)));
        if (f < sternFrac) return beam * (0.64 + 0.36 * (f / sternFrac));
        return beam;
      };
      const sheerAt = (f) => (f > 0.6 ? (f - 0.6) * 1.7 : 0) + (f < 0.1 ? (0.1 - f) * 1.0 : 0);
      const segW = len / N + 0.4;
      // fb is the full hull depth; deckY is the deck height above the waterline
      // (y=0), so the main hull box is placed with its top exactly at the deck.
      for (let i = 0; i < N; i++) {
        const xc = -half + (len * (i + 0.5)) / N, f = (xc + half) / len;
        const b = beamAt(f), dy = sheerAt(f), top = deckY + dy;
        B.push({ x: xc, y: deckY - fb / 2, z: 0, w: segW, h: fb, d: b, color: hullCol }); // hull side, top at deck
        B.push({ x: xc, y: top - 0.55, z: 0, w: segW, h: 0.4, d: b * 1.02, color: hullHi }); // sheer-strake highlight
        B.push({ x: xc, y: deckY - fb - 0.8, z: 0, w: segW, h: 2.0, d: b * 0.86, color: hullDark }); // keel (mostly submerged)
        B.push({ x: xc, y: 0, z: 0, w: segW, h: fb * 0.2, d: b * 1.04, color: boot }); // waterline boot-topping
        B.push({ x: xc, y: 0.55, z: 0, w: segW, h: 0.25, d: b * 1.05, color: antifoul }); // anti-fouling stripe
        // planked weather deck: alternating tones, crowned along the centreline
        B.push({ x: xc, y: top + 0.2, z: 0, w: segW, h: 0.4, d: b * 0.95, color: wood ? (i % 2 ? woodDeck : woodDark) : (i % 2 ? steelDeck : 0x626871) });
        B.push({ x: xc, y: top + 0.34, z: 0, w: segW, h: 0.22, d: b * 0.45, color: wood ? woodDeck : steelDeck }); // crown
      }
    };
    const portholes = (B, len, beam, y) => {
      const n = Math.floor((len - 12) / 3), zH = beam * 0.46;
      for (let i = 0; i < n; i++) {
        const x = -len / 2 + 6 + i * 3, f = (x + len / 2) / len;
        const taper = f > 0.78 ? 1 - (f - 0.78) / 0.22 : 1;
        if (taper < 0.4) continue;
        for (const z of [zH * taper, -zH * taper]) B.push({ x, y, z, w: 0.5, h: 0.5, d: 0.12, color: porthole });
      }
    };
    const railing = (B, x0, x1, zH, y) => {
      const n = Math.max(2, Math.round((x1 - x0) / 2.0));
      for (let i = 0; i <= n; i++) { const x = x0 + ((x1 - x0) * i) / n; for (const z of [zH, -zH]) B.push({ x, y: y + 0.5, z, w: 0.12, h: 1.0, d: 0.12, color: railCol }); }
      for (const yy of [0.95, 0.6]) for (const z of [zH, -zH]) B.push({ x: (x0 + x1) / 2, y: y + yy, z, w: x1 - x0, h: 0.1, d: 0.1, color: railCol }); // top + mid rail
    };
    const funnel = (B, x, y0, h, w, d, band) => {
      B.push({ x, y: y0 + h / 2, z: 0, w, h, d, color: funnelCol });
      B.push({ x, y: y0 + h - 0.1, z: 0, w: w + 0.5, h: 0.5, d: d + 0.5, color: dark }); // cap rim
      for (const dz of [-d * 0.26, 0, d * 0.26]) B.push({ x, y: y0 + h + 0.2, z: dz, w: w * 0.7, h: 0.18, d: 0.18, color: 0x15171b }); // cap grille slats
      if (band) B.push({ x, y: y0 + h * 0.62, z: 0, w: w + 0.3, h: 0.9, d: d + 0.3, color: t.color });
      for (const z of [1.2, -1.2]) B.push({ x: x - w * 0.55, y: y0 + h * 0.6, z, w: 0.3, h: h * 0.7, d: 0.3, color: dark }); // steam pipes
    };
    // Deck fittings and topside clutter that make the hull read busy and lived-in.
    const rafts = (B, x0, x1, zH, y, n) => { for (let i = 0; i < n; i++) { const x = x0 + ((x1 - x0) * (i + 0.5)) / n; const zc = clampZ(x, zH, 0.8); for (const z of [zc, -zc]) { B.push({ x, y: y + 0.4, z, w: 1.8, h: 0.4, d: 0.8, color: 0x9a9483 }); B.push({ x, y: y + 0.6, z, w: 1.2, h: 0.2, d: 0.4, color: dark }); } } }; // Carley floats
    const vents = (B, xs, zH, y) => { for (const x of xs) { const zc = clampZ(x, zH, 0.7); for (const z of [zc, -zc]) { B.push({ x, y: y + 0.6, z, w: 0.7, h: 1.2, d: 0.7, color: ssCol }); B.push({ x: x + 0.35, y: y + 1.25, z, w: 0.9, h: 0.5, d: 0.7, color: dark }); } } }; // cowl vents
    const radarBox = (B, x, y) => { for (let i = 0; i < 5; i++) B.push({ x, y: y + 0.4 + i * 0.42, z: 0, w: 0.25, h: 0.26, d: 4 - i * 0.25, color: dark }); }; // mattress array
    const winBand = (B, x, y, z, w, d) => B.push({ x, y, z, w, h: 0.5, d, color: 0x141821 }); // dark bridge windows
    const anchor = (B, x, zH, y) => { const zc = clampZ(x, zH, 0.25); for (const z of [zc, -zc]) { B.push({ x, y: y - 0.15, z, w: 1.0, h: 1.0, d: 0.25, color: dark }); B.push({ x: x + 0.7, y: y - 0.05, z: z * 0.92, w: 1.8, h: 0.18, d: 0.18, color: 0x3a3f45 }); } }; // anchor + chain
    const bollards = (B, xs, zH, y) => { for (const x of xs) { const zc = clampZ(x, zH, 1.1); for (const z of [zc, -zc]) B.push({ x, y: y + 0.35, z, w: 0.5, h: 0.7, d: 1.1, color: dark }); } };
    const searchlight = (B, x, y, z) => { B.push({ x, y: y + 0.3, z, w: 0.6, h: 0.6, d: 0.6, color: dark }); B.push({ x, y: y + 0.78, z, w: 0.95, h: 0.95, d: 0.95, color: 0xc2c8cd }); }; // platform + bright lens
    const mast = (B, x, y0, h) => {
      B.push({ x, y: y0 + h / 2, z: 0, w: 0.5, h, d: 0.5, color: dark }); // pole
      B.push({ x: x - 1.4, y: y0 + h * 0.32, z: 0, w: 0.34, h: h * 0.64, d: 0.34, color: dark }); // raked leg
      B.push({ x, y: y0 + h * 0.6, z: 0, w: 0.3, h: 0.3, d: h * 0.85, color: dark }); // yard
      B.push({ x, y: y0 + h + h * 0.16, z: 0, w: 0.28, h: h * 0.32, d: 0.28, color: dark }); // topmast
    };
    // A modeled (non-rotating) gun house: barbette, sloped-face turret, sight
    // hood and n barrels with muzzles, facing fore (dir +1) or aft (dir -1).
    const modTurret = (B, x, y, s, n, blen, dir) => {
      const w = 3.0 * s, h = 1.5 * s, d = 2.6 * s;
      B.push({ x, y: y - 0.5 * s, z: 0, w: w * 1.2, h: 1.0 * s, d: d * 1.2, color: steel }); // barbette
      B.push({ x, y, z: 0, w, h, d, color: steel }); // turret box
      B.push({ x: x + w * 0.3 * dir, y, z: 0, w: w * 0.5, h: h * 0.78, d: d * 0.9, color: 0x5e656e }); // sloped face
      B.push({ x, y: y + h * 0.5, z: 0, w: w * 0.85, h: 0.16 + 0.1 * s, d: d * 0.95, color: hullHi }); // roof highlight
      B.push({ x, y: y + h * 0.55, z: 0, w: 0.8 * s, h: 0.5 * s, d: d * 0.62, color: dark }); // sight hood
      for (const z of [d * 0.52, -d * 0.52]) B.push({ x: x - w * 0.12 * dir, y: y + h * 0.2, z, w: w * 0.5, h: 0.5 * s, d: 0.55 * s, color: dark }); // rangefinder ears
      const sp = n > 1 ? d * 0.3 : 0;
      for (let k = 0; k < n; k++) {
        const z = n > 1 ? -sp + (2 * sp * k) / (n - 1) : 0;
        B.push({ x: x + w * 0.35 * dir, y, z, w: w * 0.5, h: 0.85 * s, d: 0.85 * s, color: 0x4c525a }); // canvas blast bag
        B.push({ x: x + (blen * 0.5 + w * 0.3) * dir, y, z, w: blen, h: 0.55 * s, d: 0.55 * s, color: dark });
        B.push({ x: x + (blen + w * 0.3) * dir, y, z, w: 0.6, h: 0.75 * s, d: 0.75 * s, color: dark }); // muzzle
      }
    };
    // Rows of dual-purpose secondary mounts and light AA down both beams.
    const secRow = (B, xs, zH, y) => {
      for (const x of xs) { const zc = clampZ(x, zH, 2.0); for (const z of [zc, -zc]) {
        B.push({ x, y: y + 0.6, z, w: 2.0, h: 1.1, d: 2.0, color: steel });
        B.push({ x: x + 1.5, y: y + 0.85, z, w: 2.2, h: 0.32, d: 0.7, color: dark }); // twin DP barrels
      } }
    };
    const aaRow = (B, x0, x1, zH, y, n) => {
      for (let i = 0; i < n; i++) { const x = x0 + ((x1 - x0) * (i + 0.5)) / n; const zc = clampZ(x, zH, 1.1); for (const z of [zc, -zc]) {
        B.push({ x, y: y + 0.3, z, w: 1.1, h: 0.6, d: 1.1, color: ssCol });
        B.push({ x, y: y + 0.85, z, w: 0.4, h: 0.7, d: 0.4, color: dark });
      } }
    };
    const boats = (B, xs, zH, y) => { for (const x of xs) { const zc = clampZ(x, zH, 1.5); for (const z of [zc, -zc]) B.push({ x, y: y + 0.4, z, w: 4.5, h: 0.9, d: 1.5, color: 0x6a6f77 }); } };
    const seaplane = (B, x, y, z) => {
      B.push({ x, y: y + 0.5, z, w: 4, h: 0.6, d: 1.0, color: 0x7a818b }); // fuselage
      B.push({ x, y: y + 0.75, z, w: 1.0, h: 0.18, d: 6, color: 0x7a818b }); // wing
      B.push({ x: x - 1.9, y: y + 0.95, z, w: 0.7, h: 0.6, d: 2.2, color: 0x7a818b }); // tail
      B.push({ x, y: y - 0.05, z, w: 1.4, h: 0.5, d: 0.4, color: dark }); // float
    };
    const catapult = (B, x, y, z, len = 10) => B.push({ x, y: y + 0.4, z, w: len, h: 0.5, d: 1.2, color: steel });

    let hull, turret, barrel;
    if (type === 2 || type === 4) {
      // Battleship — a Yamato-class analogue: ~134 m of broad hull, a towering
      // pagoda superstructure, one great raked funnel, three triple main turrets
      // (two forward superfiring, one aft), and a forest of light AA.
      const B = [];
      seg(B, { len: 134, beam: 16, fb: 5.4, deckY: 3.4, wood: true, bowFrac: 0.22, sternFrac: 0.18 });
      HB = halfBeamFn(16, 134, 0.22, 0.18);
      B.push({ x: 50, y: 3.95, z: 0, w: 0.9, h: 1.0, d: 13, color: dark }); // breakwater
      // pagoda tower: stacked tiers climbing to the main rangefinder
      B.push({ x: 6, y: 5.6, z: 0, w: 24, h: 4.0, d: 13, color: ssCol }); // base block
      B.push({ x: 8, y: 8.0, z: 0, w: 16, h: 2.0, d: 11, color: ssCol }); // platform
      for (const z of [6.4, -6.4]) B.push({ x: 7, y: 6.4, z, w: 20, h: 2.4, d: 1.4, color: ssCol }); // bridge wings
      B.push({ x: 9, y: 10.4, z: 0, w: 11, h: 3.2, d: 8.5, color: ssHi }); // navigating bridge
      B.push({ x: 9, y: 13.0, z: 0, w: 8, h: 2.0, d: 6.5, color: ssHi }); // upper bridge
      B.push({ x: 10, y: 15.4, z: 0, w: 5.5, h: 2.4, d: 5.0, color: ssCol }); // fire-control top
      B.push({ x: 10, y: 17.6, z: 0, w: 4.6, h: 2.2, d: 4.6, color: steel }); // main director
      B.push({ x: 10, y: 19.6, z: 0, w: 6.5, h: 1.6, d: 2.2, color: dark }); // 15 m rangefinder
      B.push({ x: 11, y: 21.6, z: 0, w: 0.6, h: 4, d: 0.6, color: dark }); // mast spike
      funnel(B, -12, 5.0, 7.0, 7.5, 9, true); // single trunked funnel, raked
      // aft superstructure + mainmast + aft director
      B.push({ x: -34, y: 5.4, z: 0, w: 12, h: 3.2, d: 9, color: ssCol });
      B.push({ x: -34, y: 7.4, z: 0, w: 5.5, h: 1.8, d: 5, color: steel });
      mast(B, -38, 7.0, 8);
      // Main battery A/B/C are LIVE trainable mounts (see build): only their
      // barbettes are baked into the hull — B's climbs to its superfiring height.
      B.push({ x: 28, y: 5.4, z: 0, w: 10, h: 4.2, d: 10, color: steel });
      // secondary triple 6.1in turrets fore and aft of the tower
      modTurret(B, 17, 4.4, 1.2, 3, 8, 1);
      modTurret(B, -28, 4.2, 1.2, 3, 8, -1);
      // twin 5in DP secondaries and a forest of 25 mm AA down both beams
      secRow(B, [2, -10, -20], 6.2, 3.6);
      aaRow(B, 0, 24, 6.6, 5.0, 5);
      aaRow(B, -26, 2, 7.0, 6.2, 4);
      aaRow(B, 22, 44, 7.6, 3.7, 4);
      aaRow(B, -50, -32, 7.6, 3.7, 3);
      // aircraft handling aft: catapults and floatplanes (aft of the live C
      // turret at x -46, clear of its training circle)
      catapult(B, -58, 3.6, 4.5, 12);
      catapult(B, -58, 3.6, -4.5, 12);
      seaplane(B, -56, 3.9, 4.5);
      seaplane(B, -61, 3.9, -4.5);
      boats(B, [-2, -22], 7.8, 4.0);
      anchor(B, 60, 5.5, 3.4); // anchors + chain on the forecastle
      bollards(B, [56, -56], 6.5, 3.6);
      vents(B, [-2, -18], 5, 4.0); // cowl ventilators amidships
      rafts(B, -6, -30, 7.6, 3.6, 4); // Carley floats along the rails
      searchlight(B, -16, 6.5, 4.5); searchlight(B, -16, 6.5, -4.5);
      radarBox(B, 11, 19.4); // air-search array atop the pagoda
      winBand(B, 9, 11.2, 4.3, 10, 0.3); winBand(B, 9, 11.2, -4.3, 10, 0.3); // bridge windows
      portholes(B, 134, 16, 1.4);
      railing(B, -62, 60, 7.9, 3.6);
      hull = mergeBoxes(B);
      // Trainable forward main turret: a massive triple mount.
      turret = mergeBoxes([
        { x: 0, y: 1.3, z: 0, w: 11, h: 3.2, d: 11, color: steel },
        { x: 3.6, y: 1.3, z: 0, w: 4.6, h: 2.4, d: 10, color: 0x5e656e }, // sloped face
        { x: 0, y: 3.1, z: 0, w: 2.6, h: 0.7, d: 7, color: dark }, // sight hood
        { x: -3.0, y: 3.0, z: 0, w: 3.4, h: 0.9, d: 3.4, color: dark }, // rangefinder
      ]);
      barrel = mergeBoxes([
        { x: 10, y: 1.4, z: 2.6, w: 18, h: 0.95, d: 0.95, color: dark },
        { x: 10, y: 1.4, z: 0, w: 18, h: 0.95, d: 0.95, color: dark },
        { x: 10, y: 1.4, z: -2.6, w: 18, h: 0.95, d: 0.95, color: dark },
        { x: 19, y: 1.4, z: 2.6, w: 0.9, h: 1.2, d: 1.2, color: dark },
        { x: 19, y: 1.4, z: 0, w: 0.9, h: 1.2, d: 1.2, color: dark },
        { x: 19, y: 1.4, z: -2.6, w: 0.9, h: 1.2, d: 1.2, color: dark },
      ]);
    } else if (type === 1) {
      // Cruiser — a County-class analogue: a long, high-freeboard hull, three
      // tall funnels, twin 8in turrets fore and aft, and an amidships catapult.
      const B = [];
      seg(B, { len: 96, beam: 11, fb: 4.8, deckY: 2.9, wood: true, bowFrac: 0.26, sternFrac: 0.16 });
      HB = halfBeamFn(11, 96, 0.26, 0.16);
      B.push({ x: 33, y: 3.25, z: 0, w: 0.8, h: 0.8, d: 8.6, color: dark }); // breakwater
      // tall bridge block + director tower
      B.push({ x: 9, y: 4.4, z: 0, w: 14, h: 3.0, d: 9, color: ssCol });
      for (const z of [4.7, -4.7]) B.push({ x: 9, y: 4.7, z, w: 11, h: 1.8, d: 1.0, color: ssCol }); // wings
      B.push({ x: 11, y: 6.6, z: 0, w: 7, h: 2.4, d: 6, color: ssHi }); // bridge
      B.push({ x: 12, y: 8.8, z: 0, w: 4.2, h: 1.8, d: 4, color: steel }); // director
      B.push({ x: 12, y: 10.2, z: 0, w: 3, h: 1.2, d: 1.8, color: dark }); // rangefinder
      // three tall thin funnels (the County signature)
      funnel(B, 0, 3.9, 5.4, 3.8, 5.0, true);
      funnel(B, -8, 3.9, 5.6, 3.8, 5.0, false);
      funnel(B, -16, 3.9, 5.2, 3.6, 4.8, false);
      B.push({ x: -30, y: 3.4, z: 0, w: 14, h: 1.4, d: 8, color: ssCol }); // aft deckhouse
      // Main battery A (forecastle) and X (aft deckhouse) are live trainable
      // mounts (see build); the hull carries no baked-in gun houses.
      mast(B, 13, 7.2, 6);
      mast(B, -22, 4.6, 5);
      // amidships aircraft: athwartships catapult, floatplane, and crane
      catapult(B, -2, 3.0, 0, 8);
      seaplane(B, -2, 3.4, 0);
      B.push({ x: -6, y: 4.6, z: 0, w: 0.6, h: 3.5, d: 0.6, color: dark }); // crane post
      B.push({ x: -3, y: 6.0, z: 0, w: 7, h: 0.5, d: 0.5, color: dark }); // crane jib
      secRow(B, [20, -10, -20], 5.0, 2.9); // 4in HA secondaries
      aaRow(B, 4, 18, 5.2, 4.2, 3);
      aaRow(B, -24, -10, 5.0, 3.4, 3);
      boats(B, [-4], 5.0, 3.0);
      anchor(B, 44, 4.0, 2.9);
      bollards(B, [40, -40], 4.8, 3.0);
      vents(B, [-26, -34], 4, 3.4);
      rafts(B, -26, -40, 4.8, 3.0, 3);
      searchlight(B, -8, 5.0, 4.0); searchlight(B, -8, 5.0, -4.0);
      radarBox(B, 13, 11.0); // air-search array on the foremast
      winBand(B, 11, 6.9, 3.0, 6, 0.3); winBand(B, 11, 6.9, -3.0, 6, 0.3);
      portholes(B, 96, 11, 1.2);
      railing(B, -44, 44, 5.4, 2.9);
      hull = mergeBoxes(B);
      turret = mergeBoxes([
        { x: 0, y: 1.0, z: 0, w: 7, h: 2.1, d: 6.4, color: steel },
        { x: 2.4, y: 1.0, z: 0, w: 2.8, h: 1.6, d: 5.8, color: 0x5e656e }, // sloped face
        { x: -1.6, y: 2.2, z: 0, w: 2.2, h: 0.7, d: 2.2, color: dark }, // rangefinder
      ]);
      barrel = mergeBoxes([
        { x: 6, y: 1.1, z: 1.4, w: 10, h: 0.65, d: 0.65, color: dark },
        { x: 6, y: 1.1, z: -1.4, w: 10, h: 0.65, d: 0.65, color: dark },
        { x: 11.1, y: 1.1, z: 1.4, w: 0.6, h: 0.9, d: 0.9, color: dark },
        { x: 11.1, y: 1.1, z: -1.4, w: 0.6, h: 0.9, d: 0.9, color: dark },
      ]);
    } else if (type === 3) {
      // Des Moines: a long flush-deck heavy cruiser with
      // three triple autoloading 8in turrets (two forward superfiring, one aft),
      // a boxy tower superstructure, two funnels, and twin 5in/38 secondaries.
      const B = [];
      seg(B, { len: 104, beam: 11.5, fb: 4.2, deckY: 2.7, wood: true, bowFrac: 0.24, sternFrac: 0.15 });
      HB = halfBeamFn(11.5, 104, 0.24, 0.15);
      B.push({ x: 38, y: 3.05, z: 0, w: 0.8, h: 0.8, d: 9.0, color: dark }); // breakwater
      // boxy US tower superstructure with stacked directors
      B.push({ x: 3, y: 4.4, z: 0, w: 20, h: 3.0, d: 9, color: ssCol });
      for (const z of [4.9, -4.9]) B.push({ x: 3, y: 4.8, z, w: 14, h: 2.0, d: 1.0, color: ssCol }); // wings
      B.push({ x: 5, y: 6.6, z: 0, w: 9, h: 2.6, d: 6.5, color: ssHi }); // pilot house
      B.push({ x: 6, y: 8.8, z: 0, w: 5, h: 2.0, d: 4.5, color: steel }); // Mk37 director
      B.push({ x: 6, y: 10.4, z: 0, w: 2.2, h: 1.2, d: 3.2, color: dark }); // radar
      funnel(B, -8, 4.0, 5.0, 5.0, 5.2, true);
      funnel(B, -19, 3.9, 4.6, 4.6, 4.8, false);
      B.push({ x: -34, y: 3.4, z: 0, w: 12, h: 1.4, d: 7.5, color: ssCol }); // aft deckhouse
      B.push({ x: -34, y: 4.6, z: 0, w: 4.5, h: 1.4, d: 4, color: steel }); // aft director
      // Main battery A/B/X are live trainable mounts (see build): only B's
      // superfiring barbette is baked into the hull.
      B.push({ x: 20, y: 4.3, z: 0, w: 9, h: 3.4, d: 9, color: steel });
      mast(B, 8, 7.0, 6.5); // foremast
      B.push({ x: 8, y: 11.0, z: 0, w: 3.5, h: 0.4, d: 2.5, color: dark }); // air-search radar
      mast(B, -24, 4.8, 5);
      // twin 5in/38 DP secondaries and 3in/50 AA down both beams
      secRow(B, [26, 12, -12, -22], 5.0, 2.7);
      aaRow(B, 0, 18, 5.2, 4.2, 4);
      aaRow(B, -28, -6, 5.0, 3.4, 4);
      aaRow(B, 24, 36, 5.4, 2.8, 2);
      boats(B, [-2, -16], 5.0, 2.9);
      B.push({ x: -40, y: 4.4, z: 0, w: 0.6, h: 3.2, d: 0.6, color: dark }); // boat crane
      B.push({ x: -36, y: 5.8, z: 0, w: 7, h: 0.5, d: 0.5, color: dark });
      anchor(B, 48, 4.0, 2.7);
      bollards(B, [44, -42], 5.0, 2.8);
      vents(B, [-2, -26], 4, 3.2);
      rafts(B, -8, -28, 5.0, 2.8, 3);
      searchlight(B, -13, 5.2, 3.8); searchlight(B, -13, 5.2, -3.8);
      winBand(B, 5, 6.6, 3.3, 8, 0.3); winBand(B, 5, 6.6, -3.3, 8, 0.3);
      portholes(B, 104, 11.5, 1.2);
      railing(B, -48, 48, 5.5, 2.7);
      hull = mergeBoxes(B);
      // Trainable forward main turret: a triple autoloader mount.
      turret = mergeBoxes([
        { x: 0, y: 0.95, z: 0, w: 8.5, h: 2.2, d: 7.5, color: steel },
        { x: 2.6, y: 0.95, z: 0, w: 3.2, h: 1.7, d: 7.0, color: 0x5e656e }, // sloped face
        { x: 0, y: 2.2, z: 0, w: 1.6, h: 0.5, d: 5, color: dark }, // sight hood
        { x: -1.8, y: 2.3, z: 0, w: 2.2, h: 0.7, d: 2.2, color: dark }, // rangefinder
      ]);
      barrel = mergeBoxes([
        { x: 6, y: 1.05, z: 2.2, w: 12, h: 0.6, d: 0.6, color: dark },
        { x: 6, y: 1.05, z: 0, w: 12, h: 0.6, d: 0.6, color: dark },
        { x: 6, y: 1.05, z: -2.2, w: 12, h: 0.6, d: 0.6, color: dark },
        { x: 12.1, y: 1.05, z: 2.2, w: 0.6, h: 0.85, d: 0.85, color: dark },
        { x: 12.1, y: 1.05, z: 0, w: 0.6, h: 0.85, d: 0.85, color: dark },
        { x: 12.1, y: 1.05, z: -2.2, w: 0.6, h: 0.85, d: 0.85, color: dark },
      ]);
    } else {
      // Destroyer — a Fubuki/Kagero-class analogue: a lean low hull, two raked
      // funnels, single 5in gun houses fore and aft, and the heavy centreline
      // triple torpedo tube banks the IJN destroyers were built around.
      const B = [];
      seg(B, { len: 70, beam: 8.4, fb: 3.2, deckY: 2.1, wood: true, bowFrac: 0.26, sternFrac: 0.16 });
      HB = halfBeamFn(8.4, 70, 0.26, 0.16);
      B.push({ x: 15, y: 2.45, z: 0, w: 0.7, h: 0.7, d: 6.0, color: dark }); // breakwater
      // bridge stack forward
      B.push({ x: 6, y: 3.4, z: 0, w: 8, h: 2.0, d: 6.0, color: ssCol });
      for (const z of [3.2, -3.2]) B.push({ x: 6, y: 3.6, z, w: 6, h: 1.2, d: 0.9, color: ssCol }); // wings
      B.push({ x: 6.5, y: 4.9, z: 0, w: 4.5, h: 1.4, d: 4.2, color: ssHi }); // bridge top
      B.push({ x: 6.5, y: 6.0, z: 0, w: 2.4, h: 0.9, d: 2.2, color: steel }); // director
      mast(B, 9, 5.6, 4.5);
      funnel(B, -1, 2.8, 3.6, 3.2, 3.6, true);
      funnel(B, -11, 2.7, 3.2, 2.8, 3.4, false);
      // Both single 5in gun houses (A forward, X aft) are live trainable mounts
      // (see build); nothing baked into the hull.
      // centreline triple torpedo tube banks (the IJN signature)
      for (const tx of [-5, -17]) {
        B.push({ x: tx, y: 2.7, z: 0, w: 3.2, h: 1.0, d: 2.0, color: steel }); // mount
        for (const tz of [0.7, 0, -0.7]) B.push({ x: tx + 1.5, y: 3.1, z: tz, w: 5, h: 0.32, d: 0.32, color: dark }); // tubes
      }
      for (const z of [2.0, -2.0]) B.push({ x: -31, y: 2.4, z, w: 4, h: 0.6, d: 0.8, color: dark }); // depth-charge racks
      aaRow(B, -9, 1, 3.0, 2.5, 2);
      boats(B, [-20], 3.2, 2.4);
      anchor(B, 31, 3.0, 2.1);
      bollards(B, [28, -28], 3.4, 2.2);
      vents(B, [-22], 2.6, 2.5);
      rafts(B, -20, -28, 3.4, 2.2, 2);
      searchlight(B, -6, 4.2, 0);
      winBand(B, 6, 5.0, 2.8, 5, 0.3); winBand(B, 6, 5.0, -2.8, 5, 0.3);
      portholes(B, 70, 8.4, 1.0);
      railing(B, -33, 33, 3.9, 2.1);
      hull = mergeBoxes(B);
      // Trainable single 5in gun house — one barrel per mount, matching the
      // one round per turret the server actually fires (mainGun.turrets).
      turret = mergeBoxes([
        { x: 0, y: 0.7, z: 0, w: 4.6, h: 1.6, d: 4.2, color: steel },
        { x: 1.7, y: 0.7, z: 0, w: 2.0, h: 1.2, d: 3.8, color: 0x5e656e }, // sloped face
      ]);
      barrel = mergeBoxes([
        { x: 4.0, y: 0.8, z: 0, w: 7, h: 0.5, d: 0.5, color: dark },
        { x: 7.6, y: 0.8, z: 0, w: 0.6, h: 0.7, d: 0.7, color: dark },
      ]);
    }
    return { hull, turret, barrel };
  }

  build(type, team) {
    const m = this.models(type, team);
    const root = new THREE.Group();
    const shipV = this.shipType(type);
    // A warship floats on her own waterline (the root). A land hull's root is
    // the server's anchor, HULL_ANCHOR_M over the ground; its body drops the
    // model back onto the ground (track bottoms at y 0, the art contract) and
    // tilts to the slope under it (_tilt) — the smooth ground has slopes a
    // hull visibly sits on, where the old blocks were all flat tops.
    const body = shipV ? root : new THREE.Group();
    if (!shipV) {
      body.position.y = -HULL_ANCHOR_M;
      root.add(body);
    }
    const hull = new THREE.Mesh(m.hull, terrainMaterial);
    body.add(hull);
    // Weapon mounts ride atop the hull and aim in world space (their yaw is
    // driven from the snapshot / local aim, so they track independently of the
    // hull's heading). A warship gets one live mount PER MAIN-BATTERY TURRET in
    // its config layout (mainGun.turrets), each with its own train arc, so the
    // model shows exactly the guns the server will fire — the aft turret stows
    // fore-and-aft when the aim crosses its masked wedge. Land vehicles keep
    // their single turret.
    const landV = !shipV && this.landType(type);
    const li = this.landIdx(type);
    // The flash card's frame (m): a rifle's by the class's blast weight, a
    // tank gun's or a recoilless rifle's, an autocannon's.
    const art = shipV ? this.shipArt(type) : type;
    const flashSize = shipV
      ? (2.2 + 3.4 * (SHIP_BLAST_K[art] ?? 0.6)) * 2.2
      : landV
        ? (li === 0 || li === 3 ? 5.2 : 2.4)
        : 0;
    const mounts = [];
    const mkMount = (fwdM, y, tc) => {
      const grp = new THREE.Group();
      grp.position.set(fwdM, y, 0);
      grp.add(new THREE.Mesh(m.turret, terrainMaterial));
      // The barrel pivots in elevation within the turret.
      const barrel = new THREE.Group();
      grp.add(barrel);
      barrel.add(new THREE.Mesh(m.barrel, terrainMaterial));
      // A flash card on each tube's muzzle, lit while the snapshot's firing
      // window is open (the AA-mount idiom); a warship's blast and a tank's
      // report ride on top of it from the frame loop in main.js.
      let flash = null;
      if (shipV || landV) {
        flash = new THREE.Group();
        flash.userData.flashSize = flashSize;
        const tips = (shipV ? SHIP_MUZZLES[art] : LAND_MUZZLES[li]) ?? [];
        for (const mz of tips) flash.add(tubeFlash(flashSize, mz.x, mz.y, mz.z));
        flash.visible = false;
        barrel.add(flash);
      }
      body.add(grp);
      // yaw is the mount's CURRENT world train bearing (eased in update);
      // stowed it points down the keel — aft mounts over the stern.
      mounts.push({ grp, barrel, flash, fwdM, tc, yaw: tc?.aft ? Math.PI : 0, bears: true });
    };
    if (shipV) {
      const turrets = this.types?.[type]?.mainGun?.turrets ?? [];
      const ys = SHIP_MOUNT_Y[art] ?? [];
      const sy = m.stretch?.sy ?? 1; // the box deck rides the stretch
      if (turrets.length) {
        turrets.forEach((tc, i) => mkMount(tc.fwdM, (ys[i] ?? SHIP_TURRET_Y[art] ?? 2.9) * sy, tc));
      } else {
        const hl = this.types?.[type]?.hullHalfLenM ?? 32;
        mkMount(SHIP_TURRET_X[art] ?? hl * 0.6, (SHIP_TURRET_Y[art] ?? 2.9) * sy, null);
      }
    } else if (landV) {
      // The AAA bed and the truck pintle sit aft; turrets ride centered.
      const fwd = [0, 0, -1.3, -1.4][li] ?? 0;
      mkMount(fwd, LAND_TURRET_Y[li] ?? 1.8, null);
    } else {
      mkMount(0, VEHICLE_TURRET_Y[type] ?? 1.5, null);
    }
    this.scene.add(root);
    // turret/barrel/flash alias the lead mount for the single-mount call sites.
    const lead = mounts[0];
    const v = { root, body, hull, mounts, turret: lead.grp, barrel: lead.barrel, flash: lead.flash, type, team };
    if (shipV) this._upgradeShip(v);
    else this._upgrade(v, landV);
    return v;
  }

  // A warship's real-scale model (Part 5.4). The hull LOD rides the heading
  // group; each battery turret takes over its config mount — the box mount
  // stays on as the aim's anchor, moved onto the model's turret ring and
  // trunnions so the flash, the sight and the muzzle blast all leave from the
  // rifles you can see — and every other mount on the model trains along
  // with the battery (update → _aimShip).
  _upgradeShip(v) {
    const spec = navalModelOf(this.types?.[v.type], v.type);
    if (!spec) return;
    loadLevels(spec.stem, { lods: false }).then((tpls) => {
      if (!tpls || !v.root.parent) return; // gone before the model landed
      const { lod, levels } = lodOf(tpls, { team: v.team, dists: LOD_DIST.ship });
      const parts = warshipParts(levels, spec);
      if (!parts) return;
      const shift = spec.shift ?? 0;
      lod.position.x = shift;
      const hullGrp = new THREE.Group();
      hullGrp.rotation.copy(v.hull.rotation);
      v.body.remove(v.hull);
      v.body.add(hullGrp);
      hullGrp.add(lod);
      v.hull = hullGrp;
      const lead = parts[0];
      const tips = spec.tubes.map((z) => ({ x: spec.muzzle, y: 0, z }));
      v.mounts.forEach((mnt, i) => {
        const b = lead.battery[i];
        if (!b) return; // a config turret the model lacks keeps its box
        for (const c of [...mnt.grp.children]) if (c.isMesh) mnt.grp.remove(c);
        for (const c of [...mnt.barrel.children]) if (c.isMesh) mnt.barrel.remove(c);
        mnt.fwdM = b.node.position.x + shift;
        mnt.grp.position.y = b.node.position.y;
        if (b.gun) mnt.barrel.position.copy(b.gun.position);
        mnt.tips = tips;
        if (mnt.flash) {
          // The model's own rifles: a card on each.
          for (const f of [...mnt.flash.children]) mnt.flash.remove(f);
          for (const mz of tips) mnt.flash.add(tubeFlash(mnt.flash.userData.flashSize, mz.x, mz.y, mz.z));
        }
      });
      // The mimics' own train state (world bearings), seeded at their rest
      // facing so the first frame doesn't whip them round.
      const hullYaw = v.target?.yaw ?? v.hull.rotation.y;
      v.ship = {
        spec,
        parts,
        mimic: lead.mimic.map((m) => ({ yaw: hullYaw + m.rest, pitch: 0, rest: m.rest, side: m.side, gun: !!m.gun })),
      };
      v.stem = spec.stem;
    });
  }

  // Train a warship model's mounts for this frame: each battery turret and its
  // gun follow their box mount (the aim, or stowed when masked); each mimic
  // bears on the aim when its arc — or, for a beam mount, its rail — allows,
  // and swings back to its rest facing when it can't. Nodes live in the
  // hull's frame, so every bearing is taken less the heading.
  _aimShip(v, hullYaw, aimYaw, aimPitch, dt) {
    const sh = v.ship;
    if (!sh) return;
    const rel = aimYaw - hullYaw;
    const s = Math.sin(rel);
    const arc = (SHIP_TURRET_ARC_DEG * Math.PI) / 180;
    const step = SHIP_MOUNT_SLEW * dt;
    for (const m of sh.mimic) {
      let bears;
      if (m.side) bears = s * -m.side > 0.1; // its own rail (+z is starboard)
      else if (!m.gun) bears = Math.abs(s) > 0.3; // a torpedo mount: either beam
      else {
        const d = rel - m.rest;
        bears = Math.abs(Math.atan2(Math.sin(d), Math.cos(d))) <= arc;
      }
      const want = bears ? aimYaw : hullYaw + m.rest;
      const d = Math.atan2(Math.sin(want - m.yaw), Math.cos(want - m.yaw));
      m.yaw = wrapPi(m.yaw + (Math.abs(d) <= step ? d : Math.sign(d) * step));
      m.pitch += ((bears ? aimPitch : 0.02) - m.pitch) * Math.min(1, 6 * dt);
    }
    for (const lv of sh.parts) {
      lv.battery.forEach((b, i) => {
        const mnt = v.mounts[i];
        if (!mnt) return;
        b.node.rotation.set(0, wrapPi(mnt.yaw - hullYaw), 0);
        if (b.gun) b.gun.rotation.z = mnt.barrel.rotation.z;
      });
      lv.mimic.forEach((b, i) => {
        const m = sh.mimic[i];
        b.node.rotation.set(0, wrapPi(m.yaw - hullYaw), 0);
        if (b.gun) b.gun.rotation.z = m.pitch;
      });
    }
  }

  // The vehicle's model (Part 6b): its LOD rides a hull group that turns with
  // the heading, its turret and gun nodes train and elevate on the snapshot's
  // aim, and the box mount stays on as the aim's anchor — moved onto the
  // model's own turret ring and trunnion so the muzzle flash, the gunner's
  // sight and the muzzle blast all leave from the barrel you can see.
  _upgrade(v, landV) {
    const key = (landV ? LAND_VEHICLE_MODEL[this.landIdx(v.type)] : AIR_VEHICLE_MODEL[v.type]) ?? null;
    const spec = key ? VEHICLE_MODELS[key] : null;
    if (!spec) return;
    loadLevels(spec.stem).then((tpls) => {
      if (!tpls || !v.root.parent) return; // gone before the model landed
      const { lod, levels } = lodOf(tpls, { team: v.team, dists: LOD_DIST.vehicle });
      const parts = vehicleParts(levels, spec);
      const lead = parts[0];
      if (!lead.turret || !lead.gun) return;
      const hullGrp = new THREE.Group();
      hullGrp.rotation.copy(v.hull.rotation);
      v.body.remove(v.hull);
      v.body.add(hullGrp);
      hullGrp.add(lod);
      v.hull = hullGrp;
      const mnt = v.mounts[0];
      for (const c of [...mnt.grp.children]) if (c.isMesh) mnt.grp.remove(c);
      for (const c of [...mnt.barrel.children]) if (c.isMesh) mnt.barrel.remove(c);
      // The ring sits wherever the model puts it (a halftrack's flak bed is
      // 1.6 m aft); update() swings that offset round with the heading.
      mnt.fwdM = lead.turret.position.x;
      mnt.grp.position.y = lead.turret.position.y;
      mnt.barrel.position.copy(lead.gun.position);
      if (mnt.flash?.children.length) {
        // The model's own muzzles: a card on each (the recoilless rifle's
        // back-blast faces aft out of its venturi).
        for (const f of [...mnt.flash.children]) mnt.flash.remove(f);
        for (const m of spec.muzzles) mnt.flash.add(tubeFlash(mnt.flash.userData.flashSize, m[0], m[1], m[2]));
      }
      v.model = { spec, parts, lastX: v.root.position.x, lastZ: v.root.position.z, lastYaw: v.hull.rotation.y };
      v.stem = spec.stem;
    });
  }

  // Train a vehicle's model: the world bearing less the hull's heading (its
  // nodes live in the hull's frame), and the gun's elevation.
  _aimModel(v, yaw, pitch) {
    if (!v.model) return;
    const rel = yaw - v.hull.rotation.y;
    for (const lv of v.model.parts) {
      if (lv.turret) lv.turret.rotation.y = rel;
      if (lv.gun) lv.gun.rotation.z = pitch;
    }
  }

  // Run the treads by the ground the hull covered this frame: forward along
  // the heading, plus the scrub a turn puts on each side (a left turn runs
  // the right track further).
  _treads(v) {
    const m = v.model;
    if (!m?.spec.tracks) return;
    const yaw = v.hull.rotation.y;
    const dx = v.root.position.x - m.lastX, dz = v.root.position.z - m.lastZ;
    const d = dx * Math.cos(yaw) - dz * Math.sin(yaw);
    const dyaw = Math.atan2(Math.sin(yaw - m.lastYaw), Math.cos(yaw - m.lastYaw));
    m.lastX = v.root.position.x;
    m.lastZ = v.root.position.z;
    m.lastYaw = yaw;
    if (Math.abs(d) > 20) return; // a teleport (respawn), not driving
    const w = m.spec.tracks.halfW;
    scrollTreads(m.parts, m.spec, d - dyaw * w, d + dyaw * w);
  }

  /**
   * Leave a knocked-out vehicle's hulk where it died: the model's `_wreck`
   * (hull settled, turret knocked off its ring), burning for a while, then
   * gone. The server has already swept the vehicle from the snapshot.
   */
  leaveWreck(id) {
    const v = this.vehicles.get(id);
    if (!v?.stem || this.shipType(v.type)) return;
    const at = v.root.position.clone();
    at.y -= HULL_ANCHOR_M; // the hulk sits on the ground, not the server's anchor
    const yaw = v.hull.rotation.y;
    const tilt = v.body.quaternion.clone();
    wreckOf(v.stem, v.team).then((w) => {
      if (!w) return;
      w.root.position.copy(at);
      w.root.rotation.y = yaw;
      w.root.quaternion.premultiply(tilt); // settled on the slope it died on
      this.scene.add(w.root);
      this.hulks ??= [];
      this.hulks.push({ root: w.root, t: 0, fireAt: 0 });
      // Keep the battlefield's scrap bounded: the oldest hulk goes first.
      while (this.hulks.length > 24) this.scene.remove(this.hulks.shift().root);
    });
  }

  _stepHulks(dt) {
    if (!this.hulks) return;
    for (let i = this.hulks.length - 1; i >= 0; i--) {
      const h = this.hulks[i];
      h.t += dt;
      if (this.particles && h.t < 45 && (h.fireAt -= dt) <= 0 && h.root.visible) {
        h.fireAt = 0.6;
        const p = h.root.position;
        this.particles.fire?.(new THREE.Vector3(p.x + (Math.random() - 0.5) * 2, p.y + 1.6, p.z + (Math.random() - 0.5) * 2));
        this.particles.smoke?.(new THREE.Vector3(p.x, p.y + 2.8, p.z));
      }
      if (h.t > 150) {
        this.scene.remove(h.root);
        this.hulks.splice(i, 1);
      }
    }
  }

  /** Apply a snapshot's vehicle rows: [id, type, x, y, z, yaw, turYaw, turPitch, hp, team, firing, onfire, flooding, dc, aground, spotted, repairing]. */
  applySnapshot(rows, now) {
    for (const [id, type, x, y, z, yaw, turYaw, turPitch, hp, team, firing, onfire, flooding, dc, aground, spotted, repairing] of rows) {
      let v = this.vehicles.get(id);
      if (!v) {
        v = this.build(type, team);
        v.root.position.set(x, y, z);
        v.hist = [];
        v.target = { x, y, z, yaw, turYaw, turPitch };
        this.vehicles.set(id, v);
      }
      // The row joins the hull's history; update() draws her on the render
      // clock between the rows either side of it (hullpath.js), and v.target
      // is that drawn pose. The raw row stays as v.latest: the mount's
      // freshest word, which the driver's aim loop reconciles against.
      pushHullSample(v.hist, now, x, y, z, yaw, turYaw, turPitch);
      v.latest = { yaw, turYaw, turPitch };
      v.hp = hp;
      v.firing = firing === 1;
      v.onfire = !!onfire;
      v.flooding = !!flooding;
      v.dc = !!dc;
      v.aground = !!aground;
      v.spotted = !!spotted;
      v.repairing = !!repairing;
      v.seen = now;
    }
    for (const [id, v] of this.vehicles) {
      if (now - (v.seen ?? 0) > 1.5) {
        this.scene.remove(v.root);
        disposeTreads(v.model?.parts);
        this.vehicles.delete(id);
      }
    }
  }

  /** Drop every hull (a death replay's renderer, done with its clip). */
  clear() {
    for (const v of this.vehicles.values()) {
      this.scene.remove(v.root);
      disposeTreads(v.model?.parts);
    }
    this.vehicles.clear();
  }

  /** Hide the hulls within `r` m of `at` (a replay hands the spot to the live hulk). */
  hideNear(at, r) {
    for (const v of this.vehicles.values()) {
      const p = v.root.position;
      if ((p.x - at.x) ** 2 + (p.z - at.z) ** 2 < r * r) v.root.visible = false;
    }
  }

  /** A warship hull went down (vehkill): keep the model and ride her under.
   * The server sweeps the dead hull from the snapshot the next tick, so from
   * here the founder is the client's — the hull settles by the stern, takes a
   * growing heel, and slips below over HULL_SINK_SEC, the same death-tells-a-
   * story treatment the anchored armada gets in Ships.beginSink. Land vehicles
   * return false and keep their explosion: tanks burn, they don't founder. */
  beginSink(id) {
    const v = this.vehicles.get(id);
    if (!v || !this.shipType(v.type)) return false;
    this.vehicles.delete(id);
    // The mounts train in world space while she fights; freeze them into the
    // hull's frame so the guns heel over with the deck instead of hovering
    // level over a tilting ship.
    for (const mnt of v.mounts) {
      if (mnt.flash) mnt.flash.visible = false;
      v.hull.attach(mnt.grp);
    }
    // YXZ: yaw first, then heel about the keel (.x) and trim about the beam
    // (.z) in the SHIP'S frame — the default XYZ order would roll about world
    // axes, which only looks like a heel on an east-west heading.
    v.hull.rotation.order = "YXZ";
    this.wrecks.set(id, {
      root: v.root,
      hull: v.hull,
      yaw: v.target?.yaw ?? v.hull.rotation.y,
      y0: v.target?.y ?? v.root.position.y,
      len: (this.types?.[v.type]?.hullHalfLenM ?? 30) * 2, // for where the sea boils
      // Deep enough to take the masthead under with her: a real-scale Iowa
      // stands 44 m, and the stern-down trim lifts her bow 20 m more.
      depth: Math.max(HULL_SINK_DEPTH_M, (navalModelOf(this.types?.[v.type], v.type)?.top ?? 0) + 6),
      t: 0,
      boilAt: 0.3, // next steam gout (the first comes almost at once)
      heelDir: Math.random() < 0.5 ? 1 : -1, // which rail goes under first
    });
    return true;
  }

  /** Pose every hull at `renderT`, the remote-interpolation clock planes
   * are drawn on (snapshot time, INTERP_DELAY behind the newest row); 0 —
   * no clock yet — draws the newest row. */
  update(dt, renderT = 0) {
    for (const [id, v] of this.vehicles) {
      if (!v.target) continue;
      if (v.hist.length) hullPoseAt(v.hist, renderT > 0 ? renderT : v.hist[v.hist.length - 1].t, v.target);
      v.root.position.set(v.target.x, v.target.y, v.target.z);
      v.hull.rotation.y = v.target.yaw;
      if (!this.shipType(v.type)) {
        // Ground vehicle: single mount. The local driver's mount follows
        // their predicted aim (setLocalAim), not the lagged snapshot;
        // reconciliation happens in main.js. The aim is a WORLD bearing and
        // elevation; _setMountAim lays it inside the tilted body, so the gun
        // you see points where the server's gun fires.
        this._tilt(v, dt);
        if (id === this.localAimId && v.localAim) this._setMountAim(v, v.localAim.yaw, v.localAim.pitch);
        else this._setMountAim(v, v.target.turYaw, v.target.turPitch);
        this._treads(v);
        // An offset mount (the AAA bed, the truck pintle, a model's turret
        // ring off the hull centre) rides the hull: rotate its keel offset by
        // the heading (the root group itself never rotates — only the hull).
        const mnt = v.mounts[0];
        if (mnt && mnt.fwdM) {
          mnt.grp.position.x = mnt.fwdM * Math.cos(v.target.yaw);
          mnt.grp.position.z = -mnt.fwdM * Math.sin(v.target.yaw);
        }
        // Light the muzzle flash while the server's firing window is open
        // (main guns get a hot box like a warship's).
        if (this.landType(v.type) && mnt?.flash) mnt.flash.visible = !!v.firing && Math.random() > 0.4;
        continue;
      }
      // Warship: every main-battery mount rides the hull (its keel offset
      // rotated into world by the heading) and trains on the battery aim when
      // its arc allows — a mount the aim has crossed out of swings back to its
      // stowed fore-and-aft line, exactly the guns the server will not fire.
      const local = id === this.localAimId;
      const aimYaw = local ? (v.localYaw ?? v.target.turYaw) : v.target.turYaw;
      const aimPitch = local ? (v.localPitch ?? v.target.turPitch) : v.target.turPitch;
      const hullYaw = v.target.yaw;
      // The salvo (the firing window's rising edge) is the one instant a
      // turret must be exactly where its shells go. The server fires every
      // turret the moment the aim crosses into its arc, but the drawn one is
      // still swinging round from stowed at SHIP_MOUNT_SLEW — about one
      // turret-shot in twenty-five left a barrel pointing tens of degrees off
      // its own fall of shot. A turret that fires is laid on the aim, whatever
      // it was doing.
      const salvo = !!v.firing && !v.salvoLaid;
      v.salvoLaid = !!v.firing;
      // Each bearing turret converges on the battery's ranged point from its
      // own ring, the way the server lays it (navalLayAim) — not parallel on
      // the battery's train, which on a long hull put the forward and after
      // turrets' barrels a couple of degrees either side of their own fall of
      // shot. Only the elevation rides the wire, so the range is read back
      // off it (batteryLayRange); the lay falls at the sea, as a shore mark's
      // height isn't sent.
      const mg = this.types?.[v.type]?.mainGun;
      const v0 = mg?.muzzleSpeedMS ?? 0;
      const layR = batteryLayRange(aimPitch, v0);
      const pivot = { x: v.root.position.x, z: v.root.position.z, yaw: hullYaw };
      for (const mnt of v.mounts) {
        mnt.grp.position.x = mnt.fwdM * Math.cos(hullYaw);
        mnt.grp.position.z = -mnt.fwdM * Math.sin(hullYaw);
        mnt.bears = !mnt.tc || shipTurretBears(mnt.tc, hullYaw, aimYaw);
        let lay = null;
        if (mnt.bears && mnt.tc && layR > 0) {
          const { mountY, barrel } = navalMountOf(v.type, mnt.tc, mg);
          lay = navalTurretLay(pivot, mnt.fwdM, mountY, barrel, aimYaw, layR, 0, v0);
        }
        mnt.lay = lay; // this turret's own lay, for the e2e's gun check (null = the battery's)
        const want = mnt.bears ? (lay?.yaw ?? aimYaw) : hullYaw + (mnt.tc?.aft ? Math.PI : 0);
        const d = Math.atan2(Math.sin(want - mnt.yaw), Math.cos(want - mnt.yaw));
        const step = salvo && mnt.bears ? Infinity : SHIP_MOUNT_SLEW * dt;
        mnt.yaw += Math.abs(d) <= step ? d : Math.sign(d) * step;
        mnt.grp.rotation.y = mnt.yaw;
        const wantPitch = mnt.bears ? (lay?.pitch ?? aimPitch) : 0.02;
        const ease = salvo && mnt.bears ? 1 : Math.min(1, 6 * dt);
        mnt.barrel.rotation.z += (wantPitch - mnt.barrel.rotation.z) * ease;
        // Muzzle flash only on the mounts that actually fired (the ones bearing).
        if (mnt.flash) mnt.flash.visible = !!v.firing && mnt.bears && Math.random() > 0.5;
      }
      this._aimShip(v, hullYaw, aimYaw, aimPitch, dt);
    }
    this._stepWrecks(dt);
    this._stepHulks(dt);
  }

  // The founder, played out: she settles slowly at first (the killcam's chase
  // phase is still replaying the fatal salvo), then the slide accelerates —
  // down by the stern, heeling onto one rail — and the sea boils white along
  // the waterline until the masts go under. Then the wreck leaves the scene;
  // the oil slick the server painted is what remains.
  _stepWrecks(dt) {
    for (const [id, wk] of this.wrecks) {
      wk.t += dt;
      const p = Math.min(1, wk.t / HULL_SINK_SEC);
      wk.root.position.y = wk.y0 - Math.pow(p, 1.6) * wk.depth;
      // The heel and the stern-down trim build over the first two-thirds and
      // then hold — past that she's just sliding, attitude set.
      const set = Math.min(1, wk.t / (HULL_SINK_SEC * 0.65));
      wk.hull.rotation.y = wk.yaw;
      wk.hull.rotation.x = wk.heelDir * set * 0.45; // onto one rail
      wk.hull.rotation.z = set * 0.16; // down by the stern
      if (this.particles && wk.t >= wk.boilAt && p < 0.85) {
        wk.boilAt = wk.t + 0.5 + Math.random() * 0.4;
        // A gout of steam and spray somewhere along the waterline, as big as
        // the hull boiling it (a real-scale Iowa's twice a destroyer's).
        const along = (Math.random() - 0.5) * wk.len * 0.8;
        const c = Math.cos(wk.yaw), sn = Math.sin(wk.yaw);
        this.particles.steam(
          new THREE.Vector3(wk.root.position.x + along * c, wk.y0 + 0.5, wk.root.position.z - along * sn),
          THREE.MathUtils.clamp(wk.len / 120, 0.6, 2),
        );
      }
      if (p >= 1) {
        this.scene.remove(wk.root);
        this.wrecks.delete(id);
      }
    }
  }

  has(id) {
    return this.vehicles.has(id);
  }

  typeOf(id) {
    return this.vehicles.get(id)?.type ?? 0;
  }

  /** Drive the local driver's mount from their predicted aim. Warship mounts
   * consume it in update() (each turret trains only when its arc allows); a
   * land vehicle's single turret takes it directly. */
  setLocalAim(id, yaw, pitch) {
    this.localAimId = id;
    const v = this.vehicles.get(id);
    if (!v) return;
    if (this.shipType(v.type)) {
      v.localYaw = yaw;
      v.localPitch = pitch;
      return;
    }
    v.localAim = { yaw, pitch };
    this._setMountAim(v, yaw, pitch);
  }

  /**
   * Lay a land hull's mount on a WORLD aim (bearing, elevation): turned into
   * the tilted body's own frame, so on a slope the turret trains and the gun
   * elevates relative to the hull exactly as far as it takes to point where
   * the server's (level) mount fires — the stabiliser, drawn.
   */
  _setMountAim(v, yaw, pitch) {
    const cp = Math.cos(pitch);
    const d = _aimDir.set(cp * Math.cos(yaw), Math.sin(pitch), -cp * Math.sin(yaw));
    if (v.body !== v.root) d.applyQuaternion(_aimQ.copy(v.body.quaternion).invert());
    const ly = Math.atan2(-d.z, d.x);
    const lp = Math.asin(Math.max(-1, Math.min(1, d.y)));
    v.turret.rotation.y = ly;
    v.barrel.rotation.z = lp;
    this._aimModel(v, ly, lp);
  }

  /**
   * Settle a land hull onto the ground under it: pitch from the ground at
   * bow and stern, roll from port and starboard (the hull's own half-length
   * and beam), eased so a hull rocks over a crest rather than snapping. A
   * sample far off the hull's own ground height (a bridge deck over water,
   * a hull half over a cliff edge) counts as level.
   */
  _tilt(v, dt) {
    const f = this.field;
    if (!f || v.body === v.root) return;
    const t = this.types?.[v.type];
    const L = t?.hullHalfLenM > 0 ? t.hullHalfLenM : 3.5;
    const W = t?.hullHalfBeamM > 0 ? t.hullHalfBeamM : 1.6;
    const yaw = v.target.yaw;
    const c = Math.cos(yaw), s = Math.sin(yaw);
    const x = v.target.x, z = v.target.z;
    const g0 = v.target.y - HULL_ANCHOR_M;
    const at = (px, pz) => {
      const h = f.surfaceAt(px, pz);
      return h - g0 > 2.5 || g0 - h > 2.5 ? g0 : h;
    };
    const pitch = Math.atan2(at(x + c * L, z - s * L) - at(x - c * L, z + s * L), 2 * L);
    const roll = Math.atan2(at(x - s * W, z - c * W) - at(x + s * W, z + c * W), 2 * W);
    const k = v.tiltSet ? 1 - Math.exp(-8 * dt) : 1;
    v.tiltSet = true;
    v.pitch = (v.pitch ?? 0) + (pitch - (v.pitch ?? 0)) * k;
    v.roll = (v.roll ?? 0) + (roll - (v.roll ?? 0)) * k;
    // Tilt about the hull's own axes: Ry(yaw)·Rz(pitch)·Rx(roll)·Ry(-yaw)
    // on the body, with the hull's heading still its own rotation.y inside.
    _tiltE.set(v.roll, yaw, v.pitch, "YZX");
    v.body.quaternion.setFromEuler(_tiltE).multiply(_tiltQ.setFromAxisAngle(_UP, -yaw));
  }

  clearLocalAim() {
    this.localAimId = 0;
  }

  /** The mount's bearing as drawn: the server's aim played back on the
   * render clock (a smooth curve, a beat behind srvAimOf's newest row). The
   * driver's sight leans on it at its lead limit — leaning on the newest
   * row instead, a sight swung faster than the turret traverses stepped
   * along twenty times a second, and the camera with it. */
  drawnAimOf(id) {
    const v = this.vehicles.get(id);
    return v?.target ? { yaw: v.target.turYaw, pitch: v.target.turPitch } : null;
  }

  /** Authoritative mount aim (reconciliation target while driving): the
   * newest row's, not the drawn one, so the aim loop answers to the mount
   * as it is now rather than INTERP_DELAY ago. */
  srvAimOf(id) {
    const v = this.vehicles.get(id);
    if (v?.latest) return { yaw: v.latest.turYaw, pitch: v.latest.turPitch };
    // First frames of a spawn, before the interpolation record lands: face
    // the hull's way rather than due east (yaw 0), so the fresh chase camera
    // opens behind the vehicle instead of swinging round to find it.
    return { yaw: v?.hull?.rotation.y ?? 0, pitch: 0 };
  }

  /**
   * World-space muzzle tips + bore direction of a warship's main battery, for
   * the muzzle-blast particles (null for land vehicles). Only the mounts that
   * bear on the aim contribute — they're the ones that fired. k is the class's
   * blast weight.
   */
  muzzlesOf(id) {
    const v = this.vehicles.get(id);
    const tips = v?.flash && this.shipType(v.type) ? SHIP_MUZZLES[this.shipArt(v.type)] : null;
    if (!tips) return null;
    const points = [];
    let dir = null;
    for (const mnt of v.mounts) {
      if (!mnt.bears) continue;
      dir ??= new THREE.Vector3(1, 0, 0)
        .applyQuaternion(mnt.barrel.getWorldQuaternion(new THREE.Quaternion()))
        .normalize();
      // A model turret's own rifles (_upgradeShip), else the box barrels.
      for (const m of mnt.tips ?? tips) points.push(mnt.barrel.localToWorld(new THREE.Vector3(m.x, m.y, m.z)));
    }
    if (!points.length) return null;
    return { points, dir, k: SHIP_BLAST_K[this.shipArt(v.type)] ?? 0.6 };
  }

  /**
   * World-space muzzle and bore of a land hull's main gun — the Abrams'
   * 120, the MUTT's recoilless rifle — for its report (flash, cordite and
   * the dust it kicks up); null for every other hull.
   */
  mainGunOf(id) {
    const v = this.vehicles.get(id);
    if (!v || this.shipType(v.type) || !this.landType(v.type)) return null;
    const li = this.landIdx(v.type);
    const mnt = v.mounts[0];
    if ((li !== 0 && li !== 3) || !mnt) return null;
    const tip = v.model?.spec.muzzles[0] ?? [LAND_MUZZLES[li][0].x, LAND_MUZZLES[li][0].y, LAND_MUZZLES[li][0].z];
    return {
      point: mnt.barrel.localToWorld(new THREE.Vector3(tip[0], tip[1], tip[2])),
      dir: new THREE.Vector3(1, 0, 0).applyQuaternion(mnt.barrel.getWorldQuaternion(new THREE.Quaternion())).normalize(),
    };
  }

  /** How much of a warship's battery bears on a world aim bearing:
   * {bearing, total} barrel counts — the client mirror of batteryBarrels() in
   * internal/game/navalcombat.go, for the gunsight's turret readout. */
  batteryOf(id, aimYaw) {
    const v = this.vehicles.get(id);
    if (!v) return null;
    const turrets = this.types?.[v.type]?.mainGun?.turrets ?? [];
    if (!turrets.length) return null;
    const hullYaw = v.target?.yaw ?? v.hull.rotation.y;
    let bearing = 0, total = 0;
    for (const tc of turrets) {
      const n = Math.max(1, tc.barrels ?? 1);
      total += n;
      if (shipTurretBears(tc, hullYaw, aimYaw)) bearing += n;
    }
    return { bearing, total };
  }

  /** A warship class's size, for framing a shot of her: {half, top} — her
   * config half-length and the model's masthead (null for a land hull). */
  hullSizeOfType(type) {
    if (!this.shipType(type)) return null;
    const t = this.types?.[type];
    return { half: t?.hullHalfLenM ?? 32, top: navalModelOf(t, type)?.top ?? 25 };
  }

  /** A crewed warship's camera geometry: the chase boom {back, up} from its
   * pivot, and the gunsight eye's height over the waterline (`eye`). */
  shipViewOf(id) {
    const v = this.vehicles.get(id);
    const t = v ? this.types?.[v.type] : null;
    const top = v ? navalModelOf(t, v.type)?.top ?? 0 : 0;
    return {
      back: SHIP_CHASE_BACK_M + (t?.hullHalfLenM ?? 57),
      up: SHIP_CHASE_UP_M + Math.max(0, top - 30),
      eye: Math.max(NAVAL_DIRECTOR_MIN_Y, top + 4),
    };
  }

  /** World position of the mount pivot (camera anchor while driving). */
  pivotOf(id) {
    const v = this.vehicles.get(id);
    if (!v) return new THREE.Vector3();
    // A warship anchors the chase camera up around its superstructure — in
    // combined arms too, where the theater isn't naval but the hull is.
    const up = this.shipType(v.type) ? 8 : this.land ? 2.4 : 2;
    return new THREE.Vector3(v.root.position.x, v.root.position.y + up, v.root.position.z);
  }

  positionOf(id) {
    // A foundering hull still answers — the killcam and late shell splats
    // aim at the wreck, not at nothing.
    return (this.vehicles.get(id) ?? this.wrecks.get(id))?.root.position ?? null;
  }

  /** Minimap blips: [{x, z, team}]. */
  blips() {
    const out = [];
    for (const v of this.vehicles.values()) {
      out.push({ x: v.root.position.x, z: v.root.position.z, team: v.team ?? 0 });
    }
    return out;
  }

  /** Plot-table blips: like blips(), plus the hull class so the gunner's
   * chart can ink a warship as a hull and a tank as a block. The server's
   * fog of war already decides who appears here — unspotted enemies are
   * never in the snapshot at all. */
  plotBlips() {
    const out = [];
    for (const v of this.vehicles.values()) {
      out.push({
        x: v.root.position.x,
        z: v.root.position.z,
        team: v.team ?? 0,
        ship: this.shipType(v.type),
      });
    }
    return out;
  }
}

/**
 * Searchlights: lattice pylons whose beams sweep the night sky, driven by
 * server snapshots (welcome carries the static pylon sites; snapshots carry
 * aim + on/locked flags in the same order). Beams ease toward the server
 * aim and burn brighter while locked onto a plane.
 */
export class Searchlights {
  constructor(scene) {
    this.scene = scene;
    this.lights = [];
  }

  build(sites, field = null) {
    for (const l of this.lights) this.scene.remove(l.root);
    this.lights = [];
    this.field = field;
    const beamGeo = new THREE.CylinderGeometry(30, 1.0, 1300, 10, 1, true);
    beamGeo.rotateZ(-Math.PI / 2); // beam points down local +X
    beamGeo.translate(650, 0, 0);
    for (const s of sites) {
      const t = TEAM_INFO[s.team] ?? TEAM_INFO[0];
      const root = new THREE.Group();
      root.position.set(s.x, s.y - 6, s.z);
      // A warship's light is the lamp its hull model already carries (the
      // Essex island's, the Iowa tower's): only the beam hangs there. Ashore,
      // the box pylon stands in until the searchlight model arrives.
      const pylon = s.ship
        ? null
        : new THREE.Mesh(
            mergeBoxes([
              { x: 0, y: 1, z: 0, w: 5, h: 2, d: 5, color: 0x6f7174 },
              { x: 0, y: 4, z: 0, w: 1.6, h: 5, d: 1.6, color: 0x4a4d52 },
              { x: 0, y: 6.6, z: 0, w: 2.4, h: 0.5, d: 2.4, color: t.color },
            ]),
            terrainMaterial,
          );
      if (pylon) root.add(pylon);
      const head = new THREE.Group();
      head.position.y = 6;
      root.add(head);
      const lamp = s.ship
        ? null
        : new THREE.Mesh(
            mergeBoxes([
              { x: 0, y: 0, z: 0, w: 2.6, h: 2.2, d: 2.2, color: 0x3a3e45 },
              { x: 1.4, y: 0, z: 0, w: 0.3, h: 1.9, d: 1.9, color: 0xfff7cc },
            ]),
            terrainMaterial,
          );
      if (lamp) head.add(lamp);
      const beam = new THREE.Mesh(
        beamGeo,
        new THREE.MeshBasicMaterial({
          color: 0xfff3c4,
          transparent: true,
          opacity: 0.1,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
          fog: false,
          side: THREE.DoubleSide,
        }),
      );
      beam.visible = false;
      head.add(beam);
      this.scene.add(root);
      const l = {
        root, head, beam, site: s, pylon, lamp, model: null,
        yaw: 0, pitch: 0.9, tYaw: 0, tPitch: 0.9, on: false, locked: false,
      };
      this.lights.push(l);
      if (!s.ship) this._upgrade(l);
    }
  }

  // The shore light's model: the sandbagged lamp (mount yaws, lamp pitches,
  // every LOD level following the beam) up on a steel tower, because the
  // server lights the beam from 6 m up — where the old pylon's head was.
  _upgrade(l) {
    const spec = EMPLACEMENTS.searchlight;
    emplacementOf(spec, l.site.team).then((m) => {
      if (!m || !this.lights.includes(l)) return;
      const base = 6 - spec.gunY; // the model's base, over the root (ground)
      m.lod.position.y = base;
      l.root.add(m.lod);
      l.root.remove(l.pylon);
      l.head.remove(l.lamp);
      l.model = m;
      // Legs down past the lowest ground under the footprint.
      const f = this.field;
      const { x, y, z } = l.root.position;
      let low = y;
      if (f) {
        for (const [dx, dz] of [[2.5, 2.5], [2.5, -2.5], [-2.5, 2.5], [-2.5, -2.5]]) {
          low = Math.min(low, f.surfaceAt(x + dx, z + dz));
        }
      }
      const tower = new THREE.Mesh(mergeBoxes(towerBoxes(low - 1.5 - (y + base), 2.4)), terrainMaterial);
      tower.position.y = base;
      l.root.add(tower);
    });
  }

  /** Apply a snapshot's light rows (welcome order): [yaw, pitch, on, locked]. */
  applySnapshot(rows) {
    for (let i = 0; i < rows.length && i < this.lights.length; i++) {
      const [yaw, pitch, on, locked] = rows[i];
      const l = this.lights[i];
      l.tYaw = yaw;
      l.tPitch = pitch;
      l.on = on === 1;
      l.locked = locked === 1;
    }
  }

  update(dt) {
    const wrap = (a) => Math.atan2(Math.sin(a), Math.cos(a));
    const k = 1 - Math.exp(-8 * dt);
    for (const l of this.lights) {
      l.yaw += wrap(l.tYaw - l.yaw) * k;
      l.pitch += (l.tPitch - l.pitch) * k;
      // Head yaw: world yaw 0 = +X, positive toward -Z → three.js rotation.y.
      l.head.rotation.y = l.yaw;
      l.beam.rotation.z = l.pitch;
      for (const lv of l.model?.levels ?? []) {
        lv.mount.rotation.y = l.yaw;
        lv.gun.rotation.z = l.pitch;
      }
      l.beam.visible = l.on;
      l.beam.material.opacity = l.locked ? 0.22 : 0.1;
    }
  }

  /** Is the given world position inside a locked enemy beam? */
  conedAt(pos, team) {
    for (const l of this.lights) {
      if (!l.on || !l.locked || l.site.team === team) continue;
      const ch = Math.cos(l.pitch);
      const dx = Math.cos(l.yaw) * ch, dy = Math.sin(l.pitch), dz = -Math.sin(l.yaw) * ch;
      const vx = pos.x - l.site.x, vy = pos.y - l.site.y, vz = pos.z - l.site.z;
      const d = Math.hypot(vx, vy, vz);
      if (d < 30 || d > 1500) continue;
      const cos = (vx * dx + vy * dy + vz * dz) / d;
      if (cos > 0.995) return true; // within ~5.7° of the beam axis
    }
    return false;
  }
}

/**
 * Land/armada towns: every lot comes from the shared deterministic street
 * grid (terrain_core townPlan/townBuildings) — the plan the server collides
 * shells and hulls with, in both theaters — dressed with the art/town
 * buildings (Part 6d): one InstancedMesh per building model per level of
 * detail per town, and the whole town steps between levels by camera
 * distance. The old merged boxes — plastered walls with a hash-picked tint,
 * dark window bands, a gabled ridge, a door on the street — are the far
 * level, and all there is under ?boxes.
 */
// Plaster variety across a town (instanceColor over the model's paint):
// fresh lime, weathered, and sooty.
const TOWN_TINTS = [
  [1.0, 1.0, 1.0],
  [0.95, 0.92, 0.88],
  [0.86, 0.83, 0.8],
];
const townGeo = new Map(); // "stem#level" → baked geometry, shared by every town

function townBoxes(field, lots) {
  const B = [];
  for (const b of lots) {
    const ground = Math.max(field.surfaceAt(b.x, b.z), field.waterLevel);
    const n = hash2(b.x | 0, b.z | 0, (field.seed + 8111) >>> 0);
    const wall = n < 0.33 ? 0xd1c2a3 : n < 0.66 ? 0xbfae94 : 0xae9a85;
    const roof = n < 0.5 ? 0x7a4733 : 0x5c524c;
    const wallH = b.h - 2;
    // Walls (top face at ground+wallH), then a two-step blocky gable roof.
    B.push({ x: b.x, y: ground + wallH / 2, z: b.z, w: b.w, h: wallH, d: b.d, color: wall });
    B.push({ x: b.x, y: ground + wallH + 0.7, z: b.z, w: b.w + 0.8, h: 1.4, d: b.d + 0.8, color: roof });
    B.push({ x: b.x, y: ground + wallH + 1.9, z: b.z, w: b.w * 0.55, h: 1.2, d: b.d * 0.55, color: roof });
    // Window bands on the long faces, a door on the street side.
    for (let lvl = 2.2; lvl < wallH - 1; lvl += 3.6) {
      B.push({ x: b.x, y: ground + lvl, z: b.z + b.d / 2 + 0.06, w: b.w * 0.7, h: 1.0, d: 0.12, color: 0x292e38 });
      B.push({ x: b.x, y: ground + lvl, z: b.z - b.d / 2 - 0.06, w: b.w * 0.7, h: 1.0, d: 0.12, color: 0x292e38 });
    }
    B.push({ x: b.x + b.w / 2 + 0.06, y: ground + 1.2, z: b.z, w: 0.12, h: 2.4, d: 1.4, color: 0x4d3823 });
  }
  return B;
}

export class Towns {
  constructor(scene, field) {
    this.scene = scene;
    this.towns = [];
    for (const t of field.townPlan()) {
      const lots = field.townBuildings(t);
      if (!lots.length) continue;
      const box = new THREE.Mesh(mergeBoxes(townBoxes(field, lots)), terrainMaterial);
      scene.add(box);
      this.towns.push({ t, lots, box, levels: null, level: -1 });
    }
    if (modelsEnabled()) for (const town of this.towns) this._dress(field, town);
  }

  // Swap a town's boxes for its buildings once every model it uses is in.
  async _dress(field, town) {
    const { t, lots } = town;
    const seed = (field.seed + 8111) >>> 0;
    let hall = false;
    const plan = lots.map((b) => {
      const n = hash2(b.x | 0, b.z | 0, seed);
      const d = townLot(b, t, LAND_BUILDING_GRID_M, n, hall);
      if (d.stem === "town_hall") hall = true;
      const y = Math.max(field.surfaceAt(b.x, b.z), field.waterLevel);
      return { ...d, y, tint: TOWN_TINTS[Math.min(2, Math.floor(n * 3))] };
    });
    const stems = [...new Set(plan.map((p) => p.stem))];
    const loaded = await Promise.all(stems.map((s) => loadLevels(s)));
    if (loaded.some((l) => !l)) return; // a building never came: the boxes stay
    const m = new THREE.Matrix4(), q = new THREE.Quaternion(), up = new THREE.Vector3(0, 1, 0);
    const pos = new THREE.Vector3(), scl = new THREE.Vector3(), col = new THREE.Color();
    const levels = [[], [], []];
    stems.forEach((stem, si) => {
      const mine = plan.filter((p) => p.stem === stem);
      const tpls = loaded[si];
      [0, 1, 2].forEach((lv) => {
        // A level the art didn't ship reuses the next finer one, so every
        // level draws every building.
        let use = lv;
        while (use > 0 && !tpls[use]) use--;
        const tpl = tpls[use];
        const key = `${stem}#${use}`;
        if (!townGeo.has(key)) townGeo.set(key, bakedGeometry(tpl).geometry);
        const mesh = new THREE.InstancedMesh(townGeo.get(key), paintMaterialDSInstanced, mine.length);
        mine.forEach((p, i) => {
          q.setFromAxisAngle(up, p.yaw);
          m.compose(pos.set(p.x, p.y, p.z), q, scl.set(p.sx, 1, p.sz));
          mesh.setMatrixAt(i, m);
          mesh.setColorAt(i, col.setRGB(...p.tint));
        });
        mesh.computeBoundingSphere();
        mesh.visible = false;
        mesh.castShadow = mesh.receiveShadow = GFX >= 2;
        this.scene.add(mesh);
        levels[lv].push(mesh);
      });
    });
    town.levels = levels;
    town.level = -1; // re-pick on the next update
  }

  /** Step each town's level of detail for the camera at `cam` (Part 3.1). */
  update(cam) {
    const [d1, d2, d3] = LOD_DIST.building;
    for (const town of this.towns) {
      if (!town.levels) continue;
      const { t } = town;
      const d = Math.hypot(cam.x - t.x, cam.y - t.h, cam.z - t.z) - t.r * 0.5;
      const lv = d < d1 ? 0 : d < d2 ? 1 : d < d3 ? 2 : 3;
      if (lv === town.level) continue;
      town.level = lv;
      town.levels.forEach((ms, i) => ms.forEach((mesh) => (mesh.visible = i === lv)));
      town.box.visible = lv === 3;
    }
  }
}

export function buildLandTowns(scene, field) {
  return new Towns(scene, field);
}

/** Team HQ towers at each spawn corner — landmarks for orientation. */
// teamBasePos mirrors the server's teamBase(): the air map's radius-scaled
// corners, or the combined-arms map's authored HQ plateaus (armada town plan
// indices 5/6 — Crimson north, Cobalt south, fixed sides).
export function teamBasePos(field, team) {
  if (field.theater === "armada") {
    const t = field.armadaTowns()[team === 0 ? 5 : 6];
    return { x: t.x, z: t.z };
  }
  const d = field.radius * 0.72;
  return { x: team === 0 ? -d * 0.7071 : d * 0.7071, z: team === 0 ? -d * 0.7071 : d * 0.7071 };
}

export function buildBases(scene, field) {
  for (let team = 0; team < 2; team++) {
    const { x: sx, z: sz } = teamBasePos(field, team);
    const ground = Math.max(field.surfaceAt(sx, sz), field.waterLevel);
    const info = TEAM_INFO[team];
    const c = new THREE.Color(info.color);
    const a = new THREE.Color(info.accent);
    const boxes = [
      { x: 0, y: 20, z: 0, w: 46, h: 40, d: 46, color: 0x6f7174 },
      { x: 0, y: 55, z: 0, w: 30, h: 30, d: 30, color: c.getHex() },
      { x: 0, y: 86, z: 0, w: 16, h: 32, d: 16, color: 0x6f7174 },
      { x: 0, y: 108, z: 0, w: 26, h: 12, d: 26, color: c.getHex() },
      { x: 0, y: 124, z: 14, w: 4, h: 28, d: 2, color: 0x4a4d52 },
      { x: 5, y: 132, z: 14, w: 14, h: 9, d: 1.2, color: a.getHex() },
    ];
    const mesh = new THREE.Mesh(mergeBoxes(boxes), terrainMaterial);
    mesh.position.set(sx, ground, sz);
    scene.add(mesh);
    settle(mesh);
    // The HQ model (Part 6c): the concrete block, the team band and flag, and
    // the 63 m radio mast with its red beacon — half the old stack, still the
    // tallest thing on the island. The stack stands in until it lands.
    loadLevels("team_hq").then((tpls) => {
      if (!tpls) return;
      const { lod } = lodOf(tpls, { team, dists: LOD_DIST.building });
      lod.position.set(sx, ground, sz);
      // Front (+X) toward the map centre, where the fight is.
      lod.rotation.y = Math.atan2(sz, -sx);
      scene.remove(mesh);
      scene.add(lod);
      settle(lod);
    });
  }
}
